import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { db, storage } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { geocodeAddress } from './geocoding';
import { getLeadsCollectionName } from './leadsCollectionName';
import { syncPortalFilesToProject, reassignProjectFilesBetweenProjects } from './projectFileService';
import { linkLeadCommsToDeal, linkLeadCommsToProject } from './leadCommsService';
import { rfqProjectReferencesSupplierOrg, rfqProjectSupplierOrgIds, sanitizeFirestoreFieldValues } from './rfqProjectSuppliers';
import { sanitizeRfqProjectFirestoreUpdates } from './timberRfqPrePortalVersion';
import { inviteHasStructureScope } from './timberRfqStructureScope';
import { normalizeStructureSpecies } from './timberRfqStructureSpecies';
import { isOnboardingBridgeTaskId, normalizeOnboardingDepartment } from './onboardingTaskBridge';
import {
  isSimulatedUcGroupId,
  parseSimulatedSchedules,
  STAFF_PLANNING_COLLECTION,
  STAFF_PLANNING_HUB_DOC_ID,
  withSimulatedSchedule,
} from './simulateUnderContract';
import { missingContractedTaskGroups, reservedContractedTaskGroupId } from './ensureContractedTaskGroups';
import { DEFAULT_CONTRACTED_TASK_GROUPS, DEFAULT_PRECON_TASK_GROUPS } from './taskGroupOrder';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  limit,
  Timestamp,
  setDoc,
  getDoc,
  deleteField,
} from 'firebase/firestore';
import { archivePackageStructureIds } from './rfqPackageArchiveStructures';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import { propagateLeadClientIdentityToProjections } from './proposalClientIdentitySync';
import { EMPLOYEE_MENTION_DIRECTORY_ROLES } from './employeeMentionDirectory';
import {
  buildProductionCommentAccessDoc,
  collectHubTaskSupplierOrgIdsForDeal,
  productionCommentAccessHasAwards,
  productionCommentAccessId,
} from './productionCommentAccess';
import { fetchSupplierProductionSchedule } from './supplierScheduleClient';
import { normalizeUidList, stripArchitectHiddenProjectFields } from './architectProjectAccess';
import {
  initialArchitectHubHydrationState,
  reduceArchitectHubHydration,
  type ArchitectHubHydrationState,
} from './architectHubHydration';
import {
  architectDesignCommentScopesFromProjects,
  useArchitectDesignComments,
} from '../hooks/useArchitectDesignComments';
import { mapPaymentTerms, serializePaymentTerms } from './supplierBillsService';
import {
  Lead,
  Contact,
  Deal,
  Proposal,
  Payment,
  Task,
  SMSMessage,
  EmailMessage,
  Pipeline,
  PipelineStage,
  EnhancedDeal,
  StageTransition,
  Activity,
  ActivityType,
  ActivityStatus,
  TimberProject,
  ProjectTask,
  Milestone,
  TaskGroup,
  TaskComment,
  TaskActivityLog,
  ProjectTimeEntry,
  ProjectOnboarding,
  OnboardingItem,
  ArchitectInvite,
  EngineerInvite,
  CpaInvite,
  EmployeeInvite,
  ProductionCommentAccess,
  SupplierOrg,
  SupplierInvite,
  RfqProject,
  RfqPackage,
  RfqSubmission,
  ProposalNodeSet,
  DEFAULT_PIPELINES,
  LeadSource,
  StrategicMarkerLayer,
  StrategicMarker,
  CRMUser,
  UserRole,
  EngagementLetter,
  isEarlyFunnelProjectPhase,
  RfqPackageRequestEnvelope,
} from '../types';
import { Bid, BidGroup } from '../types/bidding';
import { UtmStrategy, UtmCampaign, UtmLink, UtmNote } from '../types/utmStrategy';
import { getSourceColor } from './sourceColors';
import { formatLocationForMaps } from './locationFormatter';
import {
  updateGoogleEvent,
  deleteGoogleEvent,
  isGoogleCalendarConnected
} from './googleCalendarService';
import {
  calculateNextCallTime,
  getCallCadenceConfig,
  isAutoCreateInitialCallEnabled,
} from './callTracking';
import { syncSignedDealSideEffects } from './signedDealSync';
import {
  applyActionRequiredSideEffects,
  seedActionRequiredWaitSegmentsOnCreate,
  serializeActionRequiredWaitSegmentsForFirestore,
} from './actionRequiredWorkflow';
import {
  transferDealToLead as runTransferDealToLead,
  type TransferDealToLeadResult,
} from './transferDealToLead';
import { pickWonPipelineStageId } from './pipelineStageUtils';
import { removeUndefinedDeep } from './removeUndefinedDeep';

/**
 * Heavy org-wide collections that no longer stream at app boot. Pages that need
 * them call `useCRMCollections([...])`, which starts the listener on first use
 * and keeps it for the session (stopping/restarting Firestore listeners on
 * navigation would re-bill the whole collection read each time).
 */
export type LazyCrmCollection =
  | 'projectTasks'
  | 'taskGroups'
  | 'taskComments'
  | 'taskActivity'
  | 'projectOnboardings'
  | 'projectTimeEntries'
  | 'milestones';

export const ALL_LAZY_CRM_COLLECTIONS: LazyCrmCollection[] = [
  'projectTasks',
  'taskGroups',
  'taskComments',
  'taskActivity',
  'projectOnboardings',
  'projectTimeEntries',
  'milestones',
];

interface CRMContextType {
  /** Start org-wide listeners for the given heavy collections (idempotent). */
  requestCrmCollections: (names: LazyCrmCollection[]) => void;
  /** True once the collection's first snapshot has arrived. */
  crmCollectionLoaded: Record<LazyCrmCollection, boolean>;

  // Leads
  leads: Lead[];
  createLead: (lead: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateLead: (id: string, updates: Partial<Lead>) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;
  bulkUpdateLeads: (ids: string[], updates: Partial<Lead>) => Promise<void>;
  bulkDeleteLeads: (ids: string[]) => Promise<void>;
  bulkArchiveLeads: (ids: string[], archived: boolean) => Promise<void>;
  createInitialCallsForRecentLeads: (count?: number) => Promise<{ created: number; skipped: number }>;
  getAllLabels: () => string[];
  saveLabel: (label: string) => Promise<void>;
  savedLabels: string[];

  // Contacts
  contacts: Contact[];
  createContact: (contact: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateContact: (id: string, updates: Partial<Contact>) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;

  // Legacy Deals (keeping for backward compatibility)
  deals: Deal[];
  createDeal: (deal: Omit<Deal, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateDeal: (id: string, updates: Partial<Deal>) => Promise<void>;
  deleteDeal: (id: string) => Promise<void>;
  deleteAllTestDeals: () => Promise<{ success: number; failed: number }>;

  // Pipelines
  pipelines: Pipeline[];
  createPipeline: (pipeline: Omit<Pipeline, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updatePipeline: (id: string, updates: Partial<Pipeline>) => Promise<void>;
  deletePipeline: (id: string) => Promise<void>;
  getDefaultPipeline: () => Pipeline | undefined;
  initializeDefaultPipelines: () => Promise<void>;

  // Enhanced Deals (Pipeline-based)
  enhancedDeals: EnhancedDeal[];
  createEnhancedDeal: (deal: Omit<EnhancedDeal, 'id' | 'createdAt' | 'updatedAt' | 'stageHistory'>) => Promise<string>;
  updateEnhancedDeal: (id: string, updates: Partial<EnhancedDeal>) => Promise<void>;
  deleteEnhancedDeal: (id: string) => Promise<void>;
  moveDealToStage: (dealId: string, newStageId: string, userId: string) => Promise<void>;
  markDealWon: (dealId: string, userId: string) => Promise<void>;
  markDealLost: (dealId: string, reason: string, userId: string) => Promise<void>;
  markDealActive: (dealId: string, userId: string) => Promise<void>;
  /**
   * Convert a Lead into a new EnhancedDeal. The naming convention requires
   * a Project Number, ClientLabel, and Descriptor — collected via the
   * NameProjectWizard / NameProjectFields component before calling.
   */
  convertLeadToDeal: (
    lead: Lead,
    pipelineId: string,
    userId: string,
    naming?: { projectNumber?: string; clientLabel: string; descriptor: string },
  ) => Promise<string>;
  /**
   * Add an additional project to an existing Lead. Same naming requirements
   * as convertLeadToDeal — pass `naming` to use the canonical convention,
   * or omit for legacy `${first} ${last} - ${projectName}` titles.
   * `projectNumber` within `naming` is optional (defer until design fee paid).
   */
  createProjectForLead: (
    lead: Lead,
    projectName: string,
    projectType: string,
    pipelineId: string,
    userId: string,
    budget?: number,
    naming?: { projectNumber?: string; clientLabel: string; descriptor: string },
  ) => Promise<string>;
  linkDealToLead: (dealId: string, leadId: string) => Promise<void>;
  /** Move one deal (sales project) from its current lead to another lead. */
  transferDealToLead: (dealId: string, targetLeadId: string, userId: string) => Promise<TransferDealToLeadResult>;

  // Activities
  createActivity: (activity: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateActivity: (id: string, updates: Partial<Activity>) => Promise<void>;
  deleteActivity: (id: string) => Promise<void>;
  completeActivity: (id: string, updates?: { outcome?: string; description?: string }) => Promise<void>;

  // Timber Projects
  timberProjects: TimberProject[];
  createTimberProject: (project: Omit<TimberProject, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateTimberProject: (id: string, updates: Partial<TimberProject>) => Promise<void>;
  persistProductionCommentAccessForDeal: (
    dealId: string,
    overlay?: {
      rfqProjectId?: string;
      rfqUpdates?: Partial<RfqProject>;
      hubProjectId?: string;
      hubPhase?: TimberProject['phase'];
      /** Hub supplier save — state may lag behind Firestore until the next render. */
      accessProjectTasks?: ProjectTask[];
      accessProjectOnboardings?: Array<{ projectId?: string; items?: OnboardingItem[] }>;
    },
  ) => Promise<void>;
  deleteTimberProject: (id: string) => Promise<void>;
  convertDealToProject: (deal: EnhancedDeal, userId: string, frameType?: 'custom' | 'kit') => Promise<string>;
  createPreconProject: (deal: EnhancedDeal, userId: string) => Promise<string>;
  /** Won + Project Hub timber project + sales order when a deal is signed (portal or public). */
  syncSignedDealSideEffects: (
    dealId: string,
    userId: string,
    options?: { skipMarkWon?: boolean; skipSalesOrder?: boolean; proposalDocId?: string },
  ) => Promise<void>;

  // Project Time Entries
  projectTimeEntries: ProjectTimeEntry[];
  createProjectTimeEntry: (entry: Omit<ProjectTimeEntry, 'id' | 'createdAt'>) => Promise<string>;
  updateProjectTimeEntry: (id: string, updates: Partial<ProjectTimeEntry>) => Promise<void>;
  getTimeEntriesForProject: (projectId: string) => ProjectTimeEntry[];

  // Project Onboarding
  projectOnboardings: ProjectOnboarding[];
  updateOnboardingItem: (
    projectId: string,
    itemId: string,
    updates: Partial<OnboardingItem>,
    userName: string,
    options?: { seedIfMissing?: ProjectOnboarding },
  ) => Promise<void>;
  deleteOnboardingItem: (projectId: string, itemId: string) => Promise<void>;
  getOnboardingForProject: (projectId: string) => ProjectOnboarding | undefined;

  // Project Tasks
  projectTasks: ProjectTask[];
  createProjectTask: (task: Omit<ProjectTask, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateProjectTask: (id: string, updates: Partial<ProjectTask>) => Promise<void>;
  deleteProjectTask: (id: string) => Promise<void>;
  getTasksForProject: (projectId: string) => ProjectTask[];

  // Task Groups
  taskGroups: TaskGroup[];
  createTaskGroup: (group: Omit<TaskGroup, 'id'>) => Promise<string>;
  updateTaskGroup: (id: string, updates: Partial<TaskGroup>) => Promise<void>;
  deleteTaskGroup: (id: string) => Promise<void>;
  /** Adds missing Design / Procurement / Production / Installation groups on under-contract custom jobs */
  ensureProcurementTaskGroupForProject: (projectId: string) => Promise<void>;

  // Task Comments & Activity
  taskComments: TaskComment[];
  createTaskComment: (comment: Omit<TaskComment, 'id' | 'createdAt'>) => Promise<string>;
  taskActivityLogs: TaskActivityLog[];
  createTaskActivity: (log: Omit<TaskActivityLog, 'id' | 'createdAt'>) => Promise<string>;

  // Milestones
  milestones: Milestone[];
  createMilestone: (milestone: Omit<Milestone, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateMilestone: (id: string, updates: Partial<Milestone>) => Promise<void>;
  deleteMilestone: (id: string) => Promise<void>;
  getMilestonesForProject: (projectId: string) => Milestone[];

  // Proposals
  proposals: Proposal[];
  createProposal: (proposal: Omit<Proposal, 'id' | 'createdAt'>) => Promise<string>;
  updateProposal: (id: string, updates: Partial<Proposal>) => Promise<void>;
  deleteProposal: (id: string) => Promise<void>;

  // Payments
  payments: Payment[];
  createPayment: (payment: Omit<Payment, 'id' | 'createdAt'>) => Promise<string>;
  updatePayment: (id: string, updates: Partial<Payment>) => Promise<void>;
  deletePayment: (id: string) => Promise<void>;

  // Tasks (legacy)
  tasks: Task[];
  createTask: (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateTask: (id: string, updates: Partial<Task>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;

  // SMS Messages
  smsMessages: SMSMessage[];
  createSMSMessage: (messageData: Omit<SMSMessage, 'id' | 'createdAt'>) => Promise<string>;
  updateSMSMessage: (id: string, updates: Partial<SMSMessage>) => Promise<void>;

  // Email Messages
  emailMessages: EmailMessage[];
  createEmailMessage: (messageData: Omit<EmailMessage, 'id' | 'createdAt'>) => Promise<string>;
  updateEmailMessage: (id: string, updates: Partial<EmailMessage>) => Promise<void>;

  // Bids
  bids: Bid[];
  createBid: (bid: Omit<Bid, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateBid: (id: string, updates: Partial<Bid>) => Promise<void>;
  deleteBid: (id: string) => Promise<void>;
  getBidsForLead: (leadId: string) => Bid[];
  getBidsForDeal: (dealId: string) => Bid[];
  addGroupToBid: (bidId: string, group: Omit<BidGroup, 'id'>) => Promise<string>;
  updateBidGroup: (bidId: string, groupId: string, updates: Partial<BidGroup>) => Promise<void>;
  deleteBidGroup: (bidId: string, groupId: string) => Promise<void>;
  generateBidNumber: () => Promise<string>;

  // Strategic Markers (for wealth indicators, competitor locations, etc.)
  strategicLayers: StrategicMarkerLayer[];
  strategicMarkers: StrategicMarker[];
  createStrategicLayer: (layer: Omit<StrategicMarkerLayer, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateStrategicLayer: (id: string, updates: Partial<StrategicMarkerLayer>) => Promise<void>;
  deleteStrategicLayer: (id: string) => Promise<void>;
  createStrategicMarker: (marker: Omit<StrategicMarker, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  bulkCreateStrategicMarkers: (markers: Omit<StrategicMarker, 'id' | 'createdAt' | 'updatedAt'>[]) => Promise<number>;
  updateStrategicMarker: (id: string, updates: Partial<StrategicMarker>) => Promise<void>;
  deleteStrategicMarker: (id: string) => Promise<void>;
  deleteAllMarkersInLayer: (layerId: string) => Promise<number>;
  getMarkersForLayer: (layerId: string) => StrategicMarker[];
  toggleLayerVisibility: (layerId: string, enabled: boolean) => Promise<void>;

  // User Management
  crmUsers: CRMUser[];
  updateCRMUser: (uid: string, updates: Partial<CRMUser>) => Promise<void>;
  /** Admin only: upload profile image for another user (Firestore + Storage; does not change Firebase Auth photo). */
  adminSetUserProfilePhoto: (targetUid: string, file: File) => Promise<void>;
  /** Admin only: clear profile image for another user in Firestore. */
  adminClearUserProfilePhoto: (targetUid: string) => Promise<void>;
  deactivateUser: (uid: string) => Promise<void>;
  reactivateUser: (uid: string) => Promise<void>;
  deleteCRMUser: (uid: string) => Promise<void>;

  // Architect Invites
  architectInvites: ArchitectInvite[];
  createArchitectInvite: (invite: Omit<ArchitectInvite, 'id' | 'createdAt'>) => Promise<string>;
  updateArchitectInvite: (id: string, updates: Partial<ArchitectInvite>) => Promise<void>;
  revokeArchitectInvite: (id: string) => Promise<void>;

  // Engineer Invites
  engineerInvites: EngineerInvite[];
  createEngineerInvite: (invite: Omit<EngineerInvite, 'id' | 'createdAt'>) => Promise<string>;
  updateEngineerInvite: (id: string, updates: Partial<EngineerInvite>) => Promise<void>;
  revokeEngineerInvite: (id: string) => Promise<void>;

  // CPA Invites
  cpaInvites: CpaInvite[];
  createCpaInvite: (invite: Omit<CpaInvite, 'id' | 'createdAt'>) => Promise<string>;
  updateCpaInvite: (id: string, updates: Partial<CpaInvite>) => Promise<void>;
  revokeCpaInvite: (id: string) => Promise<void>;

  employeeInvites: EmployeeInvite[];
  createEmployeeInvite: (invite: Omit<EmployeeInvite, 'id' | 'createdAt'>) => Promise<string>;
  revokeEmployeeInvite: (id: string) => Promise<void>;
  productionCommentAccess: ProductionCommentAccess[];

  // Supplier RFQs
  supplierOrgs: SupplierOrg[];
  supplierInvites: SupplierInvite[];
  rfqProjects: RfqProject[];
  rfqPackages: RfqPackage[];
  rfqSubmissions: RfqSubmission[];
  createSupplierOrg: (org: Omit<SupplierOrg, 'id' | 'createdAt'>) => Promise<string>;
  updateSupplierOrg: (id: string, updates: Partial<SupplierOrg>) => Promise<void>;
  deleteSupplierOrg: (id: string) => Promise<void>;
  createSupplierInvite: (invite: Omit<SupplierInvite, 'id' | 'createdAt'>) => Promise<string>;
  updateSupplierInvite: (id: string, updates: Partial<SupplierInvite>) => Promise<void>;
  revokeSupplierInvite: (id: string) => Promise<void>;
  deleteSupplierInvite: (id: string) => Promise<void>;
  createRfqProject: (row: Omit<RfqProject, 'id' | 'createdAt'>) => Promise<string>;
  updateRfqProject: (id: string, updates: Partial<RfqProject>) => Promise<void>;
  publishRfqPackage: (input: {
    rfqProjectId: string;
    supplierOrgId: string;
    /** When set, one package is created per org (same timber snapshot). Otherwise uses `supplierOrgId` only. */
    targetSupplierOrgIds?: string[];
    dealId: string;
    dealTitle?: string;
    workType?: import('../types').VendorWorkType;
    proposalVersionId?: string | null;
    proposalSnapshot?: { nodeSets: ProposalNodeSet[] };
    label?: string;
    releaseDisplayLabel?: string;
    timberListSource?: 'proposal_live' | 'proposal_saved_version' | 'hub_csv' | 'hub_staged_release';
    hubFileReleaseId?: string;
    /** Every staged release the package carries (request rounds publish one package per supplier). */
    hubFileReleaseIds?: string[];
    releaseVersionIndex?: number;
    linkedProposalVersionId?: string | null;
    linkedProposalVersionLabel?: string;
    linkedProposalVersionSource?: import('../types').ProposalVersionSource;
    linkedProposalVersionSavedAt?: Date;
    linkedFrom?: 'proposal_push' | 'hub_file_stage';
    cncPackagePayload?: import('../types').CncPackagePayload;
    customEmailMessage?: string;
    legacyImport?: boolean;
    /** Package-only CNC version from a combined timber+CNC quote split (no hub release). */
    quotePlaceholder?: boolean;
    shipToByOrgId?: Record<string, import('../types').SupplierShipToAddress>;
    shipToPresetByOrgId?: Record<string, import('../types').SupplierShipToPreset>;
    scopeStructuresByOrgId?: Record<string, import('../types').TimberSupplierStructureRef[]>;
    request?: RfqPackageRequestEnvelope;
    supersedesPackageIdByOrgId?: Record<string, string>;
    createdByUid: string;
    createdByName: string;
  }) => Promise<string[]>;
  deleteRfqPackage: (packageId: string) => Promise<void>;
  createRfqSubmission: (
    row: Omit<RfqSubmission, 'id' | 'createdAt'>,
    options?: { createdAt?: Date },
  ) => Promise<string>;
  updateRfqSubmission: (id: string, updates: Partial<RfqSubmission>) => Promise<void>;
  deleteRfqSubmission: (row: {
    id: string;
    storagePath?: string;
    notBidding?: boolean;
    quotePlaceholder?: boolean;
    linkedTimberSubmissionId?: string;
    linkedCncSubmissionId?: string;
  }) => Promise<void>;

  // UTM Strategies
  utmStrategies: UtmStrategy[];
  createUtmStrategy: (strategy: Omit<UtmStrategy, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateUtmStrategy: (id: string, updates: Partial<UtmStrategy>) => Promise<void>;
  deleteUtmStrategy: (id: string) => Promise<void>;
  addCampaignToStrategy: (strategyId: string, campaign: Omit<UtmCampaign, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateCampaign: (strategyId: string, campaignId: string, updates: Partial<UtmCampaign>) => Promise<void>;
  deleteCampaign: (strategyId: string, campaignId: string) => Promise<void>;
  addLinkToCampaign: (strategyId: string, campaignId: string, link: Omit<UtmLink, 'id' | 'createdAt'>) => Promise<string>;
  deleteLinkFromCampaign: (strategyId: string, campaignId: string, linkId: string) => Promise<void>;
  addNoteToCampaign: (strategyId: string, campaignId: string, note: Omit<UtmNote, 'id' | 'createdAt'>) => Promise<string>;
  deleteNoteFromCampaign: (strategyId: string, campaignId: string, noteId: string) => Promise<void>;

  // Engagement Letters
  engagementLetters: EngagementLetter[];
  createEngagementLetter: (letter: Omit<EngagementLetter, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateEngagementLetter: (id: string, updates: Partial<EngagementLetter>) => Promise<void>;
  deleteEngagementLetter: (id: string) => Promise<void>;
  getEngagementLettersForLead: (leadId: string) => EngagementLetter[];

  // Loading states
  loading: boolean;
  /**
   * Architect role only: true after the user doc’s `architectHubProjectIds` are known
   * from the **server** (not a cache-only empty snap) AND every listed timberProjects
   * doc has reported once (or the id list is genuinely empty). CRM `loading` goes false
   * earlier — deep links must wait on this flag, not `loading`.
   * Non-architects: always true (unused).
   */
  architectHubReady: boolean;
  error: string | null;
}

const CRMContext = createContext<CRMContextType | undefined>(undefined);

export const useCRM = () => {
  const context = useContext(CRMContext);
  if (context === undefined) {
    throw new Error('useCRM must be used within a CRMProvider');
  }
  return context;
};

/**
 * Declare which heavy org-wide collections this page/component needs.
 * Starts the listeners on first use (session-lifetime); returns true once
 * every requested collection has received its first snapshot.
 */
export const useCRMCollections = (names: LazyCrmCollection[]): boolean => {
  const { requestCrmCollections, crmCollectionLoaded } = useCRM();
  const namesKey = names.join(',');
  useEffect(() => {
    if (!namesKey) return;
    requestCrmCollections(namesKey.split(',') as LazyCrmCollection[]);
  }, [namesKey, requestCrmCollections]);
  return names.every(n => crmCollectionLoaded[n]);
};

/** Same as useCRM but returns undefined outside CRMProvider (e.g. client portal PDF viewer). */
export const useOptionalCRM = () => useContext(CRMContext);

interface CRMProviderProps {
  children: ReactNode;
}

/** Shared shape for `projectOnboarding.items` writes (matches {@link updateOnboardingItem}). */
function serializeOnboardingItemsForFirestore(items: OnboardingItem[]): Record<string, unknown>[] {
  return items.map((it) => {
    const clean: Record<string, any> = {};
    for (const [k, v] of Object.entries(it)) {
      if (v === undefined) continue;
      if (k === 'taskGroupId' && isSimulatedUcGroupId(typeof v === 'string' ? v : undefined)) continue;
      clean[k] = v;
    }
    const toTs = (d: unknown) =>
      Timestamp.fromDate(d instanceof Date ? d : new Date(d as string | number));
    if (clean.startDate) clean.startDate = toTs(clean.startDate);
    else delete clean.startDate;
    if (clean.endDate) clean.endDate = toTs(clean.endDate);
    else delete clean.endDate;
    if (clean.completedAt) {
      clean.completedAt = Timestamp.fromDate(
        clean.completedAt instanceof Date ? clean.completedAt : new Date(clean.completedAt),
      );
    } else {
      clean.completedAt = null;
    }
    if (!clean.completedBy) clean.completedBy = null;
    if (!clean.completedByName) clean.completedByName = null;
    if (clean.actualStartDate) clean.actualStartDate = toTs(clean.actualStartDate);
    else delete clean.actualStartDate;
    if (clean.actualEndDate) clean.actualEndDate = toTs(clean.actualEndDate);
    else delete clean.actualEndDate;
    if (clean.lockedAt) clean.lockedAt = toTs(clean.lockedAt);
    else delete clean.lockedAt;
    if (clean.scheduleReviewedByPmAt) clean.scheduleReviewedByPmAt = toTs(clean.scheduleReviewedByPmAt);
    else delete clean.scheduleReviewedByPmAt;
    return clean;
  });
}

export const CRMProvider: React.FC<CRMProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [smsMessages, setSmsMessages] = useState<SMSMessage[]>([]);
  const [emailMessages, setEmailMessages] = useState<EmailMessage[]>([]);

  // New state for Pipeline system
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [enhancedDeals, setEnhancedDeals] = useState<EnhancedDeal[]>([]);
  const [timberProjects, setTimberProjects] = useState<TimberProject[]>([]);
  const [projectTasks, setProjectTasks] = useState<ProjectTask[]>([]);
  const [taskGroups, setTaskGroups] = useState<TaskGroup[]>([]);
  const [taskComments, setTaskComments] = useState<TaskComment[]>([]);
  const [taskActivityLogs, setTaskActivityLogs] = useState<TaskActivityLog[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [projectTimeEntries, setProjectTimeEntries] = useState<ProjectTimeEntry[]>([]);
  const [projectOnboardings, setProjectOnboardings] = useState<ProjectOnboarding[]>([]);

  const [loading, setLoading] = useState(true);
  /** See CRMContextType.architectHubReady — starts false until role/hydrate settles. */
  const [architectHubReady, setArchitectHubReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lazy heavy-collection machinery (see LazyCrmCollection docs).
  const [requestedCrmCollections, setRequestedCrmCollections] = useState<ReadonlySet<LazyCrmCollection>>(new Set());
  const emptyLoadedFlags = (): Record<LazyCrmCollection, boolean> => ({
    projectTasks: false,
    taskGroups: false,
    taskComments: false,
    taskActivity: false,
    projectOnboardings: false,
    projectTimeEntries: false,
    milestones: false,
  });
  const [crmCollectionLoaded, setCrmCollectionLoaded] = useState<Record<LazyCrmCollection, boolean>>(emptyLoadedFlags);
  const lazyCrmUnsubsRef = useRef(new Map<LazyCrmCollection, () => void>());

  const requestCrmCollections = useCallback((names: LazyCrmCollection[]) => {
    if (!names.length) return;
    setRequestedCrmCollections(prev => {
      if (names.every(n => prev.has(n))) return prev;
      const next = new Set(prev);
      for (const n of names) next.add(n);
      return next;
    });
  }, []);
  const [savedLabels, setSavedLabels] = useState<string[]>([]);

  // Strategic markers state
  const [strategicLayers, setStrategicLayers] = useState<StrategicMarkerLayer[]>([]);
  const [strategicMarkers, setStrategicMarkers] = useState<StrategicMarker[]>([]);

  // Bids state
  const [bids, setBids] = useState<Bid[]>([]);

  // UTM Strategies state
  const [utmStrategies, setUtmStrategies] = useState<UtmStrategy[]>([]);

  // CRM Users state
  const [crmUsers, setCrmUsers] = useState<CRMUser[]>([]);
  const [architectInvites, setArchitectInvites] = useState<ArchitectInvite[]>([]);
  const [engineerInvites, setEngineerInvites] = useState<EngineerInvite[]>([]);
  const [cpaInvites, setCpaInvites] = useState<CpaInvite[]>([]);
  const [employeeInvites, setEmployeeInvites] = useState<EmployeeInvite[]>([]);
  const [productionCommentAccess, setProductionCommentAccess] = useState<ProductionCommentAccess[]>([]);

  const [supplierOrgs, setSupplierOrgs] = useState<SupplierOrg[]>([]);
  const [supplierInvites, setSupplierInvites] = useState<SupplierInvite[]>([]);
  /** Bumped after a supplier task update so the hub re-syncs from the callable. */
  const [supplierScheduleRefreshToken, setSupplierScheduleRefreshToken] = useState(0);
  const [rfqProjects, setRfqProjects] = useState<RfqProject[]>([]);
  const [rfqPackages, setRfqPackages] = useState<RfqPackage[]>([]);
  const [rfqSubmissions, setRfqSubmissions] = useState<RfqSubmission[]>([]);

  // Engagement Letters state
  const [engagementLetters, setEngagementLetters] = useState<EngagementLetter[]>([]);

  // Load saved labels from Firestore (CRM map — not needed for staff tier)
  useEffect(() => {
    if (
      authLoading
      || !user?.id
      || user.role === 'staff'
      || user.role === 'supplier'
      || user.role === 'architect'
      || user.role === 'subcontractor'
    ) return;
    const loadSavedLabels = async () => {
      try {
        const labelsDoc = await getDoc(doc(db, 'settings', 'labels'));
        if (labelsDoc.exists()) {
          const data = labelsDoc.data();
          setSavedLabels(data.labels || []);
        }
      } catch (err) {
        console.error('Error loading saved labels:', err);
      }
    };
    loadSavedLabels();
  }, [authLoading, user?.id, user?.role]);

  // Helper function to safely convert Firestore timestamps to Date objects
  // Returns the ACTUAL timestamp from database, or a sentinel date if missing
  // Sentinel: Jan 1, 2000 00:00:00 UTC - clearly indicates missing data
  const MISSING_TIMESTAMP_SENTINEL = new Date('2000-01-01T00:00:00Z');

  // Parse custom date formats that JS Date() doesn't handle
  const parseCustomDateString = (dateStr: string): Date | null => {
    // Format: "01/20/26 07:11PM" (MM/DD/YY hh:mmAM/PM) - Zapier format
    const zapierMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})\s+(\d{1,2}):(\d{2})(AM|PM)$/i);
    if (zapierMatch) {
      const [, month, day, year, hour, minute, ampm] = zapierMatch;
      let hours = parseInt(hour, 10);
      if (ampm.toUpperCase() === 'PM' && hours !== 12) hours += 12;
      if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
      // Assume 2000s for 2-digit year
      const fullYear = 2000 + parseInt(year, 10);
      return new Date(fullYear, parseInt(month, 10) - 1, parseInt(day, 10), hours, parseInt(minute, 10));
    }

    // Format: "2025-12-14 15:08:34" (YYYY-MM-DD HH:MM:SS) - CSV import format
    const isoLikeMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
    if (isoLikeMatch) {
      const [, year, month, day, hour, minute, second] = isoLikeMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second));
    }

    return null;
  };

  const convertTimestamp = (timestamp: any): Date => {
    // If timestamp is missing or null/undefined, return sentinel
    if (!timestamp) {
      return MISSING_TIMESTAMP_SENTINEL;
    }
    // Check for empty object {} - treat as missing
    if (typeof timestamp === 'object' && Object.keys(timestamp).length === 0) {
      return MISSING_TIMESTAMP_SENTINEL;
    }
    // Firestore Timestamp object with toDate() method
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    // Firestore timestamp stored as plain object {seconds, nanoseconds}
    // This happens with Zapier and some API integrations
    if (timestamp.seconds !== undefined && typeof timestamp.seconds === 'number') {
      const milliseconds = timestamp.seconds * 1000 + Math.floor((timestamp.nanoseconds || 0) / 1000000);
      return new Date(milliseconds);
    }
    // Already a Date object
    if (timestamp instanceof Date) {
      return timestamp;
    }
    // String date - try custom formats first, then standard parsing
    if (typeof timestamp === 'string') {
      // Try custom date formats (Zapier, CSV imports, etc.)
      const customParsed = parseCustomDateString(timestamp.trim());
      if (customParsed && !isNaN(customParsed.getTime())) {
        return customParsed;
      }
      // Try standard JS Date parsing (ISO format, etc.)
      const parsed = new Date(timestamp);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    // Unix timestamp in milliseconds
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    // Fallback - return sentinel to indicate missing data
    return MISSING_TIMESTAMP_SENTINEL;
  };

  // Helper function for optional timestamps that can return undefined
  const convertOptionalTimestamp = (timestamp: any): Date | undefined => {
    if (!timestamp) return undefined;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if (timestamp instanceof Date) {
      return timestamp;
    }
    return undefined;
  };

  const mapApprovalDateRecord = (raw: unknown): Record<string, Date> | undefined => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
    const out: Record<string, Date> = {};
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      const date = convertOptionalTimestamp(value);
      if (date) out[key] = date;
    }
    return Object.keys(out).length ? out : undefined;
  };

  const mapNestedApprovalDateRecord = (
    raw: unknown,
  ): Record<string, Record<string, Date>> | undefined => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
    const out: Record<string, Record<string, Date>> = {};
    for (const [releaseId, inner] of Object.entries(raw as Record<string, unknown>)) {
      const mapped = mapApprovalDateRecord(inner);
      if (mapped) out[releaseId] = mapped;
    }
    return Object.keys(out).length ? out : undefined;
  };

  /** Start one heavy org-wide listener (moved out of the boot effect; see LazyCrmCollection). */
  const startLazyCrmListener = (name: LazyCrmCollection): (() => void) => {
    const snapErr = (listenId: string) => (err: unknown) =>
      firestoreSnapshotListenError('CRMContext', listenId, err, {
        userId: user?.id,
        role: user?.role,
        email: user?.email,
      });
    const markLoaded = () => setCrmCollectionLoaded(prev => (prev[name] ? prev : { ...prev, [name]: true }));

    switch (name) {
      case 'projectTimeEntries': {
        const timeEntriesQuery = query(collection(db, 'projectTimeEntries'), orderBy('createdAt', 'desc'));
        return onSnapshot(timeEntriesQuery, (snapshot) => {
          const entries = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              type: data.type ?? 'explicit',
              date: convertTimestamp(data.date),
              startTime: data.startTime ? convertTimestamp(data.startTime) : undefined,
              endTime: data.endTime ? convertTimestamp(data.endTime) : undefined,
              createdAt: convertTimestamp(data.createdAt),
            };
          }) as ProjectTimeEntry[];
          setProjectTimeEntries(entries);
          markLoaded();
        }, snapErr('projectTimeEntries'));
      }
      case 'projectOnboardings': {
        const onboardingQuery = query(collection(db, 'projectOnboarding'));
        return onSnapshot(onboardingQuery, (snapshot) => {
          const obs = snapshot.docs.map(d => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              templateVersion: convertTimestamp(data.templateVersion),
              createdAt: convertTimestamp(data.createdAt),
              completedAt: data.completedAt ? convertTimestamp(data.completedAt) : undefined,
              items: (data.items || []).map((it: any) => ({
                ...it,
                completedAt: it.completedAt ? convertTimestamp(it.completedAt) : undefined,
                startDate: it.startDate ? convertTimestamp(it.startDate) : undefined,
                endDate: it.endDate ? convertTimestamp(it.endDate) : undefined,
                actualStartDate: it.actualStartDate ? convertTimestamp(it.actualStartDate) : undefined,
                actualEndDate: it.actualEndDate ? convertTimestamp(it.actualEndDate) : undefined,
                lockedAt: it.lockedAt ? convertTimestamp(it.lockedAt) : undefined,
                scheduleReviewedByPmAt: it.scheduleReviewedByPmAt
                  ? convertTimestamp(it.scheduleReviewedByPmAt)
                  : undefined,
              })),
            };
          }) as ProjectOnboarding[];
          setProjectOnboardings(obs);
          markLoaded();
        }, snapErr('projectOnboarding'));
      }
      case 'projectTasks': {
        const projectTasksQuery = query(collection(db, 'projectTasks'), orderBy('createdAt', 'desc'));
        return onSnapshot(projectTasksQuery, (snapshot) => {
          const tasksData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: convertTimestamp(data.createdAt),
              updatedAt: convertTimestamp(data.updatedAt),
              startDate: convertTimestamp(data.startDate),
              endDate: convertTimestamp(data.endDate),
              lastCommentAt: data.lastCommentAt ? convertTimestamp(data.lastCommentAt) : undefined,
              actualStartDate: data.actualStartDate ? convertTimestamp(data.actualStartDate) : undefined,
              actualEndDate: data.actualEndDate ? convertTimestamp(data.actualEndDate) : undefined,
              completedAt: data.completedAt ? convertTimestamp(data.completedAt) : undefined,
              lockedAt: data.lockedAt ? convertTimestamp(data.lockedAt) : undefined,
              scheduleReviewedByPmAt: data.scheduleReviewedByPmAt
                ? convertTimestamp(data.scheduleReviewedByPmAt)
                : undefined,
              actionRequiredWaitSegments: Array.isArray(data.actionRequiredWaitSegments)
                ? data.actionRequiredWaitSegments.map((seg: Record<string, unknown>) => ({
                    holderKey: String(seg.holderKey ?? ''),
                    holderNote: seg.holderNote ? String(seg.holderNote) : undefined,
                    startedAt: convertTimestamp(seg.startedAt),
                    endedAt: seg.endedAt ? convertTimestamp(seg.endedAt) : undefined,
                  }))
                : undefined,
            };
          }) as ProjectTask[];
          setProjectTasks(tasksData);
          markLoaded();
        }, snapErr('projectTasks'));
      }
      case 'taskGroups': {
        const taskGroupsQuery = query(collection(db, 'taskGroups'), orderBy('position', 'asc'));
        return onSnapshot(taskGroupsQuery, (snapshot) => {
          const groupsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as TaskGroup));
          setTaskGroups(groupsData);
          markLoaded();
        }, snapErr('taskGroups'));
      }
      case 'taskComments': {
        const taskCommentsQuery = query(collection(db, 'taskComments'), orderBy('createdAt', 'desc'));
        return onSnapshot(taskCommentsQuery, (snapshot) => {
          const commentsData = snapshot.docs.map(d => {
            const data = d.data();
            const externalEmailDeliveries = Array.isArray(data.externalEmailDeliveries)
              ? data.externalEmailDeliveries.map((row: Record<string, unknown>) => ({
                  userId: String(row.userId ?? ''),
                  recipientName: String(row.recipientName ?? ''),
                  role: row.role as 'architect' | 'engineer',
                  eventType: row.eventType as 'comment' | 'mention',
                  emailSubject: String(row.emailSubject ?? ''),
                  sentAt: convertTimestamp(row.sentAt),
                }))
              : undefined;
            const emailDeliveries = Array.isArray(data.emailDeliveries)
              ? data.emailDeliveries.map((row: Record<string, unknown>) => ({
                  recipientUserId: String(row.recipientUserId ?? ''),
                  recipientEmail: String(row.recipientEmail ?? ''),
                  recipientName: String(row.recipientName ?? ''),
                  recipientRole: String(row.recipientRole ?? ''),
                  eventType: row.eventType as 'comment' | 'mention',
                  status: row.status as 'sent' | 'failed',
                  emailSubject: row.emailSubject != null ? String(row.emailSubject) : undefined,
                  error: row.error != null ? String(row.error) : undefined,
                  sentAt: convertTimestamp(row.sentAt),
                  triggeredByUserId: String(row.triggeredByUserId ?? ''),
                  triggeredByName: String(row.triggeredByName ?? ''),
                }))
              : undefined;
            return {
              id: d.id,
              ...data,
              createdAt: convertTimestamp(data.createdAt),
              externalEmailDeliveries,
              emailDeliveries,
            } as TaskComment;
          });
          setTaskComments(commentsData);
          markLoaded();
        }, snapErr('taskComments'));
      }
      case 'taskActivity': {
        const taskActivityQuery = query(collection(db, 'taskActivity'), orderBy('createdAt', 'desc'));
        return onSnapshot(taskActivityQuery, (snapshot) => {
          const logsData = snapshot.docs.map(d => {
            const data = d.data();
            return { id: d.id, ...data, createdAt: convertTimestamp(data.createdAt) } as TaskActivityLog;
          });
          setTaskActivityLogs(logsData);
          markLoaded();
        }, snapErr('taskActivity'));
      }
      case 'milestones': {
        const milestonesQuery = query(collection(db, 'milestones'), orderBy('createdAt', 'desc'));
        return onSnapshot(milestonesQuery, (snapshot) => {
          const milestonesData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: convertTimestamp(data.createdAt),
              updatedAt: convertTimestamp(data.updatedAt),
              targetDate: convertTimestamp(data.targetDate),
              completedDate: convertOptionalTimestamp(data.completedDate),
              lockedAt: data.lockedAt ? convertTimestamp(data.lockedAt) : undefined,
            };
          }) as Milestone[];
          setMilestones(milestonesData);
          markLoaded();
        }, snapErr('milestones'));
      }
      default:
        return () => {};
    }
  };

  // Tear down lazy listeners (and reset requests) when the signed-in user changes.
  useEffect(() => {
    const unsubs = lazyCrmUnsubsRef.current;
    return () => {
      unsubs.forEach(unsub => unsub());
      unsubs.clear();
      setCrmCollectionLoaded(emptyLoadedFlags());
      setRequestedCrmCollections(new Set());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Start requested heavy listeners once auth is ready (suppliers keep their scoped paths).
  useEffect(() => {
    if (authLoading || !user?.id || user.role === 'supplier' || user.role === 'architect' || user.role === 'subcontractor') return;
    requestedCrmCollections.forEach(name => {
      if (lazyCrmUnsubsRef.current.has(name)) return;
      lazyCrmUnsubsRef.current.set(name, startLazyCrmListener(name));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, user?.role, requestedCrmCollections]);

  // Initialize real-time listeners (after auth; staff tier skips full CRM collections)
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user?.id) {
      setLoading(false);
      return;
    }

    const isStaffUser = user.role === 'staff';
    const snapErr = (listenId: string) => (err: unknown) =>
      firestoreSnapshotListenError('CRMContext', listenId, err, {
        userId: user.id,
        role: user.role,
        email: user.email,
      });
    /** Invited trades only need the employee mention directory for PDF comments. */
    if (user.role === 'subcontractor') {
      const mentionUsersUnsubscribe = onSnapshot(
        query(
          collection(db, 'users'),
          where('role', 'in', [...EMPLOYEE_MENTION_DIRECTORY_ROLES]),
        ),
        (snapshot) => {
          const usersData = snapshot.docs.map((userDoc) => {
            const data = userDoc.data();
            const { designerHourlyRate: _omitRate, ...safe } = data;
            return {
              uid: userDoc.id,
              ...safe,
              createdAt: convertTimestamp(data.createdAt),
              lastLogin: convertTimestamp(data.lastLogin),
            };
          }) as CRMUser[];
          setCrmUsers(usersData);
        },
        snapErr('tradeMentionUsers'),
      );
      setLoading(false);
      return () => mentionUsersUnsubscribe();
    }
    /** 1099 shell — under-contract hub projects + deal labels for timesheet picker (not full CRM). */
    if (user.is1099Contractor === true) {
      const mapTimberProjectDoc = (d: { id: string; data: () => Record<string, unknown> }): TimberProject => {
        const data = d.data();
        return {
          ...data,
          id: d.id,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt),
          startDate: convertTimestamp(data.startDate),
          targetEndDate: convertTimestamp(data.targetEndDate),
          actualEndDate: convertOptionalTimestamp(data.actualEndDate),
          contractedAt: convertOptionalTimestamp(data.contractedAt),
          standupNoteUpdatedAt: convertOptionalTimestamp(data.standupNoteUpdatedAt),
        } as TimberProject;
      };
      const loadDealsForProjects = async (projects: TimberProject[]) => {
        const dealIds = Array.from(
          new Set(projects.map((p) => p.dealId).filter((id): id is string => !!id)),
        );
        if (!dealIds.length) {
          setEnhancedDeals([]);
          return;
        }
        const snaps = await Promise.all(dealIds.map((id) => getDoc(doc(db, 'enhancedDeals', id))));
        const dealsData = snaps
          .filter((s) => s.exists())
          .map((s) => {
            const data = s.data()!;
            return {
              id: s.id,
              ...data,
              createdAt: convertTimestamp(data.createdAt),
              updatedAt: convertTimestamp(data.updatedAt),
              currentStageEnteredAt: convertTimestamp(data.currentStageEnteredAt),
              closedAt: convertOptionalTimestamp(data.closedAt),
              firstWonAt: convertOptionalTimestamp(data.firstWonAt),
              expectedCloseDate: convertOptionalTimestamp(data.expectedCloseDate),
              pmFinalizedAt: convertOptionalTimestamp(data.pmFinalizedAt),
              stageHistory: (data.stageHistory || []).map((transition: unknown) => ({
                ...(transition as object),
                transitionedAt: convertTimestamp((transition as { transitionedAt?: unknown }).transitionedAt),
              })),
            };
          }) as EnhancedDeal[];
        setEnhancedDeals(dealsData);
      };
      const projectsQuery = query(collection(db, 'timberProjects'), where('phase', '==', 'contracted'));
      const projectsUnsubscribe = onSnapshot(
        projectsQuery,
        (snapshot) => {
          const projectsData = snapshot.docs.map((d) => mapTimberProjectDoc(d));
          setTimberProjects(projectsData);
          void loadDealsForProjects(projectsData);
          setLoading(false);
        },
        snapErr('timberProjects/1099-contracted'),
      );
      return () => {
        projectsUnsubscribe();
      };
    }
    const isTimberSupplierOnly = user.role === 'supplier';
    const unsubscribeFunctions: (() => void)[] = [];

    try {
      if (!isStaffUser && user.role !== 'supplier' && user.role !== 'architect' && user.role !== 'client') {
      // Leads listener - use test or production collection based on setting
      const leadsCollectionName = getLeadsCollectionName();
      const leadsQuery = query(collection(db, leadsCollectionName), orderBy('createdAt', 'desc'));
      const leadsUnsubscribe = onSnapshot(leadsQuery, (snapshot) => {
        const leadsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
          };
        }) as Lead[];
        setLeads(leadsData);
      }, snapErr(`leads:${leadsCollectionName}`));
      unsubscribeFunctions.push(leadsUnsubscribe);

      // Contacts listener
      const contactsQuery = query(collection(db, 'contacts'), orderBy('createdAt', 'desc'));
      const contactsUnsubscribe = onSnapshot(contactsQuery, (snapshot) => {
        const contactsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
            lastContact: convertOptionalTimestamp(data.lastContact),
            nextFollowUp: convertOptionalTimestamp(data.nextFollowUp),
          };
        }) as Contact[];
        setContacts(contactsData);
      }, snapErr('contacts'));
      unsubscribeFunctions.push(contactsUnsubscribe);

      // Deals listener - check for test data preference
      const useTestDeals = localStorage.getItem('crm_use_test_leads') === 'true';
      const dealsCollectionName = useTestDeals ? 'testDeals' : 'deals';
      const dealsQuery = query(collection(db, dealsCollectionName), orderBy('createdAt', 'desc'));
      const dealsUnsubscribe = onSnapshot(dealsQuery, (snapshot) => {
        const dealsData = snapshot.docs.map(doc => {
          const data = doc.data();
          const deal = {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
            expectedCloseDate: convertOptionalTimestamp(data.expectedCloseDate),
            actualCloseDate: convertOptionalTimestamp(data.actualCloseDate),
          } as Deal;

          // Ensure coordinates are properly formatted
          if (data.coordinates) {
            const coords = data.coordinates;
            if (coords.lat !== undefined && coords.lng !== undefined) {
              deal.coordinates = {
                lat: typeof coords.lat === 'number' ? coords.lat : parseFloat(coords.lat),
                lng: typeof coords.lng === 'number' ? coords.lng : parseFloat(coords.lng),
              };
            }
          }

          return deal;
        });

        setDeals(dealsData);
      }, snapErr(`deals:${dealsCollectionName}`));
      unsubscribeFunctions.push(dealsUnsubscribe);

      if (user.role !== 'designer' && user.role !== 'engineer') {
      // Proposals listener
      const proposalsQuery = query(collection(db, 'proposals'), orderBy('createdAt', 'desc'));
      const proposalsUnsubscribe = onSnapshot(proposalsQuery, (snapshot) => {
        const proposalsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
            sentAt: convertOptionalTimestamp(data.sentAt),
            viewedAt: convertOptionalTimestamp(data.viewedAt),
            acceptedAt: convertOptionalTimestamp(data.acceptedAt),
            rejectedAt: convertOptionalTimestamp(data.rejectedAt),
            validUntil: convertOptionalTimestamp(data.validUntil),
          };
        }) as Proposal[];
        setProposals(proposalsData);
      }, snapErr('proposals'));
      unsubscribeFunctions.push(proposalsUnsubscribe);

      // Payments listener
      const paymentsQuery = query(collection(db, 'payments'), orderBy('createdAt', 'desc'));
      const paymentsUnsubscribe = onSnapshot(paymentsQuery, (snapshot) => {
        const paymentsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            dealId: data.dealId || '',
            amount: data.amount || 0,
            currency: data.currency || 'USD',
            description: data.description || '',
            paymentMethod: data.paymentMethod || 'other',
            status: data.status || 'pending',
            createdAt: convertTimestamp(data.createdAt),
            paidAt: convertOptionalTimestamp(data.paidAt),
            refundedAt: convertOptionalTimestamp(data.refundedAt),
          };
        }) as Payment[];
        setPayments(paymentsData);
      }, snapErr('payments'));
      unsubscribeFunctions.push(paymentsUnsubscribe);
      }

      // Tasks listener
      const tasksQuery = query(collection(db, 'tasks'), orderBy('createdAt', 'desc'));
      const tasksUnsubscribe = onSnapshot(tasksQuery, (snapshot) => {
        const tasksData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title || '',
            description: data.description || '',
            assignedTo: data.assignedTo || '',
            relatedTo: data.relatedTo || { entityType: 'lead', entityId: '' },
            priority: data.priority || 'medium',
            status: data.status || 'pending',
            dueDate: convertOptionalTimestamp(data.dueDate),
            createdAt: convertTimestamp(data.createdAt),
          };
        }) as Task[];
        setTasks(tasksData);
      }, snapErr('tasks'));
      unsubscribeFunctions.push(tasksUnsubscribe);

      // SMS Messages listener
      const smsQuery = query(collection(db, 'smsMessages'), orderBy('createdAt', 'desc'));
      const smsUnsubscribe = onSnapshot(smsQuery, (snapshot) => {
        const smsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
          };
        }) as SMSMessage[];
        setSmsMessages(smsData);
      }, snapErr('smsMessages'));
      unsubscribeFunctions.push(smsUnsubscribe);

      // Email Messages listener
      const emailQuery = query(collection(db, 'emailMessages'), orderBy('createdAt', 'desc'));
      const emailUnsubscribe = onSnapshot(emailQuery, (snapshot) => {
        const emailData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
          };
        }) as EmailMessage[];
        setEmailMessages(emailData);
      }, snapErr('emailMessages'));
      unsubscribeFunctions.push(emailUnsubscribe);

      // Pipelines listener
      const pipelinesQuery = query(collection(db, 'pipelines'), orderBy('createdAt', 'desc'));
      const pipelinesUnsubscribe = onSnapshot(pipelinesQuery, (snapshot) => {
        const pipelinesData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
          };
        }) as Pipeline[];
        setPipelines(pipelinesData);
      }, snapErr('pipelines'));
      unsubscribeFunctions.push(pipelinesUnsubscribe);
      }

      if (!isTimberSupplierOnly && user.role !== 'client' && user.role !== 'architect') {
      // Enhanced Deals listener
      const enhancedDealsQuery = query(collection(db, 'enhancedDeals'), orderBy('createdAt', 'desc'));
      const enhancedDealsUnsubscribe = onSnapshot(enhancedDealsQuery, (snapshot) => {
        const dealsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
            currentStageEnteredAt: convertTimestamp(data.currentStageEnteredAt),
            closedAt: convertOptionalTimestamp(data.closedAt),
            firstWonAt: convertOptionalTimestamp(data.firstWonAt),
            expectedCloseDate: convertOptionalTimestamp(data.expectedCloseDate),
            pmFinalizedAt: convertOptionalTimestamp(data.pmFinalizedAt),
            stageHistory: (data.stageHistory || []).map((transition: any) => ({
              ...transition,
              transitionedAt: convertTimestamp(transition.transitionedAt),
            })),
          };
        }) as EnhancedDeal[];
        setEnhancedDeals(dealsData);
      }, snapErr('enhancedDeals'));
      unsubscribeFunctions.push(enhancedDealsUnsubscribe);

      // Timber Projects listener
      const projectsQuery = query(collection(db, 'timberProjects'), orderBy('createdAt', 'desc'));
      const projectsUnsubscribe = onSnapshot(projectsQuery, (snapshot) => {
        const projectsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            // Doc id must win: a stored `id` field (empty/stale) overwrote the
            // Firestore id and hid the PM standup cell (CRM #71).
            id: doc.id,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
            startDate: convertTimestamp(data.startDate),
            targetEndDate: convertTimestamp(data.targetEndDate),
            actualEndDate: convertOptionalTimestamp(data.actualEndDate),
            contractedAt: convertOptionalTimestamp(data.contractedAt),
            standupNoteUpdatedAt: convertOptionalTimestamp(data.standupNoteUpdatedAt),
          };
        }) as TimberProject[];
        setTimberProjects(projectsData);
      }, snapErr('timberProjects'));
      unsubscribeFunctions.push(projectsUnsubscribe);

      // Heavy PM collections (projectTasks, taskGroups, taskComments, taskActivity,
      // projectOnboarding, projectTimeEntries, milestones) no longer stream at boot.
      // Pages that need them org-wide start them via useCRMCollections / requestCrmCollections.

      if (!isStaffUser) {
      // Bids listener
      const bidsQuery = query(collection(db, 'bids'), orderBy('createdAt', 'desc'));
      const bidsUnsubscribe = onSnapshot(bidsQuery, (snapshot) => {
        const bidsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
            sentAt: convertOptionalTimestamp(data.sentAt),
            respondedAt: convertOptionalTimestamp(data.respondedAt),
            sourceDocuments: (data.sourceDocuments || []).map((doc: any) => ({
              ...doc,
              uploadedAt: convertTimestamp(doc.uploadedAt),
            })),
          };
        }) as Bid[];
        setBids(bidsData);
      }, snapErr('bids'));
      unsubscribeFunctions.push(bidsUnsubscribe);

      // UTM Strategies listener
      const utmStrategiesQuery = query(collection(db, 'utmStrategies'), orderBy('updatedAt', 'desc'));
      const utmStrategiesUnsubscribe = onSnapshot(utmStrategiesQuery, (snapshot) => {
        const strategiesData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
            campaigns: (data.campaigns || []).map((campaign: any) => ({
              ...campaign,
              createdAt: convertTimestamp(campaign.createdAt),
              updatedAt: convertTimestamp(campaign.updatedAt),
              startDate: convertOptionalTimestamp(campaign.startDate),
              endDate: convertOptionalTimestamp(campaign.endDate),
              links: (campaign.links || []).map((link: any) => ({
                ...link,
                createdAt: convertTimestamp(link.createdAt),
              })),
              notes: (campaign.notes || []).map((note: any) => ({
                ...note,
                createdAt: convertTimestamp(note.createdAt),
              })),
            })),
          };
        }) as UtmStrategy[];
        setUtmStrategies(strategiesData);
      }, snapErr('utmStrategies'));
      unsubscribeFunctions.push(utmStrategiesUnsubscribe);

      // Strategic Marker Layers listener
      const strategicLayersQuery = query(collection(db, 'strategicLayers'), orderBy('createdAt', 'desc'));
      const strategicLayersUnsubscribe = onSnapshot(strategicLayersQuery, (snapshot) => {
        const layersData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
          };
        }) as StrategicMarkerLayer[];
        setStrategicLayers(layersData);
      }, snapErr('strategicLayers'));
      unsubscribeFunctions.push(strategicLayersUnsubscribe);

      // Strategic Markers listener
      const strategicMarkersQuery = query(collection(db, 'strategicMarkers'), orderBy('createdAt', 'desc'));
      const strategicMarkersUnsubscribe = onSnapshot(strategicMarkersQuery, (snapshot) => {
        const markersData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
          };
        }) as StrategicMarker[];
        setStrategicMarkers(markersData);
      }, snapErr('strategicMarkers'));
      unsubscribeFunctions.push(strategicMarkersUnsubscribe);
      }

      // CRM Users listener
      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const usersUnsubscribe = onSnapshot(usersQuery, (snapshot) => {
        const usersData = snapshot.docs.map(doc => {
          const data = doc.data();
          const { designerHourlyRate: _omitRate, ...safe } = data;
          return {
            uid: doc.id,
            ...safe,
            createdAt: convertTimestamp(data.createdAt),
            lastLogin: convertTimestamp(data.lastLogin),
          };
        }) as CRMUser[];
        setCrmUsers(usersData);
      }, snapErr('users'));
      unsubscribeFunctions.push(usersUnsubscribe);

      if (!isStaffUser) {
      // Architect Invites listener
      const invitesQuery = query(collection(db, 'architectInvites'), orderBy('createdAt', 'desc'));
      const invitesUnsubscribe = onSnapshot(invitesQuery, (snapshot) => {
        const invitesData = snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            acceptedAt: data.acceptedAt ? convertTimestamp(data.acceptedAt) : undefined,
          } as ArchitectInvite;
        });
        setArchitectInvites(invitesData);
      }, snapErr('architectInvites'));
      unsubscribeFunctions.push(invitesUnsubscribe);

      // Engineer Invites listener
      const engInvitesQuery = query(collection(db, 'engineerInvites'), orderBy('createdAt', 'desc'));
      const engInvitesUnsubscribe = onSnapshot(engInvitesQuery, (snapshot) => {
        const invitesData = snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            acceptedAt: data.acceptedAt ? convertTimestamp(data.acceptedAt) : undefined,
          } as EngineerInvite;
        });
        setEngineerInvites(invitesData);
      }, snapErr('engineerInvites'));
      unsubscribeFunctions.push(engInvitesUnsubscribe);

      const cpaInvitesQuery = query(collection(db, 'cpaInvites'), orderBy('createdAt', 'desc'));
      const cpaInvitesUnsubscribe = onSnapshot(cpaInvitesQuery, (snapshot) => {
        const invitesData = snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            acceptedAt: data.acceptedAt ? convertTimestamp(data.acceptedAt) : undefined,
          } as CpaInvite;
        });
        setCpaInvites(invitesData);
      }, snapErr('cpaInvites'));
      unsubscribeFunctions.push(cpaInvitesUnsubscribe);

      const employeeInvitesQuery = query(collection(db, 'employeeInvites'), orderBy('createdAt', 'desc'));
      const employeeInvitesUnsubscribe = onSnapshot(employeeInvitesQuery, (snapshot) => {
        const invitesData = snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            acceptedAt: data.acceptedAt ? convertTimestamp(data.acceptedAt) : undefined,
          } as EmployeeInvite;
        });
        setEmployeeInvites(invitesData);
      }, snapErr('employeeInvites'));
      unsubscribeFunctions.push(employeeInvitesUnsubscribe);
      }

      }

      // Suppliers skip the full CRM users list above; they still need ops + design partners for @mentions.
      if (user.role === 'supplier' || user.role === 'architect') {
        const mentionUsersUnsubscribe = onSnapshot(
          query(
            collection(db, 'users'),
            where('role', 'in', [...EMPLOYEE_MENTION_DIRECTORY_ROLES]),
          ),
          (snapshot) => {
            const usersData = snapshot.docs.map((userDoc) => {
              const data = userDoc.data();
              const { designerHourlyRate: _omitRate, ...safe } = data;
              return {
                uid: userDoc.id,
                ...safe,
                createdAt: convertTimestamp(data.createdAt),
                lastLogin: convertTimestamp(data.lastLogin),
              };
            }) as CRMUser[];
            setCrmUsers(usersData);
          },
          snapErr('supplierMentionUsers'),
        );
        unsubscribeFunctions.push(mentionUsersUnsubscribe);
      }

      const isCrmEmployee = user.role === 'employee' || user.role === 'admin';
      /** Field staff use Project Hub Suppliers to upload quotes — they need RFQ snapshots. */
      const canLoadSupplierRfq = isCrmEmployee || isStaffUser;
      if (canLoadSupplierRfq) {
        const supplierOrgsUnsub = onSnapshot(
          query(collection(db, 'supplierOrgs'), orderBy('createdAt', 'desc')),
          (snapshot) => {
            setSupplierOrgs(
              snapshot.docs.map((d) => {
                const data = d.data();
                return {
                  id: d.id,
                  ...data,
                  createdAt: convertTimestamp(data.createdAt),
                  updatedAt: data.updatedAt ? convertTimestamp(data.updatedAt) : undefined,
                } as SupplierOrg;
              }),
            );
          },
          snapErr('supplierOrgs'),
        );
        unsubscribeFunctions.push(supplierOrgsUnsub);

        const supplierInvUnsub = onSnapshot(
          query(collection(db, 'supplierInvites'), orderBy('createdAt', 'desc')),
          (snapshot) => {
            setSupplierInvites(
              snapshot.docs.map((d) => {
                const data = d.data();
                return {
                  id: d.id,
                  ...data,
                  createdAt: convertTimestamp(data.createdAt),
                  acceptedAt: data.acceptedAt ? convertTimestamp(data.acceptedAt) : undefined,
                } as SupplierInvite;
              }),
            );
          },
          snapErr('supplierInvites'),
        );
        unsubscribeFunctions.push(supplierInvUnsub);

        const rfqProjUnsub = onSnapshot(
          query(collection(db, 'rfqProjects'), orderBy('createdAt', 'desc')),
          (snapshot) => {
            setRfqProjects(
              snapshot.docs.map((d) => {
                const data = sanitizeFirestoreFieldValues(d.data() as Record<string, unknown>);
                return {
                  id: d.id,
                  ...data,
                  createdAt: convertTimestamp(data.createdAt),
                  updatedAt: data.updatedAt ? convertTimestamp(data.updatedAt) : undefined,
                  approvedSubmissionApprovedAt: mapApprovalDateRecord(data.approvedSubmissionApprovedAt),
                  approvedSupplierApprovedAtByRelease: mapNestedApprovalDateRecord(
                    data.approvedSupplierApprovedAtByRelease,
                  ),
                } as RfqProject;
              }),
            );
          },
          snapErr('rfqProjects'),
        );
        unsubscribeFunctions.push(rfqProjUnsub);

        const rfqPkgUnsub = onSnapshot(
          query(collection(db, 'rfqPackages'), orderBy('createdAt', 'desc')),
          (snapshot) => {
            setRfqPackages(
              snapshot.docs.map((d) => {
                const data = d.data();
                return {
                  id: d.id,
                  ...data,
                  createdAt: convertTimestamp(data.createdAt),
                  ...(data.scopeRevisedAt
                    ? { scopeRevisedAt: convertTimestamp(data.scopeRevisedAt) }
                    : {}),
                  ...(data.scopeDriftAcknowledgedAt
                    ? { scopeDriftAcknowledgedAt: convertTimestamp(data.scopeDriftAcknowledgedAt) }
                    : {}),
                  ...(data.quoteDeadline ? { quoteDeadline: convertTimestamp(data.quoteDeadline) } : {}),
                } as RfqPackage;
              }),
            );
          },
          snapErr('rfqPackages'),
        );
        unsubscribeFunctions.push(rfqPkgUnsub);

        const rfqSubUnsub = onSnapshot(
          query(collection(db, 'rfqSubmissions'), orderBy('createdAt', 'desc')),
          (snapshot) => {
            setRfqSubmissions(
              snapshot.docs.map((d) => {
                const data = d.data();
                return {
                  id: d.id,
                  ...data,
                  createdAt: convertTimestamp(data.createdAt),
                  quoteValidUntil: convertOptionalTimestamp(data.quoteValidUntil),
                  rejectedAt: convertOptionalTimestamp(data.rejectedAt),
                  rejectionNoticeSentAt: convertOptionalTimestamp(data.rejectionNoticeSentAt),
                  pmQuoteInputAdjustedAt: convertOptionalTimestamp(data.pmQuoteInputAdjustedAt),
                  splitAt: convertOptionalTimestamp(data.splitAt),
                  paymentTermsDraft: mapPaymentTerms(data.paymentTermsDraft),
                } as RfqSubmission;
              }),
            );
          },
          snapErr('rfqSubmissions'),
        );
        unsubscribeFunctions.push(rfqSubUnsub);
      }

      if (user.role === 'supplier' && user.supplierOrgId) {
        const orgId = user.supplierOrgId;
        const supplierOrgUnsub = onSnapshot(
          doc(db, 'supplierOrgs', orgId),
          (snapshot) => {
            if (!snapshot.exists()) {
              setSupplierOrgs([]);
              return;
            }
            const data = snapshot.data();
            setSupplierOrgs([
              {
                id: snapshot.id,
                ...data,
                createdAt: convertTimestamp(data.createdAt),
                updatedAt: data.updatedAt ? convertTimestamp(data.updatedAt) : undefined,
              } as SupplierOrg,
            ]);
          },
          snapErr('supplierOrgProfile'),
        );
        unsubscribeFunctions.push(supplierOrgUnsub);

        const supRfqProjRowsRef = { primary: [] as RfqProject[], multi: [] as RfqProject[] };
        const mergeSupplierRfqProjects = () => {
          const m = new Map<string, RfqProject>();
          [...supRfqProjRowsRef.primary, ...supRfqProjRowsRef.multi].forEach((p) => {
            if (p.id) m.set(p.id, p);
          });
          setRfqProjects(
            Array.from(m.values()).sort((a, b) => {
              const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
              const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
              return tb - ta;
            }),
          );
        };
        const mapRfqProjectDocs = (snapshot: { docs: Array<{ id: string; data: () => Record<string, unknown> }> }) =>
          snapshot.docs.map((d) => {
            const data = sanitizeFirestoreFieldValues(d.data());
            return {
              id: d.id,
              ...data,
              createdAt: convertTimestamp(data.createdAt),
              updatedAt: data.updatedAt ? convertTimestamp(data.updatedAt) : undefined,
              approvedSubmissionApprovedAt: mapApprovalDateRecord(data.approvedSubmissionApprovedAt),
              approvedSupplierApprovedAtByRelease: mapNestedApprovalDateRecord(
                data.approvedSupplierApprovedAtByRelease,
              ),
            } as RfqProject;
          });
        const supRfqProjPrimary = onSnapshot(
          query(collection(db, 'rfqProjects'), where('supplierOrgId', '==', orgId)),
          (snapshot) => {
            supRfqProjRowsRef.primary = mapRfqProjectDocs(snapshot);
            mergeSupplierRfqProjects();
          },
          snapErr('supplierRfqProjectsPrimary'),
        );
        const supRfqProjMulti = onSnapshot(
          query(collection(db, 'rfqProjects'), where('supplierOrgIds', 'array-contains', orgId)),
          (snapshot) => {
            supRfqProjRowsRef.multi = mapRfqProjectDocs(snapshot);
            mergeSupplierRfqProjects();
          },
          snapErr('supplierRfqProjectsMulti'),
        );
        unsubscribeFunctions.push(supRfqProjPrimary);
        unsubscribeFunctions.push(supRfqProjMulti);

        const supPkg = onSnapshot(
          query(collection(db, 'rfqPackages'), where('supplierOrgId', '==', orgId)),
          (snapshot) => {
            const rows = snapshot.docs
              .map((d) => {
                const data = d.data();
                return {
                  id: d.id,
                  ...data,
                  createdAt: convertTimestamp(data.createdAt),
                  ...(data.scopeRevisedAt
                    ? { scopeRevisedAt: convertTimestamp(data.scopeRevisedAt) }
                    : {}),
                  ...(data.scopeDriftAcknowledgedAt
                    ? { scopeDriftAcknowledgedAt: convertTimestamp(data.scopeDriftAcknowledgedAt) }
                    : {}),
                  ...(data.quoteDeadline ? { quoteDeadline: convertTimestamp(data.quoteDeadline) } : {}),
                } as RfqPackage;
              })
              .sort((a, b) => {
                const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
                const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
                return tb - ta;
              });
            setRfqPackages(rows);
          },
          snapErr('supplierRfqPackages'),
        );
        unsubscribeFunctions.push(supPkg);

        const supSub = onSnapshot(
          query(collection(db, 'rfqSubmissions'), where('supplierOrgId', '==', orgId)),
          (snapshot) => {
            const rows = snapshot.docs
              .map((d) => {
                const data = d.data();
                return {
                  id: d.id,
                  ...data,
                  createdAt: convertTimestamp(data.createdAt),
                  quoteValidUntil: convertOptionalTimestamp(data.quoteValidUntil),
                  rejectedAt: convertOptionalTimestamp(data.rejectedAt),
                  rejectionNoticeSentAt: convertOptionalTimestamp(data.rejectionNoticeSentAt),
                  pmQuoteInputAdjustedAt: convertOptionalTimestamp(data.pmQuoteInputAdjustedAt),
                  splitAt: convertOptionalTimestamp(data.splitAt),
                  paymentTermsDraft: mapPaymentTerms(data.paymentTermsDraft),
                } as RfqSubmission;
              })
              .sort((a, b) => {
                const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
                const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
                return tb - ta;
              });
            setRfqSubmissions(rows);
          },
          snapErr('supplierRfqSubmissions'),
        );
        unsubscribeFunctions.push(supSub);
      }

      // Designers/engineers are denied engagementLetters by rules (client-portal surface).
      if (
        !isTimberSupplierOnly
        && user.role !== 'client'
        && user.role !== 'architect'
        && user.role !== 'designer'
        && user.role !== 'engineer'
      ) {
      // Engagement Letters listener
      const engagementLettersQuery = query(collection(db, 'engagementLetters'), orderBy('createdAt', 'desc'));
      const engagementLettersUnsubscribe = onSnapshot(engagementLettersQuery, (snapshot) => {
        const lettersData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
            sentAt: convertOptionalTimestamp(data.sentAt),
            viewedAt: convertOptionalTimestamp(data.viewedAt),
            signedAt: convertOptionalTimestamp(data.signedAt),
            paidAt: convertOptionalTimestamp(data.paidAt),
            signature: data.signature ? {
              ...data.signature,
              signedAt: convertTimestamp(data.signature.signedAt),
            } : undefined,
          };
        }) as EngagementLetter[];
        setEngagementLetters(lettersData);
      }, snapErr('engagementLetters'));
      unsubscribeFunctions.push(engagementLettersUnsubscribe);
      }

    } catch (err) {
      console.error('Error setting up CRM listeners:', err);
      setError('Failed to load CRM data');
    } finally {
      setLoading(false);
    }

    // Cleanup function
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }, [authLoading, user?.id, user?.role, user?.supplierOrgId]);

  // Suppliers: listen to productionCommentAccess docs by deal id (single-doc get).
  // Collection queries with array-contains + get()-based rules are rejected by Firestore.
  const supplierAccessDealIdsKey = useMemo(() => {
    if (user?.role !== 'supplier') return '';
    const dealIds = new Set<string>();
    for (const p of rfqPackages) {
      const id = p.dealId?.trim();
      if (id) dealIds.add(id);
    }
    for (const p of rfqProjects) {
      const id = p.dealId?.trim();
      if (id) dealIds.add(id);
    }
    for (const p of timberProjects) {
      const id = p.dealId?.trim();
      if (id) dealIds.add(id);
    }
    return Array.from(dealIds).sort().join(',');
  }, [user?.role, rfqPackages, rfqProjects, timberProjects]);

  useEffect(() => {
    if (authLoading || !user?.id || user.role !== 'supplier' || !user.supplierOrgId) {
      return;
    }
    const ids = supplierAccessDealIdsKey
      ? supplierAccessDealIdsKey.split(',').filter(Boolean)
      : [];
    if (!ids.length) {
      setProductionCommentAccess([]);
      return;
    }

    const byId = new Map<string, ProductionCommentAccess>();
    const unsubs: Array<() => void> = [];
    const flush = () => setProductionCommentAccess(Array.from(byId.values()));

    for (const dealId of ids) {
      const accessId = productionCommentAccessId(dealId);
      unsubs.push(
        onSnapshot(
          doc(db, 'productionCommentAccess', accessId),
          (snapshot) => {
            if (!snapshot.exists()) {
              byId.delete(accessId);
              flush();
              return;
            }
            const data = snapshot.data();
            byId.set(accessId, {
              id: snapshot.id,
              dealId: typeof data.dealId === 'string' ? data.dealId : dealId,
              timberOrgIds: Array.isArray(data.timberOrgIds)
                ? data.timberOrgIds.filter((x): x is string => typeof x === 'string')
                : [],
              cncOrgIds: Array.isArray(data.cncOrgIds)
                ? data.cncOrgIds.filter((x): x is string => typeof x === 'string')
                : [],
              underContract: data.underContract === true,
              hubProjectId: typeof data.hubProjectId === 'string' ? data.hubProjectId : undefined,
              updatedAt: data.updatedAt ? convertTimestamp(data.updatedAt) : undefined,
            });
            flush();
          },
          (err) =>
            firestoreSnapshotListenError('CRMContext', `productionCommentAccess:${accessId}`, err, {
              userId: user.id,
              role: user.role,
              email: user.email,
            }),
        ),
      );
    }

    return () => {
      unsubs.forEach((u) => u());
    };
  }, [
    authLoading,
    user?.id,
    user?.role,
    user?.supplierOrgId,
    user?.email,
    supplierAccessDealIdsKey,
  ]);

  useEffect(() => {
    if (user?.role !== 'supplier' || !user.supplierOrgId) return;
    const orgId = user.supplierOrgId;
    const keys = productionCommentAccess
      .filter((row) =>
        // Production is one shared thread per deal — any award (timber or CNC) unlocks it.
        !!(orgId
          && (row.timberOrgIds?.includes(orgId) || row.cncOrgIds?.includes(orgId))),
      )
      .map((row) => row.id || productionCommentAccessId(row.dealId))
      .filter(Boolean);
    const unsubscribeFunctions: (() => void)[] = [];
    const commentsByKey = new Map<string, TaskComment[]>();
    const flush = () => {
      setTaskComments(Array.from(commentsByKey.values()).flat());
    };
    const mapCommentDocs = (snapshot: { docs: Array<{ id: string; data: () => Record<string, unknown> }> }) =>
      snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
        } as TaskComment;
      });
    for (const key of keys) {
      const unsub = onSnapshot(
        query(collection(db, 'taskComments'), where('groupCommentKey', '==', key)),
        (snapshot) => {
          commentsByKey.set(key, mapCommentDocs(snapshot));
          flush();
        },
        (err) =>
          firestoreSnapshotListenError('CRMContext', `supplierTaskComments:${key}`, err, {
            userId: user.id,
            role: user.role,
            email: user.email,
          }),
      );
      unsubscribeFunctions.push(unsub);
    }
    if (!keys.length) setTaskComments([]);
    return () => {
      unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
    };
  }, [
    user?.id,
    user?.role,
    user?.supplierOrgId,
    user?.email,
    productionCommentAccess,
  ]);

  // ── Supplier Project Hub hydration ─────────────────────────────────────────
  // Rules block suppliers from timberProjects/projectTasks/taskGroups, so the
  // hub collections come from the org-scoped `supplierProductionSchedule`
  // callable (awarded projects, Production groups, mirrored checklist tasks).
  // Bumping `supplierScheduleRefreshToken` re-fetches after a task update.
  useEffect(() => {
    if (authLoading || !user?.id || user.role !== 'supplier' || !user.supplierOrgId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchSupplierProductionSchedule();
        if (cancelled) return;
        setTimberProjects(data.projects);
        setTaskGroups(data.groups);
        setProjectTasks(data.tasks);
        if (data.deals.length) {
          setEnhancedDeals(prev => (prev.length ? prev : data.deals));
        }
        setCrmCollectionLoaded(prev => ({
          ...prev,
          projectTasks: true,
          taskGroups: true,
          projectOnboardings: true,
        }));
      } catch (err) {
        console.warn('[CRMContext] supplier schedule hydration failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.id, user?.role, user?.supplierOrgId, supplierScheduleRefreshToken]);

  useEffect(() => {
    if (authLoading) return;
    if (!user?.id || user.role !== 'architect') {
      setArchitectHubReady(true);
      return;
    }

    setArchitectHubReady(false);
    let hubState: ArchitectHubHydrationState = initialArchitectHubHydrationState();

    const applyHubState = (next: ArchitectHubHydrationState) => {
      hubState = next;
      setArchitectHubReady(next.ready);
    };

    const mapTimberProjectDoc = (projectId: string, data: Record<string, unknown>): TimberProject => {
      const safeProject = stripArchitectHiddenProjectFields(data);
      return {
        id: projectId,
        ...safeProject,
        value: 0,
        startDate: convertTimestamp(data.startDate),
        targetEndDate: convertTimestamp(data.targetEndDate),
        actualEndDate: data.actualEndDate ? convertTimestamp(data.actualEndDate) : undefined,
      } as TimberProject;
    };

    const hydrateArchitectHubFromProjects = async (projects: TimberProject[], markCollectionsLoaded: boolean) => {
      setTimberProjects(projects);
      const projectIds = projects.map((p) => p.id).filter((id): id is string => !!id);
      const dealIds = Array.from(new Set(projects.map((p) => p.dealId).filter(Boolean)));
      if (dealIds.length) {
        const deals: EnhancedDeal[] = [];
        for (const dealId of dealIds) {
          const snap = await getDoc(doc(db, 'enhancedDeals', dealId));
          if (snap.exists()) {
            const data = snap.data();
            const { value: _omitDealValue, ...safeDeal } = data;
            deals.push({
              id: snap.id,
              ...safeDeal,
              value: 0,
              createdAt: convertTimestamp(data.createdAt),
              updatedAt: convertTimestamp(data.updatedAt),
            } as EnhancedDeal);
          }
        }
        setEnhancedDeals(deals);
      } else {
        setEnhancedDeals([]);
      }
      if (!projectIds.length) {
        setTaskGroups([]);
        setProjectTasks([]);
        if (markCollectionsLoaded) {
          setCrmCollectionLoaded((prev) => ({
            ...prev,
            projectTasks: true,
            taskGroups: true,
            projectOnboardings: true,
          }));
        }
        return;
      }
      const groupSnaps = await Promise.all(
        projectIds.map((pid) => getDocs(query(collection(db, 'taskGroups'), where('projectId', '==', pid)))),
      );
      const taskSnaps = await Promise.all(
        projectIds.map((pid) => getDocs(query(collection(db, 'projectTasks'), where('projectId', '==', pid)))),
      );
      setTaskGroups(groupSnaps.flatMap((s) => s.docs.map((d) => ({ id: d.id, ...d.data() }) as TaskGroup)));
      setProjectTasks(taskSnaps.flatMap((s) => s.docs.map((d) => ({ id: d.id, ...d.data() }) as ProjectTask)));
      if (markCollectionsLoaded) {
        setCrmCollectionLoaded((prev) => ({
          ...prev,
          projectTasks: true,
          taskGroups: true,
          projectOnboardings: true,
        }));
      }
    };

    const projectMap = new Map<string, TimberProject>();
    let projectUnsubs: Array<() => void> = [];
    let hydrateGeneration = 0;

    const syncProjectsFromMap = (opts: { markCollectionsLoaded: boolean }) => {
      const generation = ++hydrateGeneration;
      const projects = Array.from(projectMap.values()).sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      );
      void hydrateArchitectHubFromProjects(projects, opts.markCollectionsLoaded).then(() => {
        if (generation !== hydrateGeneration) return;
      });
    };

    const unsubUser = onSnapshot(
      doc(db, 'users', user.id),
      (userSnap) => {
        projectUnsubs.forEach((unsub) => unsub());
        projectUnsubs = [];
        projectMap.clear();

        const hubProjectIds = normalizeUidList(userSnap.data()?.architectHubProjectIds);
        const fromCache = userSnap.metadata.fromCache;
        const nextHub = reduceArchitectHubHydration(hubState, {
          type: 'user_doc',
          projectIds: hubProjectIds,
          fromCache,
        });
        applyHubState(nextHub);

        // Cache-only empty (or waiting for server): keep list empty, not ready.
        if (!nextHub.userDocFromServer && !hubProjectIds.length) {
          setTimberProjects([]);
          return;
        }

        if (!hubProjectIds.length) {
          // Server-confirmed empty grant list.
          syncProjectsFromMap({ markCollectionsLoaded: nextHub.ready });
          return;
        }

        for (const projectId of hubProjectIds) {
          const unsubProject = onSnapshot(
            doc(db, 'timberProjects', projectId),
            (projSnap) => {
              if (!projSnap.exists()) {
                projectMap.delete(projectId);
              } else {
                projectMap.set(projectId, mapTimberProjectDoc(projectId, projSnap.data() as Record<string, unknown>));
              }
              const afterProject = reduceArchitectHubHydration(hubState, {
                type: 'project_doc',
                projectId,
              });
              applyHubState(afterProject);
              // Show cards as docs arrive; only mark collections loaded once hub is ready.
              syncProjectsFromMap({ markCollectionsLoaded: afterProject.ready });
            },
            (err) => {
              console.warn('[CRMContext] architect project listen failed', projectId, err);
              // Count a failed listen as "reported" so we don't hang forever.
              const afterProject = reduceArchitectHubHydration(hubState, {
                type: 'project_doc',
                projectId,
              });
              applyHubState(afterProject);
              syncProjectsFromMap({ markCollectionsLoaded: afterProject.ready });
            },
          );
          projectUnsubs.push(unsubProject);
        }
      },
      (err) => {
        console.warn('[CRMContext] architect hub hydration failed', err);
        setArchitectHubReady(true);
      },
    );

    return () => {
      unsubUser();
      projectUnsubs.forEach((unsub) => unsub());
      setArchitectHubReady(false);
    };
  }, [authLoading, user?.id, user?.role]);

  const architectDesignCommentScopes = useMemo(
    () =>
      user?.role === 'architect'
        ? architectDesignCommentScopesFromProjects(timberProjects)
        : [],
    [user?.role, timberProjects],
  );
  const architectDesignComments = useArchitectDesignComments(
    architectDesignCommentScopes,
    !authLoading && user?.role === 'architect',
  );

  // Status + other architect surfaces read taskComments from context. Keep it in
  // sync with the same Design-discussion query Project Hub uses (decisionOnly).
  useEffect(() => {
    if (user?.role !== 'architect') return;
    setTaskComments(architectDesignComments);
    setCrmCollectionLoaded((prev) =>
      prev.taskComments ? prev : { ...prev, taskComments: true },
    );
  }, [user?.role, architectDesignComments]);

  // Lead operations
  const createLead = async (leadData: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const leadsCollectionName = getLeadsCollectionName();
      const now = new Date();

      // Format location for Google Maps readability
      const formattedLocation = formatLocationForMaps(leadData.location);

      // Geocode if no coordinates provided
      let coordinates = leadData.coordinates || null;
      let geocoded = leadData.geocoded ?? (coordinates ? true : false);

      if (!coordinates && formattedLocation) {
        const result = await geocodeAddress(formattedLocation);
        if (result) {
          coordinates = result;
          geocoded = true;
        }
      }

      const leadWithDefaults = {
        ...leadData,
        location: formattedLocation,
        coordinates: coordinates || { lat: 36.85, lng: -75.4 },
        geocoded,
      };

      // Strip undefined fields — Firestore does not accept undefined values
      const cleanData = Object.fromEntries(
        Object.entries({
          ...leadWithDefaults,
          createdAt: Timestamp.fromDate(now),
          updatedAt: Timestamp.fromDate(now),
        }).filter(([_, v]) => v !== undefined)
      );

      const docRef = await addDoc(collection(db, leadsCollectionName), cleanData);

      // Create initial "Call Lead" activity if enabled
      // Schedule for within configured interval during working hours (or next working time)
      if (isAutoCreateInitialCallEnabled()) {
        const callCadence = getCallCadenceConfig();
        const initialCallTime = calculateNextCallTime(null, 0, now, callCadence);

        if (initialCallTime) {
          const leadName = `${leadData.firstName || ''} ${leadData.lastName || ''}`.trim() || 'New Lead';

          const activityData = {
            type: 'call' as ActivityType,
            title: `Call ${leadName}`,
            description: 'Initial call to new lead',
            entityType: 'lead' as const,
            entityId: docRef.id,
            entityName: leadName,
            scheduledAt: Timestamp.fromDate(initialCallTime),
            dueDate: Timestamp.fromDate(initialCallTime),
            createdBy: leadData.addedBy || 'system',
            assignedTo: leadData.addedBy || 'system',
            status: 'scheduled' as ActivityStatus,
            createdAt: Timestamp.fromDate(now),
            updatedAt: Timestamp.fromDate(now),
          };

          await addDoc(collection(db, 'activities'), activityData);
        }
      }

      return docRef.id;
    } catch (err) {
      console.error('Error creating lead:', err);
      throw err;
    }
  };

  // Helper function to remove undefined values from objects (Firestore doesn't allow undefined)
  const removeUndefined = (obj: any): any => {
    if (obj === null || obj === undefined) {
      return null;
    }
    if (obj instanceof Date || obj instanceof Timestamp) {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map(removeUndefined);
    }
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
          cleaned[key] = removeUndefined(value);
        }
      }
      return cleaned;
    }
    return obj;
  };

  const updateLead = async (id: string, updates: Partial<Lead>): Promise<void> => {
    try {
      const leadsCollectionName = getLeadsCollectionName();
      const docRef = doc(db, leadsCollectionName, id);

      // If coordinates are being EXPLICITLY removed or set to null/undefined, assign default North Atlantic location
      // Only apply this if 'coordinates' is actually a key in the updates object (not just missing)
      const updatesWithDefaults = { ...updates };
      if ('coordinates' in updates && (updates.coordinates === null || updates.coordinates === undefined)) {
        updatesWithDefaults.coordinates = { lat: 36.85, lng: -75.4 }; // 50 miles off Norfolk, VA
        updatesWithDefaults.geocoded = false;
      }

      // Remove undefined values before saving to Firestore
      let cleanedUpdates = removeUndefined({
        ...updatesWithDefaults,
        updatedAt: Timestamp.fromDate(new Date()),
      });

      // POC edit log (Matthew Lynch only): stamp lastEdited* and write auditLog.
      const { withCrmAuditUpdate } = await import('./crmAuditLog');
      if (user?.id) {
        cleanedUpdates = await withCrmAuditUpdate(
          { uid: user.id, email: user.email, name: user.name },
          {
            collection: leadsCollectionName,
            docId: id,
            action: 'update',
            entityType: 'lead',
            entityId: id,
            changedFields: Object.keys(updates),
          },
          cleanedUpdates as Record<string, unknown>,
        ) as typeof cleanedUpdates;
      }

      await updateDoc(docRef, cleanedUpdates);

      if (
        'firstName' in updates
        || 'lastName' in updates
        || 'company' in updates
      ) {
        const existing = leads.find(l => l.id === id);
        const merged = { ...existing, ...updatesWithDefaults };
        void propagateLeadClientIdentityToProjections(id, {
          firstName: merged.firstName,
          lastName: merged.lastName,
          company: merged.company,
        }).catch(err => {
          console.warn('[updateLead] client identity projection sync failed:', err);
        });
      }

    } catch (err) {
      console.error('Error updating lead:', err);
      throw err;
    }
  };

  const deleteLead = async (id: string): Promise<void> => {
    try {
      const leadsCollectionName = getLeadsCollectionName();

      // Also delete any enhanced deals associated with this lead
      const dealsToDelete = enhancedDeals.filter(deal => deal.leadId === id);
      for (const deal of dealsToDelete) {
        if (deal.id) {
          await deleteDoc(doc(db, 'enhancedDeals', deal.id));
        }
      }

      // Delete the lead
      await deleteDoc(doc(db, leadsCollectionName, id));

    } catch (err) {
      console.error('Error deleting lead:', err);
      throw err;
    }
  };

  const bulkUpdateLeads = async (ids: string[], updates: Partial<Lead>): Promise<void> => {
    try {
      const leadsCollectionName = getLeadsCollectionName();
      const batch = ids.map(id => {
        const docRef = doc(db, leadsCollectionName, id);
        // Ensure coordinates default if being EXPLICITLY removed
        const updatesWithDefaults = { ...updates };
        if ('coordinates' in updates && (updates.coordinates === null || updates.coordinates === undefined)) {
          updatesWithDefaults.coordinates = { lat: 36.85, lng: -75.4 };
          updatesWithDefaults.geocoded = false;
        }
        return updateDoc(docRef, {
          ...updatesWithDefaults,
          updatedAt: Timestamp.now(),
        });
      });
      await Promise.all(batch);

    } catch (err) {
      console.error('Error bulk updating leads:', err);
      throw err;
    }
  };

  const bulkDeleteLeads = async (ids: string[]): Promise<void> => {
    try {
      const leadsCollectionName = getLeadsCollectionName();
      const batch = ids.map(id => {
        const docRef = doc(db, leadsCollectionName, id);
        return deleteDoc(docRef);
      });
      await Promise.all(batch);

    } catch (err) {
      console.error('Error bulk deleting leads:', err);
      throw err;
    }
  };

  const bulkArchiveLeads = async (ids: string[], archived: boolean): Promise<void> => {
    try {
      const leadsCollectionName = getLeadsCollectionName();
      const batch = ids.map(id => {
        const docRef = doc(db, leadsCollectionName, id);
        return updateDoc(docRef, {
          archived,
          updatedAt: Timestamp.now(),
        });
      });
      await Promise.all(batch);

    } catch (err) {
      console.error('Error bulk archiving leads:', err);
      throw err;
    }
  };

  // Create initial call activities for recent leads that don't have any calls
  const createInitialCallsForRecentLeads = async (count: number = 30): Promise<{ created: number; skipped: number }> => {
    try {
      const now = new Date();

      // Sort leads by creation date (most recent first) and take the specified count
      const recentLeads = [...leads]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, count);

      let created = 0;
      let skipped = 0;

      for (const lead of recentLeads) {
        if (!lead.id) {
          skipped++;
          continue;
        }

        // Check if this lead already has any call activities (scheduled or completed)
        const existingActivities = await getDocs(
          query(
            collection(db, 'activities'),
            where('entityType', '==', 'lead'),
            where('entityId', '==', lead.id),
            limit(10),
          ),
        );
        const hasCallActivity = existingActivities.docs.some(d => d.data().type === 'call');

        if (hasCallActivity) {
          skipped++;
          continue;
        }

        // Create initial call activity
        const callCadence = getCallCadenceConfig();
        const initialCallTime = calculateNextCallTime(null, 0, new Date(lead.createdAt), callCadence);

        if (initialCallTime) {
          const leadName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead';

          const activityData = {
            type: 'call' as ActivityType,
            title: `Call ${leadName}`,
            description: 'Initial call to lead',
            entityType: 'lead' as const,
            entityId: lead.id,
            entityName: leadName,
            scheduledAt: Timestamp.fromDate(initialCallTime),
            dueDate: Timestamp.fromDate(initialCallTime),
            createdBy: lead.addedBy || 'system',
            assignedTo: lead.addedBy || 'system',
            status: 'scheduled' as ActivityStatus,
            createdAt: Timestamp.fromDate(now),
            updatedAt: Timestamp.fromDate(now),
          };

          await addDoc(collection(db, 'activities'), activityData);
          created++;
        } else {
          skipped++;
        }
      }

      return { created, skipped };
    } catch (err) {
      console.error('Error creating initial calls for recent leads:', err);
      throw err;
    }
  };

  // Contact operations
  const createContact = async (contactData: Omit<Contact, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'contacts'), {
        ...contactData,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });


      return docRef.id;
    } catch (err) {
      console.error('Error creating contact:', err);
      throw err;
    }
  };

  const updateContact = async (id: string, updates: Partial<Contact>): Promise<void> => {
    try {
      const docRef = doc(db, 'contacts', id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date()),
      });

    } catch (err) {
      console.error('Error updating contact:', err);
      throw err;
    }
  };

  const deleteContact = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'contacts', id));

    } catch (err) {
      console.error('Error deleting contact:', err);
      throw err;
    }
  };

  // Deal operations
  const createDeal = async (dealData: Omit<Deal, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'deals'), {
        ...dealData,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });



      return docRef.id;
    } catch (err) {
      console.error('Error creating deal:', err);
      throw err;
    }
  };

  const updateDeal = async (id: string, updates: Partial<Deal>): Promise<void> => {
    try {
      // Check if using test data
      const useTestDeals = localStorage.getItem('crm_use_test_leads') === 'true';
      const dealsCollectionName = useTestDeals ? 'testDeals' : 'deals';
      const docRef = doc(db, dealsCollectionName, id);

      // Remove undefined values before updating
      const cleanedUpdates = removeUndefined(updates);

      await updateDoc(docRef, {
        ...cleanedUpdates,
        updatedAt: Timestamp.fromDate(new Date()),
      });


    } catch (err) {
      console.error('Error updating deal:', err);
      throw err;
    }
  };

  const deleteDeal = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'deals', id));

    } catch (err) {
      console.error('Error deleting deal:', err);
      throw err;
    }
  };

  const deleteAllTestDeals = async (): Promise<{ success: number; failed: number }> => {
    try {
      const testDealsCollection = collection(db, 'testDeals');
      const testDealsSnapshot = await getDocs(testDealsCollection);

      if (testDealsSnapshot.empty) {
        return { success: 0, failed: 0 };
      }

      const results = {
        success: 0,
        failed: 0,
      };

      // Delete all test deals
      const deletePromises = testDealsSnapshot.docs.map(async (document) => {
        try {
          await deleteDoc(doc(db, 'testDeals', document.id));
          results.success++;

        } catch (error) {
          console.error(`Failed to delete test deal ${document.id}:`, error);
          results.failed++;
        }
      });

      await Promise.all(deletePromises);

      return results;
    } catch (err) {
      console.error('Error deleting all test deals:', err);
      throw err;
    }
  };

  // Proposal operations
  const createProposal = async (proposalData: Omit<Proposal, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'proposals'), {
        ...proposalData,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating proposal:', err);
      throw err;
    }
  };

  const updateProposal = async (id: string, updates: Partial<Proposal>): Promise<void> => {
    try {
      const docRef = doc(db, 'proposals', id);
      let patch: Record<string, unknown> = {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date()),
      };
      if (user?.id) {
        const { withCrmAuditUpdate } = await import('./crmAuditLog');
        patch = await withCrmAuditUpdate(
          { uid: user.id, email: user.email, name: user.name },
          {
            collection: 'proposals',
            docId: id,
            action: 'update',
            entityType: 'proposal',
            entityId: id,
            changedFields: Object.keys(updates),
          },
          patch,
        );
      }
      await updateDoc(docRef, patch as Record<string, unknown> as never);
    } catch (err) {
      console.error('Error updating proposal:', err);
      throw err;
    }
  };

  const deleteProposal = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'proposals', id));
    } catch (err) {
      console.error('Error deleting proposal:', err);
      throw err;
    }
  };

  // Payment operations
  const createPayment = async (paymentData: Omit<Payment, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'payments'), {
        ...paymentData,
        createdAt: Timestamp.fromDate(now),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating payment:', err);
      throw err;
    }
  };

  const updatePayment = async (id: string, updates: Partial<Payment>): Promise<void> => {
    try {
      const docRef = doc(db, 'payments', id);
      await updateDoc(docRef, updates);
    } catch (err) {
      console.error('Error updating payment:', err);
      throw err;
    }
  };

  const deletePayment = async (id: string): Promise<void> => {
    try {
      const docRef = doc(db, 'payments', id);
      await deleteDoc(docRef);
    } catch (err) {
      console.error('Error deleting payment:', err);
      throw err;
    }
  };

  // Task operations
  const createTask = async (taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'tasks'), {
        ...taskData,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating task:', err);
      throw err;
    }
  };

  const updateTask = async (id: string, updates: Partial<Task>): Promise<void> => {
    try {
      const docRef = doc(db, 'tasks', id);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date()),
      });
      if (updates.status === 'completed') {
        const task = tasks.find(t => t.id === id);
        if (task) {
          // Task completion logic can be added here
        }
      }
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  };

  const deleteTask = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'tasks', id));
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  };

  // SMS Message operations
  const createSMSMessage = async (messageData: Omit<SMSMessage, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const docRef = await addDoc(collection(db, 'smsMessages'), {
        ...messageData,
        createdAt: Timestamp.fromDate(new Date()),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating SMS message:', err);
      throw err;
    }
  };

  const updateSMSMessage = async (id: string, updates: Partial<SMSMessage>): Promise<void> => {
    try {
      const docRef = doc(db, 'smsMessages', id);
      await updateDoc(docRef, updates);
    } catch (err) {
      console.error('Error updating SMS message:', err);
      throw err;
    }
  };

  // Email Message operations
  const createEmailMessage = async (messageData: Omit<EmailMessage, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const docRef = await addDoc(collection(db, 'emailMessages'), {
        ...messageData,
        createdAt: Timestamp.fromDate(new Date()),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating email message:', err);
      throw err;
    }
  };

  const updateEmailMessage = async (id: string, updates: Partial<EmailMessage>): Promise<void> => {
    try {
      const docRef = doc(db, 'emailMessages', id);
      await updateDoc(docRef, updates);
    } catch (err) {
      console.error('Error updating email message:', err);
      throw err;
    }
  };

  // ==========================================
  // PIPELINE OPERATIONS
  // ==========================================

  const createPipeline = async (pipelineData: Omit<Pipeline, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'pipelines'), {
        ...pipelineData,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating pipeline:', err);
      throw err;
    }
  };

  const updatePipeline = async (id: string, updates: Partial<Pipeline>): Promise<void> => {
    try {
      const docRef = doc(db, 'pipelines', id);
      await updateDoc(docRef, {
        ...removeUndefined(updates),
        updatedAt: Timestamp.fromDate(new Date()),
      });
    } catch (err) {
      console.error('Error updating pipeline:', err);
      throw err;
    }
  };

  const deletePipeline = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'pipelines', id));
    } catch (err) {
      console.error('Error deleting pipeline:', err);
      throw err;
    }
  };

  const getDefaultPipeline = (): Pipeline | undefined => {
    return pipelines.find(p => p.isDefault) || pipelines[0];
  };

  const initializeDefaultPipelines = async (): Promise<void> => {
    try {
      const pipelinesSnapshot = await getDocs(collection(db, 'pipelines'));

      if (pipelinesSnapshot.empty) {
        // Fresh install — create unified pipeline
        for (const pipelineConfig of DEFAULT_PIPELINES) {
          await createPipeline(pipelineConfig);
        }
        return;
      }

      // Check if we need to migrate from old dual-pipeline to unified
      const existingPipelines = pipelinesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const hasOldKits = existingPipelines.some(p => (p as any).name === 'Timber Frame Kits');
      const hasOldCustom = existingPipelines.some(p => (p as any).name === 'Custom Timber Frames');
      const hasUnified = existingPipelines.some(p => (p as any).name === 'Timber Frames');

      if ((hasOldKits || hasOldCustom) && !hasUnified) {
        // Create the unified pipeline
        const unifiedRef = await addDoc(collection(db, 'pipelines'), {
          ...DEFAULT_PIPELINES[0],
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        const unifiedId = unifiedRef.id;

        // Collect old pipeline IDs
        const oldPipelineIds = existingPipelines
          .filter(p => (p as any).name === 'Timber Frame Kits' || (p as any).name === 'Custom Timber Frames')
          .map(p => p.id);

        // Remap all deals from old pipelines to unified
        const dealsSnapshot = await getDocs(collection(db, 'enhancedDeals'));
        let migratedCount = 0;
        for (const dealDoc of dealsSnapshot.docs) {
          const deal = dealDoc.data();
          if (oldPipelineIds.includes(deal.pipelineId)) {
            const oldStage = deal.stageId as string;
            let newStageId = oldStage;
            let designSubStage: string | undefined;

            // Map old stages to new unified stages
            if (oldStage === 'payment-requested') {
              newStageId = 'design';
              designSubStage = 'payment-requested';
            } else if (oldStage === 'design') {
              newStageId = 'design';
              designSubStage = 'in-design';
            } else if (oldStage === 'delivered') {
              newStageId = 'design';
              designSubStage = 'design-complete';
            }
            // 'qualified' and 'estimated' stay as-is

            const updateData: any = {
              pipelineId: unifiedId,
              stageId: newStageId,
              updatedAt: Timestamp.now(),
            };
            if (designSubStage) {
              updateData.designSubStage = designSubStage;
            }

            await updateDoc(doc(db, 'enhancedDeals', dealDoc.id), updateData);
            migratedCount++;
          }
        }

        // Delete old pipelines
        for (const oldId of oldPipelineIds) {
          await deleteDoc(doc(db, 'pipelines', oldId));
        }
      } else {
        // Already on unified — just clean up duplicates
        const seen = new Map<string, string>();
        const toDelete: string[] = [];
        pipelinesSnapshot.docs.forEach(d => {
          const data = d.data();
          const name = data.name as string;
          if (seen.has(name)) {
            toDelete.push(d.id);
          } else {
            seen.set(name, d.id);
          }
        });
        for (const id of toDelete) {
          await deleteDoc(doc(db, 'pipelines', id));
        }

        // Repair orphaned deals — remap any deal pointing to a non-existent pipeline
        const validPipelineIds = new Set(pipelinesSnapshot.docs.filter(d => !toDelete.includes(d.id)).map(d => d.id));
        const unifiedPipeline = pipelinesSnapshot.docs.find(d => !toDelete.includes(d.id) && (d.data() as any).name === 'Timber Frames');
        if (unifiedPipeline) {
          const unifiedId = unifiedPipeline.id;
          const dealsSnapshot = await getDocs(collection(db, 'enhancedDeals'));
          let repairedCount = 0;
          for (const dealDoc of dealsSnapshot.docs) {
            const deal = dealDoc.data();
            if (deal.pipelineId && !validPipelineIds.has(deal.pipelineId)) {
              const oldStage = deal.stageId as string;
              let newStageId = oldStage;
              let designSubStage: string | undefined = deal.designSubStage;

              // Map old stages to new unified stages
              if (oldStage === 'payment-requested') {
                newStageId = 'design';
                designSubStage = designSubStage || 'payment-requested';
              } else if (oldStage === 'design') {
                newStageId = 'design';
                designSubStage = designSubStage || 'in-design';
              } else if (oldStage === 'delivered') {
                newStageId = 'design';
                designSubStage = designSubStage || 'design-complete';
              }
              // qualified and estimated stay as-is

              const updateData: any = {
                pipelineId: unifiedId,
                stageId: newStageId,
                updatedAt: Timestamp.now(),
              };
              if (designSubStage) {
                updateData.designSubStage = designSubStage;
              }

              await updateDoc(doc(db, 'enhancedDeals', dealDoc.id), updateData);
              repairedCount++;
            }
          }
        }
      }
    } catch (err) {
      console.error('Error initializing default pipelines:', err);
      throw err;
    }
  };

  // ==========================================
  // ENHANCED DEAL OPERATIONS
  // ==========================================

  const createEnhancedDeal = async (
    dealData: Omit<EnhancedDeal, 'id' | 'createdAt' | 'updatedAt' | 'stageHistory'>
  ): Promise<string> => {
    try {
      const now = new Date();
      // Remove undefined values to prevent Firestore errors
      const cleanedDealData = removeUndefined(dealData);
      const docRef = await addDoc(collection(db, 'enhancedDeals'), {
        ...cleanedDealData,
        stageHistory: [{
          fromStage: null,
          toStage: dealData.stageId,
          transitionedAt: Timestamp.fromDate(now),
          durationMs: 0,
          transitionedBy: dealData.assignedTo || 'system',
        }],
        currentStageEnteredAt: Timestamp.fromDate(now),
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating enhanced deal:', err);
      throw err;
    }
  };

  const updateEnhancedDeal = async (id: string, updates: Partial<EnhancedDeal>): Promise<void> => {
    const valueForTimber = updates.value;
    try {
      const docRef = doc(db, 'enhancedDeals', id);
      await updateDoc(docRef, {
        ...removeUndefined(updates),
        updatedAt: Timestamp.fromDate(new Date()),
      });

      // Project Hub sidebar shows `timberProjects.value` — keep in sync when deal value changes (e.g. proposal save)
      if (typeof valueForTimber === 'number') {
        try {
          const qp = query(collection(db, 'timberProjects'), where('dealId', '==', id));
          const snap = await getDocs(qp);
          const nowTs = Timestamp.fromDate(new Date());
          await Promise.all(
            snap.docs.map((d) => updateDoc(d.ref, { value: valueForTimber, updatedAt: nowTs })),
          );
        } catch (syncErr) {
          console.warn('[CRM] Could not sync timber project value for deal', id, syncErr);
        }
      }
    } catch (err) {
      console.error('Error updating enhanced deal:', err);
      throw err;
    }
  };

  const deleteEnhancedDeal = async (id: string): Promise<void> => {
    try {
      // Also delete associated proposal if it exists
      const proposalRef = doc(db, 'proposalBuilders', id);
      try {
        await deleteDoc(proposalRef);
      } catch {
        // Proposal may not exist, that's OK
      }

      // Delete the deal itself
      await deleteDoc(doc(db, 'enhancedDeals', id));
    } catch (err) {
      console.error('Error deleting enhanced deal:', err);
      throw err;
    }
  };

  const moveDealToStage = async (dealId: string, newStageId: string, userId: string): Promise<void> => {
    try {
      const deal = enhancedDeals.find(d => d.id === dealId);
      if (!deal) throw new Error('Deal not found');

      const now = new Date();
      const previousStageEnteredAt = deal.currentStageEnteredAt;
      const durationMs = now.getTime() - new Date(previousStageEnteredAt).getTime();

      const newTransition: StageTransition = {
        fromStage: deal.stageId,
        toStage: newStageId,
        transitionedAt: now,
        durationMs,
        transitionedBy: userId,
      };

      // Get probability from pipeline stage
      const pipeline = pipelines.find(p => p.id === deal.pipelineId);
      const newStage = pipeline?.stages.find(s => s.id === newStageId);
      const newProbability = newStage?.probability || deal.probability;

      await updateEnhancedDeal(dealId, {
        stageId: newStageId,
        stageHistory: [...deal.stageHistory, newTransition],
        currentStageEnteredAt: now,
        probability: newProbability,
      });

      // Auto-create preconstruction project when deal enters design stage
      if (newStageId === 'design' && !timberProjects.some(p => p.dealId === dealId)) {
        try {
          await createPreconProject(deal, userId);
        } catch (projErr) {
          console.error('Auto precon project creation failed:', projErr);
        }
      }
    } catch (err) {
      console.error('Error moving deal to stage:', err);
      throw err;
    }
  };

  const markDealWon = async (dealId: string, userId: string): Promise<void> => {
    try {
      const dealSnap = await getDoc(doc(db, 'enhancedDeals', dealId));
      if (!dealSnap.exists()) throw new Error('Deal not found');
      const deal = { id: dealId, ...dealSnap.data() } as EnhancedDeal;

      const now = new Date();
      const prevEntered = deal.currentStageEnteredAt
        ? new Date(deal.currentStageEnteredAt)
        : new Date(deal.createdAt);
      const durationMs = Math.max(0, now.getTime() - prevEntered.getTime());

      let wonStageId = deal.stageId;
      const pipelineId = deal.pipelineId;
      if (pipelineId) {
        try {
          const pipelineSnap = await getDoc(doc(db, 'pipelines', pipelineId));
          if (pipelineSnap.exists()) {
            const stages = (pipelineSnap.data().stages || []) as Array<{ id: string; name: string; order: number }>;
            const picked = pickWonPipelineStageId(stages);
            if (picked) wonStageId = picked;
          }
        } catch {
          /* keep wonStageId */
        }
      }

      const wonTransition: StageTransition = {
        fromStage: deal.stageId,
        toStage: wonStageId,
        transitionedAt: now,
        durationMs,
        transitionedBy: userId,
      };

      await updateEnhancedDeal(dealId, {
        outcome: 'won',
        stageId: wonStageId,
        closedAt: now,
        // Write-once: first transition to won only.
        ...(deal.firstWonAt ? {} : { firstWonAt: now }),
        probability: 100,
        currentStageEnteredAt: now,
        stageHistory: [...(deal.stageHistory || []), wonTransition],
      });
    } catch (err) {
      console.error('Error marking deal as won:', err);
      throw err;
    }
  };

  const markDealLost = async (dealId: string, reason: string, userId: string): Promise<void> => {
    try {
      const deal = enhancedDeals.find(d => d.id === dealId);
      if (!deal) throw new Error('Deal not found');

      const now = new Date();
      const durationMs = now.getTime() - new Date(deal.currentStageEnteredAt).getTime();

      const lostTransition: StageTransition = {
        fromStage: deal.stageId,
        toStage: 'lost',
        transitionedAt: now,
        durationMs,
        transitionedBy: userId,
      };

      await updateEnhancedDeal(dealId, {
        outcome: 'lost',
        lostReason: reason,
        closedAt: now,
        probability: 0,
        stageHistory: [...deal.stageHistory, lostTransition],
      });

      // Do not change the lead record (no status flip, no archive). Lost is tracked on the deal only.
    } catch (err) {
      console.error('Error marking deal as lost:', err);
      throw err;
    }
  };

  const markDealActive = async (dealId: string, userId: string): Promise<void> => {
    try {
      const deal = enhancedDeals.find(d => d.id === dealId);
      if (!deal) throw new Error('Deal not found');

      const pipeline = pipelines.find(p => p.id === deal.pipelineId);
      const stage = pipeline?.stages.find(s => s.id === deal.stageId);
      const probability = stage?.probability ?? 20;

      const now = new Date();
      const reactivateTransition: StageTransition = {
        fromStage: 'lost',
        toStage: deal.stageId,
        transitionedAt: now,
        durationMs: 0,
        transitionedBy: userId,
      };

      const docRef = doc(db, 'enhancedDeals', dealId);
      await updateDoc(docRef, {
        outcome: 'active',
        probability,
        lostReason: deleteField(),
        closedAt: deleteField(),
        // firstWonAt is write-once — never cleared on reactivate.
        stageHistory: [...deal.stageHistory, reactivateTransition],
        updatedAt: Timestamp.fromDate(now),
      });

      if (deal.leadId) {
        await updateLead(deal.leadId, {
          status: 'qualified',
          archived: false,
        });
      }
    } catch (err) {
      console.error('Error reactivating deal:', err);
      throw err;
    }
  };

  const convertLeadToDeal = async (
    lead: Lead,
    pipelineId: string,
    userId: string,
    naming?: { projectNumber?: string; clientLabel: string; descriptor: string },
  ): Promise<string> => {
    try {
      const pipeline = pipelines.find(p => p.id === pipelineId) || getDefaultPipeline();
      if (!pipeline) throw new Error('No pipeline found');

      const firstStage = pipeline.stages.sort((a, b) => a.order - b.order)[0];
      if (!firstStage) throw new Error('Pipeline has no stages');

      // Build the canonical title from the naming convention when supplied.
      // Legacy fallback (no naming param) keeps the old "${first} ${last} - ${projectType}"
      // format so unmigrated callers still work during the rollout.
      const legacyTitle = `${lead.firstName} ${lead.lastName} - ${lead.projectType}`;
      const canonicalTitle = naming
        ? [naming.projectNumber, naming.clientLabel, naming.descriptor].filter(Boolean).join(' ').trim()
        : legacyTitle;

      const dealData: Omit<EnhancedDeal, 'id' | 'createdAt' | 'updatedAt' | 'stageHistory'> = {
        pipelineId: pipeline.id!,
        stageId: firstStage.id,
        leadId: lead.id,
        contactId: lead.id || '',
        contactName: `${lead.firstName} ${lead.lastName}`,
        company: lead.company,
        ...(naming
          ? {
              ...(naming.projectNumber?.trim()
                ? { projectNumber: naming.projectNumber.trim() }
                : {}),
              clientLabel: naming.clientLabel,
              descriptor: naming.descriptor,
            }
          : {}),
        title: canonicalTitle,
        value: lead.budget,
        outcome: 'active',
        probability: firstStage.probability,
        currentStageEnteredAt: new Date(),
        colorBar: getSourceColor(lead.source),
        projectType: lead.projectType,
        location: lead.location,
        address: lead.address,
        coordinates: lead.coordinates,
        budget: lead.budget,
        source: lead.source,
        timeline: lead.timeline, // Carry over timeline from lead
        assignedTo: lead.assignedTo || userId,
        description: lead.notes,
      };

      const dealId = await createEnhancedDeal(dealData);

      if (lead.id) {
        await updateLead(lead.id, {
          convertedToDeal: dealId,
          dealIds: [dealId],
        });
        // Stamp dealId on existing leadComms in place (no copy) so history stays linked.
        linkLeadCommsToDeal(lead.id, dealId).catch(err =>
          console.warn('Lead comms → deal link failed (non-fatal):', err),
        );
      }

      return dealId;
    } catch (err) {
      console.error('Error converting lead to deal:', err);
      throw err;
    }
  };

  const createProjectForLead = async (
    lead: Lead,
    projectName: string,
    projectType: string,
    pipelineId: string,
    userId: string,
    budget?: number,
    naming?: { projectNumber?: string; clientLabel: string; descriptor: string },
  ): Promise<string> => {
    try {
      const pipeline = pipelines.find(p => p.id === pipelineId) || getDefaultPipeline();
      if (!pipeline) throw new Error('No pipeline found');

      const firstStage = pipeline.stages.sort((a, b) => a.order - b.order)[0];
      if (!firstStage) throw new Error('Pipeline has no stages');

      const legacyTitle = `${lead.firstName} ${lead.lastName} - ${projectName}`;
      const canonicalTitle = naming
        ? [naming.projectNumber, naming.clientLabel, naming.descriptor].filter(Boolean).join(' ').trim()
        : legacyTitle;

      const dealData: Omit<EnhancedDeal, 'id' | 'createdAt' | 'updatedAt' | 'stageHistory'> = {
        pipelineId: pipeline.id!,
        stageId: firstStage.id,
        leadId: lead.id,
        contactId: lead.id || '',
        contactName: `${lead.firstName} ${lead.lastName}`,
        company: lead.company,
        ...(naming
          ? {
              ...(naming.projectNumber?.trim()
                ? { projectNumber: naming.projectNumber.trim() }
                : {}),
              clientLabel: naming.clientLabel,
              descriptor: naming.descriptor,
            }
          : {}),
        title: canonicalTitle,
        projectName,
        value: budget || lead.budget || 0,
        outcome: 'active',
        probability: firstStage.probability,
        currentStageEnteredAt: new Date(),
        colorBar: getSourceColor(lead.source),
        projectType,
        location: lead.location,
        address: lead.address,
        coordinates: lead.coordinates,
        budget: budget || lead.budget || 0,
        source: lead.source,
        timeline: lead.timeline,
        assignedTo: lead.assignedTo || userId,
        description: '',
      };

      const dealId = await createEnhancedDeal(dealData);

      if (lead.id) {
        const existingIds = lead.dealIds || [];
        if (!lead.convertedToDeal) {
          await updateLead(lead.id, {
            convertedToDeal: dealId,
            dealIds: [...existingIds, dealId],
          });
        } else {
          await updateLead(lead.id, {
            dealIds: [...existingIds, dealId],
          });
        }
        linkLeadCommsToDeal(lead.id, dealId).catch(err =>
          console.warn('Lead comms → deal link failed (non-fatal):', err),
        );
      }

      return dealId;
    } catch (err) {
      console.error('Error creating project for lead:', err);
      throw err;
    }
  };

  const linkDealToLead = async (dealId: string, leadId: string): Promise<void> => {
    try {
      await updateEnhancedDeal(dealId, { leadId } as any);
      const lead = leads.find(l => l.id === leadId);
      if (lead) {
        const existingIds = lead.dealIds || [];
        if (!existingIds.includes(dealId)) {
          const updates: Partial<Lead> = { dealIds: [...existingIds, dealId] };
          if (!lead.convertedToDeal) {
            updates.convertedToDeal = dealId;
          }
          await updateLead(leadId, updates);
        }
      }
      linkLeadCommsToDeal(leadId, dealId).catch(err =>
        console.warn('Lead comms → deal link failed (non-fatal):', err),
      );
    } catch (err) {
      console.error('Error linking deal to lead:', err);
      throw err;
    }
  };

  const transferDealToLead = async (
    dealId: string,
    targetLeadId: string,
    userId: string,
  ): Promise<TransferDealToLeadResult> => {
    return runTransferDealToLead({
      dealId,
      targetLeadId,
      userId,
      leadsCollectionName: getLeadsCollectionName(),
      createActivity,
    });
  };

  // ==========================================
  // ACTIVITY OPERATIONS
  // ==========================================

  const createActivity = async (activityData: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();

      // Prepare activity data with proper date conversions
      const dataToSave: any = {
        ...activityData,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      };

      // Convert Date objects to Timestamps for Firestore
      if (activityData.scheduledAt) {
        dataToSave.scheduledAt = Timestamp.fromDate(
          activityData.scheduledAt instanceof Date
            ? activityData.scheduledAt
            : new Date(activityData.scheduledAt)
        );
      }
      if (activityData.dueDate) {
        dataToSave.dueDate = Timestamp.fromDate(
          activityData.dueDate instanceof Date
            ? activityData.dueDate
            : new Date(activityData.dueDate)
        );
      }
      if (activityData.completedAt) {
        dataToSave.completedAt = Timestamp.fromDate(
          activityData.completedAt instanceof Date
            ? activityData.completedAt
            : new Date(activityData.completedAt)
        );
      }

      const docRef = await addDoc(collection(db, 'activities'), dataToSave);
      return docRef.id;
    } catch (err) {
      console.error('Error creating activity:', err);
      throw err;
    }
  };

  const updateActivity = async (id: string, updates: Partial<Activity>): Promise<void> => {
    try {
      const docRef = doc(db, 'activities', id);

      // Fetch current doc for Google Calendar sync (no global activities cache)
      const existingSnap = await getDoc(docRef);
      const activity = existingSnap.exists()
        ? ({ id: existingSnap.id, ...existingSnap.data() } as Activity)
        : undefined;

      // Prepare update data with proper date conversions
      const updateData: any = {
        ...removeUndefined(updates),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      // Convert Date objects to Timestamps for Firestore
      if (updates.scheduledAt) {
        updateData.scheduledAt = Timestamp.fromDate(updates.scheduledAt instanceof Date ? updates.scheduledAt : new Date(updates.scheduledAt));
      }
      if (updates.dueDate) {
        updateData.dueDate = Timestamp.fromDate(updates.dueDate instanceof Date ? updates.dueDate : new Date(updates.dueDate));
      }
      if (updates.completedAt) {
        updateData.completedAt = Timestamp.fromDate(updates.completedAt instanceof Date ? updates.completedAt : new Date(updates.completedAt));
      }

      // Update Firestore
      await updateDoc(docRef, updateData);

      // Sync to Google Calendar if the activity is linked to a Google event
      if (activity?.googleEventId && activity?.googleCalendarId && activity?.createdBy) {
        try {
          const isConnected = await isGoogleCalendarConnected(activity.createdBy);
          if (isConnected) {
            // Merge updates with existing activity for Google sync
            const updatedActivity = { ...activity, ...updates };
            await updateGoogleEvent(
              activity.createdBy,
              activity.googleCalendarId,
              activity.googleEventId,
              updatedActivity as Activity
            );
          }
        } catch (googleErr) {
          console.error('Error syncing activity update to Google Calendar:', googleErr);
          // Don't throw - activity was updated in Firestore successfully
        }
      }
    } catch (err) {
      console.error('Error updating activity:', err);
      throw err;
    }
  };

  const deleteActivity = async (id: string): Promise<void> => {
    try {
      const docRef = doc(db, 'activities', id);
      const existingSnap = await getDoc(docRef);
      const activity = existingSnap.exists()
        ? ({ id: existingSnap.id, ...existingSnap.data() } as Activity)
        : undefined;

      // Delete from Firestore
      await deleteDoc(docRef);

      // Delete from Google Calendar if the activity is linked to a Google event
      if (activity?.googleEventId && activity?.googleCalendarId && activity?.createdBy) {
        try {
          const isConnected = await isGoogleCalendarConnected(activity.createdBy);
          if (isConnected) {
            await deleteGoogleEvent(
              activity.createdBy,
              activity.googleCalendarId,
              activity.googleEventId
            );
          }
        } catch (googleErr) {
          console.error('Error deleting activity from Google Calendar:', googleErr);
          // Don't throw - activity was deleted from Firestore successfully
        }
      }
    } catch (err) {
      console.error('Error deleting activity:', err);
      throw err;
    }
  };

  const completeActivity = async (id: string, updates?: { outcome?: string; description?: string }): Promise<void> => {
    try {
      await updateActivity(id, {
        status: 'completed',
        completedAt: new Date(),
        ...updates,
      });
    } catch (err) {
      console.error('Error completing activity:', err);
      throw err;
    }
  };

  // ==========================================
  // TIMBER PROJECT OPERATIONS
  // ==========================================

  const createTimberProject = async (projectData: Omit<TimberProject, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'timberProjects'), {
        ...projectData,
        startDate: Timestamp.fromDate(projectData.startDate),
        targetEndDate: Timestamp.fromDate(projectData.targetEndDate),
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        ...(projectData.contractedAt ? { contractedAt: Timestamp.fromDate(projectData.contractedAt) } : {}),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating timber project:', err);
      throw err;
    }
  };

  const updateTimberProject = async (id: string, updates: Partial<TimberProject>): Promise<void> => {
    try {
      const docRef = doc(db, 'timberProjects', id);
      let updateData: any = {
        ...removeUndefined(updates),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      // Convert dates to timestamps
      if (updates.startDate) updateData.startDate = Timestamp.fromDate(updates.startDate);
      if (updates.targetEndDate) updateData.targetEndDate = Timestamp.fromDate(updates.targetEndDate);
      if (updates.actualEndDate) updateData.actualEndDate = Timestamp.fromDate(updates.actualEndDate);
      if (updates.contractedAt) updateData.contractedAt = Timestamp.fromDate(updates.contractedAt);

      if (user?.id) {
        const { withCrmAuditUpdate } = await import('./crmAuditLog');
        updateData = await withCrmAuditUpdate(
          { uid: user.id, email: user.email, name: user.name },
          {
            collection: 'timberProjects',
            docId: id,
            action: 'update',
            entityType: 'project',
            entityId: id,
            changedFields: Object.keys(updates),
          },
          updateData,
        );
      }

      await updateDoc(docRef, updateData);
      if (updates.phase !== undefined) {
        const existing = timberProjects.find((p) => p.id === id);
        const dealId = existing?.dealId;
        if (dealId) {
          void persistProductionCommentAccessForDeal(dealId, {
            hubProjectId: id,
            hubPhase: updates.phase,
          });
        }
      }
    } catch (err) {
      console.error('Error updating timber project:', err);
      throw err;
    }
  };

  const deleteTimberProject = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'timberProjects', id));
    } catch (err) {
      console.error('Error deleting timber project:', err);
      throw err;
    }
  };

  // Create a preconstruction project from an active deal
  const createPreconProject = async (deal: EnhancedDeal, userId: string): Promise<string> => {
    try {
      const now = new Date();
      const targetEndDate = new Date(now);
      targetEndDate.setMonth(targetEndDate.getMonth() + 3); // 3 month default

      const projectData: any = {
        dealId: deal.id!,
        clientEmail: '',
        clientName: deal.contactName || '',
        name: deal.projectName || deal.title || 'Untitled Precon',
        description: deal.description || '',
        status: 'design' as const,
        frameType: 'custom' as const,
        phase: 'estimating' as const,
        startDate: now,
        targetEndDate,
        value: deal.value || 0,
        location: deal.location || '',
        projectType: deal.projectType || '',
        projectManager: userId,
      };

      if (deal.address) projectData.address = deal.address;
      if (deal.coordinates) projectData.coordinates = deal.coordinates;

      const projectId = await createTimberProject(projectData as Omit<TimberProject, 'id' | 'createdAt' | 'updatedAt'>);

      // Create default precon task groups (Estimating first)
      for (let i = 0; i < DEFAULT_PRECON_TASK_GROUPS.length; i++) {
        const group = DEFAULT_PRECON_TASK_GROUPS[i];
        await createTaskGroup({
          projectId,
          name: group.name,
          color: group.color,
          position: (i + 1) * 1.0,
        });
      }

      // Pull files from lead / client portal. Untagged lead docs and comment
      // attachments are skipped when sibling projects exist on this lead, but
      // lead documents explicitly tagged to this deal always sync.
      if (deal.id) {
        const dealIdsOnLead = new Set(
          enhancedDeals
            .filter(d => deal.leadId && d.leadId === deal.leadId)
            .map(d => d.id)
            .filter((id): id is string => !!id)
        );
        const hasOtherProjectsOnLead = timberProjects.some(
          p => p.dealId && p.dealId !== deal.id && dealIdsOnLead.has(p.dealId)
        );
        syncPortalFilesToProject(projectId, deal.id, deal.leadId, {
          skipLeadScopedFiles: hasOtherProjectsOnLead,
        }).catch(err => console.warn('Portal file sync (precon) failed:', err));
      }

      if (deal.leadId) {
        linkLeadCommsToProject(deal.leadId, projectId, deal.id).catch(err =>
          console.warn('Lead comms → precon project link failed (non-fatal):', err),
        );
      }

      return projectId;
    } catch (err) {
      console.error('Error creating precon project:', err);
      throw err;
    }
  };

  const convertDealToProject = async (deal: EnhancedDeal, userId: string, frameType: 'custom' | 'kit' = 'custom'): Promise<string> => {
    try {
      if (deal.outcome !== 'won') {
        throw new Error('Can only convert won deals to projects');
      }

      const now = new Date();
      const targetEndDate = new Date(now);
      targetEndDate.setMonth(targetEndDate.getMonth() + (frameType === 'kit' ? 3 : 6));

      const projectData: any = {
        dealId: deal.id!,
        clientEmail: '',
        clientName: deal.contactName || '',
        name: deal.title || 'Untitled Project',
        description: deal.description || '',
        status: 'planning',
        frameType,
        phase: 'contracted' as const,
        contractedAt: now,
        startDate: now,
        targetEndDate,
        value: deal.value || 0,
        location: deal.location || '',
        projectType: deal.projectType || '',
        projectManager: userId,
      };

      if (deal.address) projectData.address = deal.address;
      if (deal.coordinates) projectData.coordinates = deal.coordinates;

      const projectId = await createTimberProject(projectData as Omit<TimberProject, 'id' | 'createdAt' | 'updatedAt'>);

      // Create milestones
      const defaultMilestones = frameType === 'kit'
        ? [
          { title: 'Procurement Start', targetDate: new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000) },
          { title: 'Production Complete', targetDate: new Date(now.getTime() + 45 * 24 * 60 * 60 * 1000) },
          { title: 'Shipment Date', targetDate: new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000) },
          { title: 'Delivery Complete', targetDate: new Date(now.getTime() + 75 * 24 * 60 * 60 * 1000) },
        ]
        : [
          { title: 'Design Approval', targetDate: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000) },
          { title: 'Production Start', targetDate: new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000) },
          { title: 'Production Complete', targetDate: new Date(now.getTime() + 120 * 24 * 60 * 60 * 1000) },
          { title: 'Installation Complete', targetDate: new Date(now.getTime() + 150 * 24 * 60 * 60 * 1000) },
        ];

      for (const ms of defaultMilestones) {
        await createMilestone({ projectId, title: ms.title, targetDate: ms.targetDate, status: 'pending' });
      }

      // Create default task groups
      const groupDefs = frameType === 'kit'
        ? [
            { name: 'Procurement', color: '#4299E1' },
            { name: 'Production', color: '#ED8936' },
            { name: 'Shipment', color: '#9F7AEA' },
            { name: 'Delivery', color: '#48BB78' },
          ]
        : DEFAULT_CONTRACTED_TASK_GROUPS.map(({ name, color }) => ({ name, color }));

      for (let i = 0; i < groupDefs.length; i++) {
        await createTaskGroup({
          projectId,
          name: groupDefs[i].name,
          color: groupDefs[i].color,
          position: (i + 1) * 1.0,
        });
      }

      // === Merge preconstruction project if one exists for this deal ===
      const existingPrecon = timberProjects.find(
        p => p.dealId === deal.id && isEarlyFunnelProjectPhase(p.phase) && p.status !== 'completed'
      );
      if (existingPrecon && existingPrecon.id) {
        try {
          // Create a "Preconstruction" group in the new project
          const preconGroupId = await createTaskGroup({
            projectId,
            name: 'Preconstruction',
            color: '#6366F1',
            position: 0, // Before other groups
          });

          // Migrate tasks from precon project
          const preconTasks = projectTasks.filter(t => t.projectId === existingPrecon.id);
          for (const task of preconTasks) {
            await updateProjectTask(task.id!, { projectId, groupId: preconGroupId });
          }

          // Migrate time entries
          const preconTimeEntries = projectTimeEntries.filter(e => e.projectId === existingPrecon.id);
          for (const entry of preconTimeEntries) {
            const entryRef = doc(db, 'projectTimeEntries', entry.id!);
            await updateDoc(entryRef, { projectId });
          }

          // Migrate project files from precon hub
          await reassignProjectFilesBetweenProjects(existingPrecon.id, projectId);

          // Migrate task groups (other than the new one) — re-parent them
          const preconGroups = taskGroups.filter(g => g.projectId === existingPrecon.id);
          for (const group of preconGroups) {
            await deleteTaskGroup(group.id!);
          }

          // Mark precon project as completed
          await updateTimberProject(existingPrecon.id, { status: 'completed' });
        } catch (mergeErr) {
          console.error('Precon merge failed (non-fatal):', mergeErr);
        }
      }

      // Generate onboarding checklist from template
      try {
        const templateSnap = await getDoc(doc(db, 'settings', 'onboardingChecklist'));
        if (templateSnap.exists()) {
          const template = templateSnap.data();
          if (template.items?.length) {
            const existingOb = await getDoc(doc(db, 'projectOnboarding', projectId));
            if (!existingOb.exists()) {
              const userCache: Record<string, string> = {};
              for (const item of template.items) {
                if (item.defaultAssigneeId && !userCache[item.defaultAssigneeId]) {
                  try {
                    const uSnap = await getDoc(doc(db, 'users', item.defaultAssigneeId));
                    if (uSnap.exists()) {
                      const u = uSnap.data();
                      userCache[item.defaultAssigneeId] = u.displayName || u.email || '';
                    }
                  } catch { /* skip */ }
                }
              }
              let simulatedByItem: ReturnType<typeof parseSimulatedSchedules>[string] | undefined;
              try {
                const planningSnap = await getDoc(doc(db, STAFF_PLANNING_COLLECTION, STAFF_PLANNING_HUB_DOC_ID));
                simulatedByItem = parseSimulatedSchedules(planningSnap.data())[projectId];
              } catch {
                /* Simulated dates are optional; the checklist still generates. */
              }
              const obItems = template.items.map((t: any) => withSimulatedSchedule({
                id: t.id,
                title: t.title,
                description: t.description || '',
                category: t.category,
                department: normalizeOnboardingDepartment(t.department),
                assigneeId: t.defaultAssigneeId || null,
                assigneeName: t.defaultAssigneeId ? (userCache[t.defaultAssigneeId] || null) : null,
                required: t.required ?? true,
                order: t.order,
                completed: false,
                completedAt: null,
                completedBy: null,
                completedByName: null,
                scheduleNeedsPmReview: true,
              }, simulatedByItem));
              await setDoc(doc(db, 'projectOnboarding', projectId), {
                projectId,
                projectName: deal.title || 'Project',
                templateVersion: template.updatedAt || Timestamp.fromDate(new Date()),
                status: 'active',
                items: obItems,
                createdAt: Timestamp.fromDate(new Date()),
              });
            }
          }
        }
      } catch (obErr) {
        console.warn('Onboarding checklist generation failed (non-fatal):', obErr);
      }

      // Pull files from lead / client portal. Untagged lead docs and comment
      // attachments are skipped when sibling projects exist; deal-tagged docs
      // always sync (see precon path above).
      if (deal.id) {
        const dealIdsOnLead = new Set(
          enhancedDeals
            .filter(d => deal.leadId && d.leadId === deal.leadId)
            .map(d => d.id)
            .filter((id): id is string => !!id)
        );
        const hasOtherProjectsOnLead = timberProjects.some(
          p => p.dealId && p.dealId !== deal.id && dealIdsOnLead.has(p.dealId)
        );
        syncPortalFilesToProject(projectId, deal.id, deal.leadId, {
          skipLeadScopedFiles: hasOtherProjectsOnLead,
        }).catch(err => console.warn('Portal file sync (contracted) failed:', err));
      }

      // Stamp projectId on leadComms in place so Project Hub Meeting Notes can show
      // the same rows (no copy / no duplicates).
      if (deal.leadId) {
        linkLeadCommsToProject(deal.leadId, projectId, deal.id).catch(err =>
          console.warn('Lead comms → project link failed (non-fatal):', err),
        );
      }

      return projectId;
    } catch (err) {
      console.error('Error converting deal to project:', err);
      throw err;
    }
  };

  const syncSignedDealSideEffectsFn = async (
    dealId: string,
    userId: string,
    options?: { skipMarkWon?: boolean; skipSalesOrder?: boolean; proposalDocId?: string },
  ) => {
    await syncSignedDealSideEffects(dealId, userId, {
      ...options,
      deps: {
        convertDealToProject,
        markDealWon,
        updateTimberProject,
      },
    });
  };

  // ==========================================
  // PROJECT TIME ENTRY OPERATIONS
  // ==========================================

  const createProjectTimeEntry = async (entry: Omit<ProjectTimeEntry, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const payload = removeUndefined({
        ...entry,
        date: entry.date instanceof Date ? Timestamp.fromDate(entry.date) : entry.date,
        createdAt: Timestamp.fromDate(new Date()),
      });
      const docRef = await addDoc(collection(db, 'projectTimeEntries'), payload);
      return docRef.id;
    } catch (err) {
      console.error('Error creating project time entry:', err);
      throw err;
    }
  };

  const updateProjectTimeEntry = async (id: string, updates: Partial<ProjectTimeEntry>): Promise<void> => {
    try {
      const data: Record<string, any> = { ...updates };
      if (data.date instanceof Date) data.date = Timestamp.fromDate(data.date);
      const cleaned = removeUndefined(data);
      await updateDoc(doc(db, 'projectTimeEntries', id), cleaned);
    } catch (err) {
      console.error('Error updating project time entry:', err);
      throw err;
    }
  };

  const getTimeEntriesForProject = (projectId: string): ProjectTimeEntry[] => {
    return projectTimeEntries.filter(e => e.projectId === projectId);
  };

  const getOnboardingForProject = (projectId: string): ProjectOnboarding | undefined => {
    return projectOnboardings.find(ob => ob.projectId === projectId);
  };

  const onboardingFromDoc = (projectId: string, data: Record<string, any>): ProjectOnboarding => ({
    id: projectId,
    ...data,
    projectId: typeof data.projectId === 'string' ? data.projectId : projectId,
    projectName: typeof data.projectName === 'string' ? data.projectName : 'Project',
    templateVersion: convertTimestamp(data.templateVersion),
    createdAt: convertTimestamp(data.createdAt),
    completedAt: data.completedAt ? convertTimestamp(data.completedAt) : undefined,
    status: data.status === 'completed' ? 'completed' : 'active',
    items: (Array.isArray(data.items) ? data.items : []).map((it: any) => ({
      ...it,
      completedAt: it.completedAt ? convertTimestamp(it.completedAt) : undefined,
      startDate: it.startDate ? convertTimestamp(it.startDate) : undefined,
      endDate: it.endDate ? convertTimestamp(it.endDate) : undefined,
      actualStartDate: it.actualStartDate ? convertTimestamp(it.actualStartDate) : undefined,
      actualEndDate: it.actualEndDate ? convertTimestamp(it.actualEndDate) : undefined,
      lockedAt: it.lockedAt ? convertTimestamp(it.lockedAt) : undefined,
      scheduleReviewedByPmAt: it.scheduleReviewedByPmAt
        ? convertTimestamp(it.scheduleReviewedByPmAt)
        : undefined,
    })),
  });

  /**
   * Checklist the user is editing may live on the project-scoped listener while
   * the org-wide `projectOnboardings` cache is still empty. Read the doc first,
   * then fall back to that cache or a virtual simulate-UC seed.
   */
  const resolveOnboardingForWrite = async (
    projectId: string,
    seed?: ProjectOnboarding,
  ): Promise<{ ob: ProjectOnboarding; docExists: boolean }> => {
    const docRef = doc(db, 'projectOnboarding', projectId);
    const existing = await getDoc(docRef);
    if (existing.exists()) {
      return { ob: onboardingFromDoc(projectId, existing.data()), docExists: true };
    }
    const cached = projectOnboardings.find((o) => o.projectId === projectId);
    const ob = cached ?? (seed?.projectId === projectId ? seed : undefined);
    if (!ob) throw new Error('Onboarding not found');
    return { ob, docExists: false };
  };

  const updateOnboardingItem = async (
    projectId: string,
    itemId: string,
    updates: Partial<OnboardingItem>,
    userName: string,
    options?: { seedIfMissing?: ProjectOnboarding },
  ): Promise<void> => {
    try {
      const { ob, docExists } = await resolveOnboardingForWrite(projectId, options?.seedIfMissing);

      const updatedItems = ob.items.map(it => {
        if (it.id !== itemId) return it;
        const merged = { ...it, ...updates };
        if (updates.completed && !it.completed) {
          merged.completedAt = new Date();
          merged.completedBy = userName;
          merged.completedByName = userName;
        }
        if (updates.completed === false) {
          merged.completedAt = undefined;
          merged.completedBy = undefined;
          merged.completedByName = undefined;
        }
        return merged;
      });

      const allRequiredDone = updatedItems.filter(i => i.required).every(i => i.completed);
      const allDone = updatedItems.every(i => i.completed);
      const newStatus = (allRequiredDone && allDone) ? 'completed' as const : 'active' as const;

      const firestoreItems = serializeOnboardingItemsForFirestore(updatedItems);

      const docRef = doc(db, 'projectOnboarding', projectId);
      if (!docExists) {
        const templateVersion = ob.templateVersion instanceof Date
          ? Timestamp.fromDate(ob.templateVersion)
          : ob.templateVersion
            ? Timestamp.fromDate(new Date(ob.templateVersion as string | number | Date))
            : Timestamp.now();
        await setDoc(docRef, {
          projectId,
          projectName: ob.projectName || 'Project',
          templateVersion,
          status: newStatus,
          items: firestoreItems,
          createdAt: Timestamp.now(),
          ...(newStatus === 'completed' ? { completedAt: Timestamp.fromDate(new Date()) } : {}),
        });
        return;
      }
      await updateDoc(docRef, {
        items: firestoreItems,
        status: newStatus,
        ...(newStatus === 'completed'
          ? { completedAt: Timestamp.fromDate(new Date()) }
          : { completedAt: deleteField() }),
      });

      // Notifications are handled after calling this function
    } catch (err) {
      console.error('Error updating onboarding item:', err);
      throw err;
    }
  };

  const deleteOnboardingItem = async (projectId: string, itemId: string): Promise<void> => {
    try {
      const { ob } = await resolveOnboardingForWrite(projectId);

      const updatedItems = ob.items
        .filter(it => it.id !== itemId)
        .map(it => {
          const edges = it.dependencyEdges;
          if (!edges?.length) return it;
          const nextEdges = edges.filter(e => e.predecessorId !== itemId);
          if (nextEdges.length === edges.length) return it;
          return { ...it, dependencyEdges: nextEdges.length ? nextEdges : undefined };
        });
      if (updatedItems.length === ob.items.length) {
        console.warn('[deleteOnboardingItem] Item not found:', itemId);
        return;
      }

      let newStatus: 'active' | 'completed';
      if (updatedItems.length === 0) {
        newStatus = 'active';
      } else {
        const allRequiredDone = updatedItems.filter(i => i.required).every(i => i.completed);
        const allDone = updatedItems.every(i => i.completed);
        newStatus = (allRequiredDone && allDone) ? 'completed' : 'active';
      }

      const docRef = doc(db, 'projectOnboarding', projectId);
      await updateDoc(docRef, {
        items: serializeOnboardingItemsForFirestore(updatedItems),
        status: newStatus,
        ...(newStatus === 'completed'
          ? { completedAt: Timestamp.fromDate(new Date()) }
          : { completedAt: deleteField() }),
      });
    } catch (err) {
      console.error('Error deleting onboarding item:', err);
      throw err;
    }
  };

  // ==========================================
  // PROJECT TASK OPERATIONS
  // ==========================================

  const createProjectTask = async (taskData: Omit<ProjectTask, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const waitSegments = seedActionRequiredWaitSegmentsOnCreate({
        actionRequired: taskData.actionRequired,
        actionRequiredNote: taskData.actionRequiredNote,
        createdAt: now,
      });
      const docRef = await addDoc(collection(db, 'projectTasks'), removeUndefined({
        ...taskData,
        ...(waitSegments
          ? {
              actionRequiredWaitSegments: serializeActionRequiredWaitSegmentsForFirestore(waitSegments, Timestamp),
            }
          : {}),
        startDate: Timestamp.fromDate(taskData.startDate),
        endDate: Timestamp.fromDate(taskData.endDate),
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      }));
      return docRef.id;
    } catch (err) {
      console.error('Error creating project task:', err);
      throw err;
    }
  };

  const updateProjectTask = async (id: string, updates: Partial<ProjectTask>): Promise<void> => {
    // Checklist rows are mirrored as `onboarding-task:{projectId}:{itemId}` — they
    // are not documents in projectTasks. Callers must use updateOnboardingItem.
    if (isOnboardingBridgeTaskId(id)) {
      throw new Error(
        'Onboarding checklist items are not projectTasks documents. Update the checklist item instead.',
      );
    }
    // Supplier accounts cannot write projectTasks directly; route the
    // whitelisted fields (progress/status) through the secure callable.
    if (user?.role === 'architect') {
      throw new Error('Architects can comment on Design work but cannot edit tasks.');
    }
    if (user?.role === 'supplier') {
      const payload: { taskId: string; progress?: number; status?: string } = { taskId: id };
      if (updates.progress !== undefined) payload.progress = updates.progress;
      if (updates.status !== undefined) payload.status = updates.status;
      if (payload.progress === undefined && payload.status === undefined) {
        throw new Error('Suppliers can update task progress and status only.');
      }
      const fn = httpsCallable<typeof payload, { ok: boolean }>(
        getFunctions(undefined, 'us-central1'),
        'supplierUpdateProductionTask',
      );
      await fn(payload);
      // Optimistic local update; the refresh token re-syncs from the callable.
      setProjectTasks(prev =>
        prev.map(t => {
          if (t.id !== id) return t;
          const next: ProjectTask = {
            ...t,
            ...(payload.progress !== undefined ? { progress: payload.progress } : {}),
            ...(payload.status !== undefined
              ? { status: payload.status as ProjectTask['status'] }
              : {}),
            updatedAt: new Date(),
          };
          if (payload.status === 'completed') {
            delete next.actionRequired;
            delete next.actionRequiredNote;
          }
          return next;
        }),
      );
      setSupplierScheduleRefreshToken(v => v + 1);
      return;
    }
    try {
      const docRef = doc(db, 'projectTasks', id);
      // Safety net for callers that skip Project Hub's side-effect helper
      // (e.g. completion date prompt): completing a task clears action required.
      const existingTask = projectTasks.find((t) => t.id === id);
      const effectiveUpdates = existingTask
        ? applyActionRequiredSideEffects(existingTask, updates)
        : updates;
      const { actionRequiredWaitSegments, ...restUpdates } = effectiveUpdates;
      const updateData: any = {
        ...removeUndefined(restUpdates),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      // Firestore ignores omitted keys — `removeUndefined` strips `undefined`, so "clear" updates
      // would not remove existing `actionRequired` / `actionRequiredNote` (e.g. external sentinel).
      const clearToDeleteField = (v: unknown) => v === undefined || v === null || v === '';
      if (
        Object.prototype.hasOwnProperty.call(effectiveUpdates, 'actionRequired') &&
        clearToDeleteField(effectiveUpdates.actionRequired)
      ) {
        updateData.actionRequired = deleteField();
        const noteInPayload = Object.prototype.hasOwnProperty.call(
          effectiveUpdates,
          'actionRequiredNote',
        );
        if (!noteInPayload || clearToDeleteField(effectiveUpdates.actionRequiredNote)) {
          updateData.actionRequiredNote = deleteField();
        }
      } else if (
        Object.prototype.hasOwnProperty.call(effectiveUpdates, 'actionRequiredNote') &&
        clearToDeleteField(effectiveUpdates.actionRequiredNote)
      ) {
        updateData.actionRequiredNote = deleteField();
      }

      if (effectiveUpdates.startDate) {
        updateData.startDate = Timestamp.fromDate(effectiveUpdates.startDate);
      }
      if (effectiveUpdates.endDate) {
        updateData.endDate = Timestamp.fromDate(effectiveUpdates.endDate);
      }
      if (effectiveUpdates.actualStartDate) {
        updateData.actualStartDate = Timestamp.fromDate(effectiveUpdates.actualStartDate);
      }
      if (effectiveUpdates.actualEndDate) {
        updateData.actualEndDate = Timestamp.fromDate(effectiveUpdates.actualEndDate);
      }
      if (effectiveUpdates.completedAt) {
        updateData.completedAt = Timestamp.fromDate(effectiveUpdates.completedAt);
      }
      if (effectiveUpdates.lockedAt) {
        updateData.lockedAt = Timestamp.fromDate(effectiveUpdates.lockedAt);
      }
      if (effectiveUpdates.scheduleReviewedByPmAt) {
        updateData.scheduleReviewedByPmAt = Timestamp.fromDate(
          effectiveUpdates.scheduleReviewedByPmAt,
        );
      }

      if (actionRequiredWaitSegments) {
        updateData.actionRequiredWaitSegments = serializeActionRequiredWaitSegmentsForFirestore(
          actionRequiredWaitSegments,
          Timestamp,
        );
      }

      // Lock-clear semantics: when scheduleLocked is explicitly set to false, also clear lockedAt/lockedBy.
      if (
        Object.prototype.hasOwnProperty.call(effectiveUpdates, 'scheduleLocked') &&
        effectiveUpdates.scheduleLocked === false
      ) {
        updateData.lockedAt = deleteField();
        updateData.lockedBy = deleteField();
      }

      setProjectTasks((prev) =>
        prev.map((t) => {
          if (t.id !== id) return t;
          const merged: ProjectTask = { ...t, ...restUpdates, updatedAt: new Date() };
          if (
            Object.prototype.hasOwnProperty.call(effectiveUpdates, 'actionRequired') &&
            clearToDeleteField(effectiveUpdates.actionRequired)
          ) {
            delete merged.actionRequired;
            delete merged.actionRequiredNote;
          }
          if (actionRequiredWaitSegments) {
            merged.actionRequiredWaitSegments = actionRequiredWaitSegments;
          }
          return merged;
        }),
      );
      await updateDoc(docRef, updateData);
    } catch (err) {
      console.error('Error updating project task:', err);
      throw err;
    }
  };

  const deleteProjectTask = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'projectTasks', id));
    } catch (err) {
      console.error('Error deleting project task:', err);
      throw err;
    }
  };

  const getTasksForProject = (projectId: string): ProjectTask[] => {
    return projectTasks.filter(t => t.projectId === projectId);
  };

  // ==========================================
  // TASK GROUP OPERATIONS
  // ==========================================

  const createTaskGroup = async (groupData: Omit<TaskGroup, 'id'>): Promise<string> => {
    try {
      const docRef = await addDoc(collection(db, 'taskGroups'), groupData);
      return docRef.id;
    } catch (err) {
      console.error('Error creating task group:', err);
      throw err;
    }
  };

  const updateTaskGroup = async (id: string, updates: Partial<TaskGroup>): Promise<void> => {
    try {
      await updateDoc(doc(db, 'taskGroups', id), removeUndefined(updates));
    } catch (err) {
      console.error('Error updating task group:', err);
      throw err;
    }
  };

  const deleteTaskGroup = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'taskGroups', id));
    } catch (err) {
      console.error('Error deleting task group:', err);
      throw err;
    }
  };

  /**
   * Precon-originated jobs keep Estimating / Design / Client Communication.
   * On contract they also need Production and Installation (and Procurement)
   * so checklist items in those departments are not dumped into Ungrouped.
   *
   * Must read Firestore (not CRMContext.taskGroups). Org-wide taskGroups no
   * longer stream at boot, so the in-memory list is often empty on deal /
   * change-order pages and used to create duplicate empty groups.
   */
  const procurementEnsureInFlight = useRef(new Set<string>());
  const ensureProcurementTaskGroupForProject = useCallback(async (projectId: string): Promise<void> => {
    if (user?.role === 'supplier' || user?.role === 'architect' || user?.role === 'subcontractor') return;
    const proj = timberProjects.find((p) => p.id === projectId);
    if (!proj) return;
    if (isEarlyFunnelProjectPhase(proj.phase)) return;
    if (proj.frameType === 'kit') return;
    if (procurementEnsureInFlight.current.has(projectId)) return;
    procurementEnsureInFlight.current.add(projectId);

    try {
      const groupsSnap = await getDocs(query(collection(db, 'taskGroups'), where('projectId', '==', projectId)));
      const groups = groupsSnap.docs.map((d) => {
        const data = d.data() as { name?: string; position?: number };
        return {
          id: d.id,
          projectId,
          name: String(data.name || ''),
          position: data.position || 0,
        };
      });
      const missing = missingContractedTaskGroups(projectId, groups);
      for (const g of missing) {
        await setDoc(
          doc(db, 'taskGroups', reservedContractedTaskGroupId(projectId, g.department)),
          {
            projectId,
            name: g.name,
            color: g.color,
            position: g.position,
          },
          { merge: true },
        );
      }
    } catch (err) {
      console.warn('ensureProcurementTaskGroupForProject:', err);
    } finally {
      procurementEnsureInFlight.current.delete(projectId);
    }
  }, [user?.role, timberProjects, createTaskGroup]);

  const procurementGroupBackfillDone = useRef(false);
  useEffect(() => {
    // Suppliers cannot write taskGroups; hub rows come from the schedule callable.
    if (user?.role === 'supplier' || user?.role === 'architect' || user?.role === 'subcontractor') return;
    if (procurementGroupBackfillDone.current) return;
    if (!timberProjects.length || !taskGroups.length) return;
    procurementGroupBackfillDone.current = true;
    void (async () => {
      for (const p of timberProjects) {
        if (!p.id || isEarlyFunnelProjectPhase(p.phase) || p.frameType !== 'custom') continue;
        await ensureProcurementTaskGroupForProject(p.id);
      }
    })();
  }, [user?.role, timberProjects, taskGroups, ensureProcurementTaskGroupForProject]);

  // ==========================================
  // TASK COMMENT & ACTIVITY OPERATIONS
  // ==========================================

  const createTaskComment = async (commentData: Omit<TaskComment, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const docRef = await addDoc(collection(db, 'taskComments'), {
        ...commentData,
        audience: commentData.audience === 'architect' ? 'architect' : 'internal',
        createdAt: Timestamp.fromDate(new Date()),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating task comment:', err);
      throw err;
    }
  };

  const createTaskActivity = async (logData: Omit<TaskActivityLog, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const docRef = await addDoc(collection(db, 'taskActivity'), {
        ...logData,
        createdAt: Timestamp.fromDate(new Date()),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating task activity:', err);
      throw err;
    }
  };

  // ==========================================
  // MILESTONE OPERATIONS
  // ==========================================

  const createMilestone = async (milestoneData: Omit<Milestone, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'milestones'), {
        ...milestoneData,
        targetDate: Timestamp.fromDate(milestoneData.targetDate),
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating milestone:', err);
      throw err;
    }
  };

  const updateMilestone = async (id: string, updates: Partial<Milestone>): Promise<void> => {
    try {
      const docRef = doc(db, 'milestones', id);
      const updateData: any = {
        ...removeUndefined(updates),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      if (updates.targetDate) updateData.targetDate = Timestamp.fromDate(updates.targetDate);
      if (updates.completedDate) updateData.completedDate = Timestamp.fromDate(updates.completedDate);
      if (updates.lockedAt) updateData.lockedAt = Timestamp.fromDate(updates.lockedAt);

      if (Object.prototype.hasOwnProperty.call(updates, 'scheduleLocked') && updates.scheduleLocked === false) {
        updateData.lockedAt = deleteField();
        updateData.lockedBy = deleteField();
      }

      await updateDoc(docRef, updateData);
    } catch (err) {
      console.error('Error updating milestone:', err);
      throw err;
    }
  };

  const deleteMilestone = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'milestones', id));
    } catch (err) {
      console.error('Error deleting milestone:', err);
      throw err;
    }
  };

  const getMilestonesForProject = (projectId: string): Milestone[] => {
    return milestones.filter(m => m.projectId === projectId);
  };

  // ==========================================
  // STRATEGIC MARKER OPERATIONS
  // ==========================================

  const createStrategicLayer = async (layerData: Omit<StrategicMarkerLayer, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'strategicLayers'), {
        ...layerData,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating strategic layer:', err);
      throw err;
    }
  };

  const updateStrategicLayer = async (id: string, updates: Partial<StrategicMarkerLayer>): Promise<void> => {
    try {
      const docRef = doc(db, 'strategicLayers', id);
      await updateDoc(docRef, {
        ...removeUndefined(updates),
        updatedAt: Timestamp.fromDate(new Date()),
      });
    } catch (err) {
      console.error('Error updating strategic layer:', err);
      throw err;
    }
  };

  const deleteStrategicLayer = async (id: string): Promise<void> => {
    try {
      // First delete all markers in this layer
      await deleteAllMarkersInLayer(id);
      // Then delete the layer
      await deleteDoc(doc(db, 'strategicLayers', id));
    } catch (err) {
      console.error('Error deleting strategic layer:', err);
      throw err;
    }
  };

  const createStrategicMarker = async (markerData: Omit<StrategicMarker, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'strategicMarkers'), {
        ...markerData,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating strategic marker:', err);
      throw err;
    }
  };

  const bulkCreateStrategicMarkers = async (markers: Omit<StrategicMarker, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<number> => {
    try {
      const now = new Date();
      let successCount = 0;

      // Process in batches of 500 (Firestore limit)
      const batchSize = 500;
      for (let i = 0; i < markers.length; i += batchSize) {
        const batch = markers.slice(i, i + batchSize);
        const promises = batch.map(marker =>
          addDoc(collection(db, 'strategicMarkers'), {
            ...marker,
            createdAt: Timestamp.fromDate(now),
            updatedAt: Timestamp.fromDate(now),
          })
        );
        await Promise.all(promises);
        successCount += batch.length;
      }

      return successCount;
    } catch (err) {
      console.error('Error bulk creating strategic markers:', err);
      throw err;
    }
  };

  const updateStrategicMarker = async (id: string, updates: Partial<StrategicMarker>): Promise<void> => {
    try {
      const docRef = doc(db, 'strategicMarkers', id);
      await updateDoc(docRef, {
        ...removeUndefined(updates),
        updatedAt: Timestamp.fromDate(new Date()),
      });
    } catch (err) {
      console.error('Error updating strategic marker:', err);
      throw err;
    }
  };

  const deleteStrategicMarker = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'strategicMarkers', id));
    } catch (err) {
      console.error('Error deleting strategic marker:', err);
      throw err;
    }
  };

  const deleteAllMarkersInLayer = async (layerId: string): Promise<number> => {
    try {
      const markersToDelete = strategicMarkers.filter(m => m.layerId === layerId);
      const promises = markersToDelete.map(m => m.id && deleteDoc(doc(db, 'strategicMarkers', m.id)));
      await Promise.all(promises);
      return markersToDelete.length;
    } catch (err) {
      console.error('Error deleting markers in layer:', err);
      throw err;
    }
  };

  const getMarkersForLayer = (layerId: string): StrategicMarker[] => {
    return strategicMarkers.filter(m => m.layerId === layerId);
  };

  const toggleLayerVisibility = async (layerId: string, enabled: boolean): Promise<void> => {
    await updateStrategicLayer(layerId, { enabled });
  };

  // ==========================================
  // USER MANAGEMENT OPERATIONS
  // ==========================================

  const updateCRMUser = async (uid: string, updates: Partial<CRMUser>): Promise<void> => {
    try {
      const { designerHourlyRate: _omitRate, ...safeUpdates } = updates;
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        ...removeUndefined(safeUpdates),
      });
    } catch (err) {
      console.error('Error updating user:', err);
      throw err;
    }
  };

  const adminSetUserProfilePhoto = async (targetUid: string, file: File): Promise<void> => {
    if (user?.role !== 'admin') {
      throw new Error('Only admins can set another user\'s profile photo');
    }
    if (!targetUid?.trim()) throw new Error('Invalid user');
    const max = 8 * 1024 * 1024;
    if (file.size > max) throw new Error('Image must be 8 MB or smaller');
    if (!file.type.startsWith('image/')) throw new Error('Please choose an image file');

    const imageBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Could not read image'));
          return;
        }
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(new Error('Could not read image'));
      reader.readAsDataURL(file);
    });

    const functions = getFunctions(undefined, 'us-central1');
    const uploadFn = httpsCallable<
      { targetUid: string; imageBase64: string; contentType: string },
      { photoURL: string }
    >(functions, 'adminUploadUserProfilePhoto');

    await uploadFn({
      targetUid: targetUid.trim(),
      imageBase64,
      contentType: file.type || 'image/jpeg',
    });
  };

  const adminClearUserProfilePhoto = async (targetUid: string): Promise<void> => {
    if (user?.role !== 'admin') {
      throw new Error('Only admins can clear another user\'s profile photo');
    }
    if (!targetUid?.trim()) throw new Error('Invalid user');
    const userRef = doc(db, 'users', targetUid);
    await updateDoc(userRef, { photoURL: null });
  };

  const deactivateUser = async (uid: string): Promise<void> => {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        isActive: false,
        deactivatedAt: new Date(),
      });
    } catch (err) {
      console.error('Error deactivating user:', err);
      throw err;
    }
  };

  const reactivateUser = async (uid: string): Promise<void> => {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        isActive: true,
        deactivatedAt: null,
      });
    } catch (err) {
      console.error('Error reactivating user:', err);
      throw err;
    }
  };

  const deleteCRMUser = async (uid: string): Promise<void> => {
    try {
      const userRef = doc(db, 'users', uid);
      await deleteDoc(userRef);
    } catch (err) {
      console.error('Error deleting user:', err);
      throw err;
    }
  };

  // ==========================================
  // ARCHITECT INVITE OPERATIONS
  // ==========================================

  const createArchitectInvite = async (inviteData: Omit<ArchitectInvite, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const docRef = await addDoc(collection(db, 'architectInvites'), {
        ...inviteData,
        email: inviteData.email.trim().toLowerCase(),
        createdAt: Timestamp.fromDate(new Date()),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating architect invite:', err);
      throw err;
    }
  };

  const updateArchitectInvite = async (id: string, updates: Partial<ArchitectInvite>): Promise<void> => {
    try {
      const updateData: any = { ...removeUndefined(updates) };
      if (updates.acceptedAt) updateData.acceptedAt = Timestamp.fromDate(updates.acceptedAt);
      await updateDoc(doc(db, 'architectInvites', id), updateData);
    } catch (err) {
      console.error('Error updating architect invite:', err);
      throw err;
    }
  };

  const revokeArchitectInvite = async (id: string): Promise<void> => {
    try {
      await updateDoc(doc(db, 'architectInvites', id), { status: 'revoked' });
    } catch (err) {
      console.error('Error revoking architect invite:', err);
      throw err;
    }
  };

  // ==========================================
  // ENGINEER INVITE OPERATIONS
  // ==========================================

  const createEngineerInvite = async (inviteData: Omit<EngineerInvite, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const docRef = await addDoc(collection(db, 'engineerInvites'), {
        ...inviteData,
        createdAt: Timestamp.fromDate(new Date()),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating engineer invite:', err);
      throw err;
    }
  };

  const updateEngineerInvite = async (id: string, updates: Partial<EngineerInvite>): Promise<void> => {
    try {
      const updateData: any = { ...removeUndefined(updates) };
      if (updates.acceptedAt) updateData.acceptedAt = Timestamp.fromDate(updates.acceptedAt);
      await updateDoc(doc(db, 'engineerInvites', id), updateData);
    } catch (err) {
      console.error('Error updating engineer invite:', err);
      throw err;
    }
  };

  const revokeEngineerInvite = async (id: string): Promise<void> => {
    try {
      await updateDoc(doc(db, 'engineerInvites', id), { status: 'revoked' });
    } catch (err) {
      console.error('Error revoking engineer invite:', err);
      throw err;
    }
  };

  // ==========================================
  // CPA INVITE OPERATIONS
  // ==========================================

  const createCpaInvite = async (inviteData: Omit<CpaInvite, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const docRef = await addDoc(collection(db, 'cpaInvites'), {
        ...inviteData,
        createdAt: Timestamp.fromDate(new Date()),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating CPA invite:', err);
      throw err;
    }
  };

  const updateCpaInvite = async (id: string, updates: Partial<CpaInvite>): Promise<void> => {
    try {
      const updateData: any = { ...removeUndefined(updates) };
      if (updates.acceptedAt) updateData.acceptedAt = Timestamp.fromDate(updates.acceptedAt);
      await updateDoc(doc(db, 'cpaInvites', id), updateData);
    } catch (err) {
      console.error('Error updating CPA invite:', err);
      throw err;
    }
  };

  const revokeCpaInvite = async (id: string): Promise<void> => {
    try {
      await updateDoc(doc(db, 'cpaInvites', id), { status: 'revoked' });
    } catch (err) {
      console.error('Error revoking CPA invite:', err);
      throw err;
    }
  };

  const createEmployeeInvite = async (
    inviteData: Omit<EmployeeInvite, 'id' | 'createdAt'>,
  ): Promise<string> => {
    const payload: Record<string, unknown> = {
      ...inviteData,
      createdAt: Timestamp.fromDate(new Date()),
    };
    for (const key of Object.keys(payload)) {
      if (payload[key] === undefined) delete payload[key];
    }
    const docRef = await addDoc(collection(db, 'employeeInvites'), payload);
    return docRef.id;
  };

  const revokeEmployeeInvite = async (id: string): Promise<void> => {
    await updateDoc(doc(db, 'employeeInvites', id), { status: 'revoked' });
  };

  const persistProductionCommentAccessForDeal = async (
    dealId: string,
    overlay?: {
      rfqProjectId?: string;
      rfqUpdates?: Partial<RfqProject>;
      hubProjectId?: string;
      hubPhase?: TimberProject['phase'];
      accessProjectTasks?: ProjectTask[];
      accessProjectOnboardings?: Array<{ projectId?: string; items?: OnboardingItem[] }>;
    },
  ): Promise<void> => {
    const trimmed = dealId.trim();
    if (!trimmed) return;
    const rfqOverlay = overlay;
    const projects = rfqProjects
      .filter((p) => p.dealId === trimmed || p.id === rfqOverlay?.rfqProjectId)
      .map((p) => {
        if (!rfqOverlay || p.id !== rfqOverlay.rfqProjectId) return p;
        return {
          ...p,
          ...rfqOverlay.rfqUpdates,
          dealId: rfqOverlay.rfqUpdates?.dealId || p.dealId || trimmed,
        };
      });
    const hubProjects = timberProjects.map((p) => {
      if (!rfqOverlay?.hubPhase || p.id !== rfqOverlay.hubProjectId) return p;
      return { ...p, phase: rfqOverlay.hubPhase };
    });
    const hubTaskSupplierOrgIds = collectHubTaskSupplierOrgIdsForDeal({
      dealId: trimmed,
      hubProjects: timberProjects,
      projectTasks: overlay?.accessProjectTasks ?? projectTasks,
      projectOnboardings: overlay?.accessProjectOnboardings ?? projectOnboardings,
    });
    const access = buildProductionCommentAccessDoc({
      dealId: trimmed,
      projects,
      submissions: rfqSubmissions,
      hubProjects,
      hubTaskSupplierOrgIds,
    });
    const accessId = productionCommentAccessId(trimmed);
    if (!productionCommentAccessHasAwards(access)) {
      try {
        await deleteDoc(doc(db, 'productionCommentAccess', accessId));
      } catch {
        /* missing doc is fine */
      }
      return;
    }
    await setDoc(
      doc(db, 'productionCommentAccess', accessId),
      {
        dealId: access.dealId,
        timberOrgIds: access.timberOrgIds,
        cncOrgIds: access.cncOrgIds,
        underContract: access.underContract === true,
        ...(access.hubProjectId ? { hubProjectId: access.hubProjectId } : {}),
        updatedAt: Timestamp.fromDate(new Date()),
      },
      { merge: true },
    );
  };

  // ==========================================
  // SUPPLIER RFQ
  // ==========================================

  const createSupplierOrg = async (org: Omit<SupplierOrg, 'id' | 'createdAt'>): Promise<string> => {
    const now = new Date();
    const docRef = await addDoc(collection(db, 'supplierOrgs'), {
      name: org.name,
      defaultContactEmail: org.defaultContactEmail ?? null,
      defaultContactFirstName: org.defaultContactFirstName ?? null,
      notes: org.notes ?? null,
      capabilities: org.capabilities?.length ? org.capabilities : ['timber'],
      cncDefaultFlatFee: org.cncDefaultFlatFee ?? null,
      cncDefaultRatePerBdft: org.cncDefaultRatePerBdft ?? null,
      leadTimeWeeks: org.leadTimeWeeks ?? null,
      serviceRegion: org.serviceRegion ?? null,
      createdByUid: org.createdByUid ?? null,
      createdByName: org.createdByName ?? null,
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
    });
    return docRef.id;
  };

  const updateSupplierOrg = async (id: string, updates: Partial<SupplierOrg>): Promise<void> => {
    const payload: Record<string, unknown> = {
      updatedAt: Timestamp.fromDate(new Date()),
    };
    if (updates.name !== undefined) payload.name = updates.name;
    if (updates.notes !== undefined) payload.notes = updates.notes ?? null;
    if (updates.capabilities !== undefined) payload.capabilities = updates.capabilities;
    if (updates.cncDefaultFlatFee !== undefined) payload.cncDefaultFlatFee = updates.cncDefaultFlatFee ?? null;
    if (updates.cncDefaultRatePerBdft !== undefined) {
      payload.cncDefaultRatePerBdft = updates.cncDefaultRatePerBdft ?? null;
    }
    if (updates.leadTimeWeeks !== undefined) payload.leadTimeWeeks = updates.leadTimeWeeks ?? null;
    if (updates.serviceRegion !== undefined) payload.serviceRegion = updates.serviceRegion ?? null;
    if (updates.defaultContactEmail !== undefined) {
      const v = updates.defaultContactEmail;
      if (v == null || String(v).trim() === '') {
        payload.defaultContactEmail = deleteField();
      } else {
        payload.defaultContactEmail = String(v).trim();
      }
    }
    if (updates.defaultContactFirstName !== undefined) {
      const v = updates.defaultContactFirstName;
      if (v == null || String(v).trim() === '') {
        payload.defaultContactFirstName = deleteField();
      } else {
        payload.defaultContactFirstName = String(v).trim();
      }
    }
    await updateDoc(doc(db, 'supplierOrgs', id), payload as Record<string, any>);
  };

  const deleteSupplierOrg = async (id: string): Promise<void> => {
    const linkedProjectCount = rfqProjects.filter((p) => rfqProjectReferencesSupplierOrg(p, id)).length;
    if (linkedProjectCount > 0) {
      throw new Error(
        `Cannot delete this vendor — ${linkedProjectCount} project assignment${linkedProjectCount === 1 ? '' : 's'} still reference it. Remove the vendor from those work orders first.`,
      );
    }

    const linkedPortalUsers = crmUsers.filter((u) => u.supplierOrgId === id);
    if (linkedPortalUsers.length > 0) {
      throw new Error(
        `Cannot delete this vendor — ${linkedPortalUsers.length} portal account${linkedPortalUsers.length === 1 ? '' : 's'} still linked. Remove or reassign those users first.`,
      );
    }

    const invites = supplierInvites.filter((inv) => inv.supplierOrgId === id && inv.id);
    await Promise.all(invites.map((inv) => deleteDoc(doc(db, 'supplierInvites', inv.id!))));
    await deleteDoc(doc(db, 'supplierOrgs', id));
  };

  const createSupplierInvite = async (
    inviteData: Omit<SupplierInvite, 'id' | 'createdAt'>,
  ): Promise<string> => {
    const docRef = await addDoc(collection(db, 'supplierInvites'), {
      ...inviteData,
      createdAt: Timestamp.fromDate(new Date()),
    });
    return docRef.id;
  };

  const updateSupplierInvite = async (id: string, updates: Partial<SupplierInvite>): Promise<void> => {
    const updateData: Record<string, unknown> = { ...removeUndefined(updates as Record<string, unknown>) };
    if (updates.acceptedAt) updateData.acceptedAt = Timestamp.fromDate(updates.acceptedAt);
    await updateDoc(doc(db, 'supplierInvites', id), updateData as Record<string, any>);
  };

  const revokeSupplierInvite = async (id: string): Promise<void> => {
    await updateDoc(doc(db, 'supplierInvites', id), { status: 'revoked' });
  };

  const deleteSupplierInvite = async (id: string): Promise<void> => {
    await deleteDoc(doc(db, 'supplierInvites', id));
  };

  const createRfqProject = async (row: Omit<RfqProject, 'id' | 'createdAt'>): Promise<string> => {
    const now = new Date();
    const supplierOrgIds = rfqProjectSupplierOrgIds(row);
    const { supplierOrgIds: _omitIds, ...rowRest } = row as RfqProject & { supplierOrgIds?: string[] };
    const docRef = await addDoc(collection(db, 'rfqProjects'), {
      ...removeUndefined(rowRest as Record<string, unknown>),
      supplierOrgIds: supplierOrgIds.length ? supplierOrgIds : undefined,
      status: row.status || 'active',
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
    });
    return docRef.id;
  };

  const updateRfqProject = async (id: string, updates: Partial<RfqProject>): Promise<void> => {
    const safeUpdates = sanitizeRfqProjectFirestoreUpdates(updates);
    await updateDoc(doc(db, 'rfqProjects', id), {
      ...removeUndefined(safeUpdates as Record<string, unknown>),
      updatedAt: Timestamp.fromDate(new Date()),
    });
    const existing = rfqProjects.find((p) => p.id === id);
    const dealId = (updates.dealId || existing?.dealId || '').trim();
    if (dealId) {
      void persistProductionCommentAccessForDeal(dealId, {
        rfqProjectId: id,
        rfqUpdates: safeUpdates,
      });
    }
  };

  const publishRfqPackage = async (input: {
    rfqProjectId: string;
    supplierOrgId: string;
    targetSupplierOrgIds?: string[];
    dealId: string;
    dealTitle?: string;
    workType?: import('../types').VendorWorkType;
    proposalVersionId?: string | null;
    proposalSnapshot?: { nodeSets: ProposalNodeSet[] };
    label?: string;
    releaseDisplayLabel?: string;
    timberListSource?: 'proposal_live' | 'proposal_saved_version' | 'hub_csv' | 'hub_staged_release';
    hubFileReleaseId?: string;
    /** Every staged release the package carries (request rounds publish one package per supplier). */
    hubFileReleaseIds?: string[];
    releaseVersionIndex?: number;
    linkedProposalVersionId?: string | null;
    linkedProposalVersionLabel?: string;
    linkedProposalVersionSource?: import('../types').ProposalVersionSource;
    linkedProposalVersionSavedAt?: Date;
    linkedFrom?: 'proposal_push' | 'hub_file_stage';
    cncPackagePayload?: import('../types').CncPackagePayload;
    customEmailMessage?: string;
    legacyImport?: boolean;
    quotePlaceholder?: boolean;
    shipToByOrgId?: Record<string, import('../types').SupplierShipToAddress>;
    shipToPresetByOrgId?: Record<string, import('../types').SupplierShipToPreset>;
    /** Per-supplier structure scope chosen at publish review (overrides invite snapshot). */
    scopeStructuresByOrgId?: Record<string, import('../types').TimberSupplierStructureRef[]>;
    /** Request-round envelope (Files → RFQs). Denormalized onto every package so suppliers see it. */
    request?: RfqPackageRequestEnvelope;
    /** Per-supplier: the earlier package for the same structure lane this one replaces. */
    supersedesPackageIdByOrgId?: Record<string, string>;
    /** Overrides the staged release's purpose (defaults to the hub release doc's value). */
    releasePurpose?: import('../types').RfqReleasePurpose;
    createdByUid: string;
    createdByName: string;
  }): Promise<string[]> => {
    const now = new Date();
    const workType = input.workType === 'cnc_quote'
      ? 'cnc_quote'
      : input.workType === 'other_quote'
        ? 'other_quote'
        : 'timber_rfq';
    const targetsRaw =
      input.targetSupplierOrgIds !== undefined && input.targetSupplierOrgIds.length > 0
        ? input.targetSupplierOrgIds
        : [input.supplierOrgId];
    const targets = Array.from(new Set(targetsRaw.filter(Boolean)));
    if (!targets.length) {
      throw new Error('No supplier organizations selected to publish to.');
    }

    const projRef = doc(db, 'rfqProjects', input.rfqProjectId);
    const projSnapPre = await getDoc(projRef);
    const preData: Partial<Pick<RfqProject, 'supplierOrgId' | 'supplierOrgIds' | 'timberSupplierInvites'>> =
      projSnapPre.exists()
        ? (projSnapPre.data() as Pick<RfqProject, 'supplierOrgId' | 'supplierOrgIds' | 'timberSupplierInvites'>)
        : {};
    const allowedOnProject = rfqProjectSupplierOrgIds(preData);
    const safeTargets = targets.filter((id) => allowedOnProject.includes(id));
    if (!safeTargets.length) {
      throw new Error('None of the selected suppliers are assigned to this RFQ project.');
    }

    const basePayload: Record<string, unknown> = {
      rfqProjectId: input.rfqProjectId,
      dealId: input.dealId,
      dealTitle: input.dealTitle ?? null,
      workType,
      proposalVersionId: input.proposalVersionId ?? null,
      createdByUid: input.createdByUid,
      createdByName: input.createdByName,
      createdAt: Timestamp.fromDate(now),
    };
    if (input.label) basePayload.label = input.label;
    if (input.releaseDisplayLabel) basePayload.releaseDisplayLabel = input.releaseDisplayLabel;
    if (input.timberListSource) basePayload.timberListSource = input.timberListSource;
    if (input.hubFileReleaseId) basePayload.hubFileReleaseId = input.hubFileReleaseId;
    const releaseIds = Array.from(new Set((input.hubFileReleaseIds ?? []).filter(Boolean)));
    if (releaseIds.length > 1) basePayload.hubFileReleaseIds = releaseIds;
    if (typeof input.releaseVersionIndex === 'number') {
      basePayload.releaseVersionIndex = input.releaseVersionIndex;
    }
    if (input.linkedProposalVersionId !== undefined) {
      basePayload.linkedProposalVersionId = input.linkedProposalVersionId;
    }
    if (input.linkedProposalVersionLabel) {
      basePayload.linkedProposalVersionLabel = input.linkedProposalVersionLabel;
    }
    if (input.linkedProposalVersionSource) {
      basePayload.linkedProposalVersionSource = input.linkedProposalVersionSource;
    }
    if (input.linkedProposalVersionSavedAt instanceof Date) {
      basePayload.linkedProposalVersionSavedAt = Timestamp.fromDate(input.linkedProposalVersionSavedAt);
    }
    if (input.linkedFrom) basePayload.linkedFrom = input.linkedFrom;
    if (input.proposalSnapshot) basePayload.proposalSnapshot = removeUndefinedDeep(input.proposalSnapshot);
    if (input.cncPackagePayload) basePayload.cncPackagePayload = removeUndefinedDeep(input.cncPackagePayload);
    const customMsg = typeof input.customEmailMessage === 'string' ? input.customEmailMessage.trim() : '';
    if (customMsg) basePayload.customEmailMessage = customMsg;
    if (input.legacyImport) basePayload.legacyImport = true;
    if (input.quotePlaceholder) basePayload.quotePlaceholder = true;
    if (input.request) {
      const r = input.request;
      basePayload.rfqRequestId = r.rfqRequestId;
      basePayload.rfqRequestLineKey = r.rfqRequestLineKey;
      if (r.rfqRequestLineKeys?.length) basePayload.rfqRequestLineKeys = Array.from(new Set(r.rfqRequestLineKeys));
      basePayload.requestLabel = r.requestLabel;
      if (r.requestStage) basePayload.requestStage = r.requestStage;
      const msg = typeof r.requestMessage === 'string' ? r.requestMessage.trim() : '';
      if (msg) basePayload.requestMessage = msg;
      if (r.quoteDeadline instanceof Date) basePayload.quoteDeadline = Timestamp.fromDate(r.quoteDeadline);
    }

    // Last-resort structure scope: the staged release carries a snapshot of the
    // hub track's structure tags. When nobody picked a scope (no publish review
    // selection, no invite scope, no CNC payload) the package still records
    // which structures the file covers so supplier-side grouping stays right.
    let stagedScope: { id: string; name: string }[] = [];
    let stagedPurpose: import('../types').RfqReleasePurpose | undefined;
    if (input.hubFileReleaseId) {
      try {
        const relSnap = await getDoc(
          doc(db, 'rfqProjects', input.rfqProjectId, 'hubFileReleases', input.hubFileReleaseId),
        );
        const rel = relSnap.exists()
          ? (relSnap.data() as { structureIds?: unknown; structureNames?: unknown; releasePurpose?: unknown })
          : null;
        if (rel?.releasePurpose === 'reference') stagedPurpose = 'reference';
        if (rel && Array.isArray(rel.structureIds)) {
          const names = Array.isArray(rel.structureNames) ? rel.structureNames : [];
          stagedScope = rel.structureIds
            .map((id, i) => ({ id: String(id || ''), name: String(names[i] || '') }))
            .filter((s) => s.id);
        }
      } catch (e) {
        console.warn('[publishRfqPackage] staged release structure snapshot unavailable', e);
      }
    }
    // Reference releases are shared for production, never priced. Stamp the
    // package so the portal, release panels, and forecast coverage skip it.
    const resolvedPurpose = input.releasePurpose ?? stagedPurpose;
    if (resolvedPurpose === 'reference') basePayload.releasePurpose = 'reference';

    // The package snapshot is the whole timber list. Structure scope is recorded
    // separately so suppliers can tag their quote uploads; it does not trim the CSV.
    const packageIds: string[] = [];
    for (const supplierOrgId of safeTargets) {
      const perOrgPayload: Record<string, unknown> = { ...basePayload, supplierOrgId };
      // Snapshot the invite's structure refs so later scope edits can be detected
      // exactly (absent = package covers all structures).
      const orgInvite = preData.timberSupplierInvites?.find(
        (inv) => inv.supplierOrgId === supplierOrgId,
      );
      const publishScope = input.scopeStructuresByOrgId?.[supplierOrgId];
      if (publishScope?.length) {
        perOrgPayload.scopeStructures = publishScope.map((s) => ({
          id: s.id,
          name: s.name,
        }));
      } else if (inviteHasStructureScope(orgInvite)) {
        perOrgPayload.scopeStructures = orgInvite!.structures!.map((s) => ({
          id: s.id,
          name: s.name,
        }));
      } else if (input.cncPackagePayload?.structures?.length) {
        perOrgPayload.scopeStructures = input.cncPackagePayload.structures.map((s) => ({
          id: s.id,
          name: s.name,
        }));
      } else if (stagedScope.length) {
        perOrgPayload.scopeStructures = stagedScope.map((s) => ({ id: s.id, name: s.name }));
      }
      // Snapshot species-per-structure too, so the portal and the drift check see
      // exactly what this supplier was sent (absent = every species on every structure).
      if (workType === 'timber_rfq') {
        const structureSpecies = normalizeStructureSpecies(
          orgInvite?.structureSpecies,
          orgInvite?.requestedSpecies ?? [],
        );
        if (structureSpecies) perOrgPayload.structureSpecies = structureSpecies;
      }
      const shipTo = input.shipToByOrgId?.[supplierOrgId];
      if (shipTo && (
        shipTo.line1?.trim()
        || shipTo.line2?.trim()
        || shipTo.city?.trim()
        || shipTo.state?.trim()
        || shipTo.zip?.trim()
        || shipTo.recipient?.trim()
      )) {
        perOrgPayload.shipToAddress = removeUndefinedDeep(shipTo);
      }
      const preset = input.shipToPresetByOrgId?.[supplierOrgId];
      if (preset) perOrgPayload.shipToPreset = preset;
      const supersedes = input.supersedesPackageIdByOrgId?.[supplierOrgId];
      if (supersedes) perOrgPayload.supersedesPackageId = supersedes;
      const docRef = await addDoc(collection(db, 'rfqPackages'), perOrgPayload);
      packageIds.push(docRef.id);
      const newScopeIds = ((perOrgPayload.scopeStructures as { id?: string }[] | undefined) ?? [])
        .map((s) => String(s?.id || '').trim())
        .filter(Boolean);
      // A reference release is the production file for a quote that is already
      // approved. Archiving those structures off the quoting package makes
      // finances treat them as if the award no longer covers them.
      if (newScopeIds.length && resolvedPurpose !== 'reference') {
        try {
          const olderSnap = await getDocs(query(
            collection(db, 'rfqPackages'),
            where('rfqProjectId', '==', input.rfqProjectId),
          ));
          for (const older of olderSnap.docs) {
            if (older.id === docRef.id) continue;
            const data = older.data() as {
              supplierOrgId?: string;
              scopeStructures?: { id?: string }[];
              archivedStructureIds?: string[];
            };
            if ((data.supplierOrgId || '') !== supplierOrgId) continue;
            const already = new Set((data.archivedStructureIds ?? []).map((id) => String(id)));
            const overlap = (data.scopeStructures ?? [])
              .map((s) => String(s?.id || '').trim())
              .filter((id) => id && newScopeIds.includes(id) && !already.has(id));
            if (overlap.length) await archivePackageStructureIds([older.id], overlap);
          }
        } catch (err) {
          console.warn('[publishRfqPackage] could not archive overlapping structures on earlier packages', err);
        }
      }
    }

    const merged = Array.from(new Set([...rfqProjectSupplierOrgIds(preData), ...safeTargets]));
    await updateDoc(projRef, {
      supplierOrgIds: merged,
      workType,
      updatedAt: Timestamp.fromDate(now),
    });

    return packageIds;
  };

  const deleteRfqPackage = async (packageId: string): Promise<void> => {
    const subsQ = query(collection(db, 'rfqSubmissions'), where('rfqPackageId', '==', packageId));
    const subsSnap = await getDocs(subsQ);
    for (const d of subsSnap.docs) {
      const data = d.data() as { storagePath?: string; notBidding?: boolean };
      if (data.storagePath && !data.notBidding) {
        try {
          await deleteObject(ref(storage, data.storagePath));
        } catch (e) {
          console.warn('[deleteRfqPackage] storage delete skipped', d.id, e);
        }
      }
      await deleteDoc(doc(db, 'rfqSubmissions', d.id));
    }
    await deleteDoc(doc(db, 'rfqPackages', packageId));
  };

  const createRfqSubmission = async (
    row: Omit<RfqSubmission, 'id' | 'createdAt'>,
    options?: { createdAt?: Date },
  ): Promise<string> => {
    const createdAt = options?.createdAt ?? new Date();
    const payload: Record<string, unknown> = {
      ...removeUndefined(row as Record<string, unknown>),
      createdAt: Timestamp.fromDate(createdAt),
    };
    if (row.quoteValidUntil instanceof Date) {
      payload.quoteValidUntil = Timestamp.fromDate(row.quoteValidUntil);
    }
    if (row.paymentTermsDraft) {
      payload.paymentTermsDraft = serializePaymentTerms(row.paymentTermsDraft);
    }
    const docRef = await addDoc(collection(db, 'rfqSubmissions'), payload);
    return docRef.id;
  };

  const updateRfqSubmission = async (id: string, updates: Partial<RfqSubmission>): Promise<void> => {
    const payload: Record<string, unknown> = removeUndefined(updates as Record<string, unknown>);
    if (updates.rejectedAt instanceof Date) {
      payload.rejectedAt = Timestamp.fromDate(updates.rejectedAt);
    }
    if (updates.rejectionNoticeSentAt instanceof Date) {
      payload.rejectionNoticeSentAt = Timestamp.fromDate(updates.rejectionNoticeSentAt);
    }
    if (updates.pmQuoteInputAdjustedAt instanceof Date) {
      payload.pmQuoteInputAdjustedAt = Timestamp.fromDate(updates.pmQuoteInputAdjustedAt);
    }
    if (updates.splitAt instanceof Date) {
      payload.splitAt = Timestamp.fromDate(updates.splitAt);
    }
    if (updates.createdAt instanceof Date) {
      payload.createdAt = Timestamp.fromDate(updates.createdAt);
    }
    await updateDoc(doc(db, 'rfqSubmissions', id), payload as Record<string, any>);
  };

  const deleteRfqSubmission = async (row: {
    id: string;
    storagePath?: string;
    notBidding?: boolean;
    /** Shared PDF with the timber half of a combined-quote split — do not delete storage. */
    quotePlaceholder?: boolean;
    linkedTimberSubmissionId?: string;
    linkedCncSubmissionId?: string;
  }): Promise<void> => {
    if (!row.id) return;
    const sharedSplitPdf = !!(
      row.quotePlaceholder
      || row.linkedTimberSubmissionId
      || row.linkedCncSubmissionId
    );
    if (row.storagePath && !row.notBidding && !sharedSplitPdf) {
      try {
        await deleteObject(ref(storage, row.storagePath));
      } catch (e) {
        console.warn('[deleteRfqSubmission] storage delete skipped', e);
      }
    }
    await deleteDoc(doc(db, 'rfqSubmissions', row.id));
  };

  // ==========================================
  // BID OPERATIONS
  // ==========================================

  const generateBidNumber = async (): Promise<string> => {
    const year = new Date().getFullYear();
    const bidsThisYear = bids.filter(b =>
      b.bidNumber.startsWith(`BID-${year}`)
    );
    const nextNumber = bidsThisYear.length + 1;
    return `BID-${year}-${String(nextNumber).padStart(3, '0')}`;
  };

  const createBid = async (bidData: Omit<Bid, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const bidNumber = await generateBidNumber();

      const docRef = await addDoc(collection(db, 'bids'), {
        ...bidData,
        bidNumber,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });

      return docRef.id;
    } catch (err) {
      console.error('Error creating bid:', err);
      throw err;
    }
  };

  const updateBid = async (id: string, updates: Partial<Bid>): Promise<void> => {
    try {
      const docRef = doc(db, 'bids', id);
      await updateDoc(docRef, {
        ...removeUndefined(updates),
        updatedAt: Timestamp.fromDate(new Date()),
      });
    } catch (err) {
      console.error('Error updating bid:', err);
      throw err;
    }
  };

  const deleteBid = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'bids', id));
      // Note: Files in Firebase Storage should be cleaned up by Cloud Functions
    } catch (err) {
      console.error('Error deleting bid:', err);
      throw err;
    }
  };

  const getBidsForLead = (leadId: string): Bid[] => {
    return bids.filter(b => b.leadId === leadId);
  };

  const getBidsForDeal = (dealId: string): Bid[] => {
    return bids.filter(b => b.dealId === dealId);
  };

  const addGroupToBid = async (bidId: string, group: Omit<BidGroup, 'id'>): Promise<string> => {
    try {
      const bid = bids.find(b => b.id === bidId);
      if (!bid) throw new Error('Bid not found');

      const groupId = `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newGroup: BidGroup = {
        ...group,
        id: groupId,
      };

      const updatedGroups = [...bid.groups, newGroup];
      await updateBid(bidId, { groups: updatedGroups });

      return groupId;
    } catch (err) {
      console.error('Error adding group to bid:', err);
      throw err;
    }
  };

  const updateBidGroup = async (bidId: string, groupId: string, updates: Partial<BidGroup>): Promise<void> => {
    try {
      const bid = bids.find(b => b.id === bidId);
      if (!bid) throw new Error('Bid not found');

      const groupIndex = bid.groups.findIndex(g => g.id === groupId);
      if (groupIndex === -1) throw new Error('Group not found');

      const updatedGroups = [...bid.groups];
      updatedGroups[groupIndex] = {
        ...updatedGroups[groupIndex],
        ...updates,
      };

      await updateBid(bidId, { groups: updatedGroups });
    } catch (err) {
      console.error('Error updating bid group:', err);
      throw err;
    }
  };

  const deleteBidGroup = async (bidId: string, groupId: string): Promise<void> => {
    try {
      const bid = bids.find(b => b.id === bidId);
      if (!bid) throw new Error('Bid not found');

      const updatedGroups = bid.groups.filter(g => g.id !== groupId);
      await updateBid(bidId, { groups: updatedGroups });
    } catch (err) {
      console.error('Error deleting bid group:', err);
      throw err;
    }
  };

  // ==========================================
  // UTM STRATEGY OPERATIONS
  // ==========================================

  const createUtmStrategy = async (strategyData: Omit<UtmStrategy, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'utmStrategies'), {
        ...strategyData,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });
      return docRef.id;
    } catch (err) {
      console.error('Error creating UTM strategy:', err);
      throw err;
    }
  };

  const updateUtmStrategy = async (id: string, updates: Partial<UtmStrategy>): Promise<void> => {
    try {
      const docRef = doc(db, 'utmStrategies', id);
      await updateDoc(docRef, {
        ...removeUndefined(updates),
        updatedAt: Timestamp.fromDate(new Date()),
      });
    } catch (err) {
      console.error('Error updating UTM strategy:', err);
      throw err;
    }
  };

  const deleteUtmStrategy = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'utmStrategies', id));
    } catch (err) {
      console.error('Error deleting UTM strategy:', err);
      throw err;
    }
  };

  const addCampaignToStrategy = async (strategyId: string, campaignData: Omit<UtmCampaign, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    try {
      const strategy = utmStrategies.find(s => s.id === strategyId);
      if (!strategy) throw new Error('Strategy not found');

      const now = new Date();
      const campaignId = `campaign-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newCampaign: UtmCampaign = {
        ...campaignData,
        id: campaignId,
        createdAt: now,
        updatedAt: now,
      };

      const updatedCampaigns = [...strategy.campaigns, newCampaign];
      await updateUtmStrategy(strategyId, { campaigns: updatedCampaigns });
      return campaignId;
    } catch (err) {
      console.error('Error adding campaign to strategy:', err);
      throw err;
    }
  };

  const updateCampaign = async (strategyId: string, campaignId: string, updates: Partial<UtmCampaign>): Promise<void> => {
    try {
      const strategy = utmStrategies.find(s => s.id === strategyId);
      if (!strategy) throw new Error('Strategy not found');

      const campaignIndex = strategy.campaigns.findIndex(c => c.id === campaignId);
      if (campaignIndex === -1) throw new Error('Campaign not found');

      const updatedCampaigns = [...strategy.campaigns];
      updatedCampaigns[campaignIndex] = {
        ...updatedCampaigns[campaignIndex],
        ...updates,
        updatedAt: new Date(),
      };

      await updateUtmStrategy(strategyId, { campaigns: updatedCampaigns });
    } catch (err) {
      console.error('Error updating campaign:', err);
      throw err;
    }
  };

  const deleteCampaign = async (strategyId: string, campaignId: string): Promise<void> => {
    try {
      const strategy = utmStrategies.find(s => s.id === strategyId);
      if (!strategy) throw new Error('Strategy not found');

      const updatedCampaigns = strategy.campaigns.filter(c => c.id !== campaignId);
      await updateUtmStrategy(strategyId, { campaigns: updatedCampaigns });
    } catch (err) {
      console.error('Error deleting campaign:', err);
      throw err;
    }
  };

  const addLinkToCampaign = async (strategyId: string, campaignId: string, linkData: Omit<UtmLink, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const strategy = utmStrategies.find(s => s.id === strategyId);
      if (!strategy) throw new Error('Strategy not found');

      const campaignIndex = strategy.campaigns.findIndex(c => c.id === campaignId);
      if (campaignIndex === -1) throw new Error('Campaign not found');

      const linkId = `link-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newLink: UtmLink = {
        ...linkData,
        id: linkId,
        createdAt: new Date(),
      };

      const updatedCampaigns = [...strategy.campaigns];
      updatedCampaigns[campaignIndex] = {
        ...updatedCampaigns[campaignIndex],
        links: [...updatedCampaigns[campaignIndex].links, newLink],
        updatedAt: new Date(),
      };

      await updateUtmStrategy(strategyId, { campaigns: updatedCampaigns });
      return linkId;
    } catch (err) {
      console.error('Error adding link to campaign:', err);
      throw err;
    }
  };

  const deleteLinkFromCampaign = async (strategyId: string, campaignId: string, linkId: string): Promise<void> => {
    try {
      const strategy = utmStrategies.find(s => s.id === strategyId);
      if (!strategy) throw new Error('Strategy not found');

      const campaignIndex = strategy.campaigns.findIndex(c => c.id === campaignId);
      if (campaignIndex === -1) throw new Error('Campaign not found');

      const updatedCampaigns = [...strategy.campaigns];
      updatedCampaigns[campaignIndex] = {
        ...updatedCampaigns[campaignIndex],
        links: updatedCampaigns[campaignIndex].links.filter(l => l.id !== linkId),
        updatedAt: new Date(),
      };

      await updateUtmStrategy(strategyId, { campaigns: updatedCampaigns });
    } catch (err) {
      console.error('Error deleting link from campaign:', err);
      throw err;
    }
  };

  const addNoteToCampaign = async (strategyId: string, campaignId: string, noteData: Omit<UtmNote, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const strategy = utmStrategies.find(s => s.id === strategyId);
      if (!strategy) throw new Error('Strategy not found');

      const campaign = strategy.campaigns.find(c => c.id === campaignId);
      if (!campaign) throw new Error('Campaign not found');

      const now = new Date();
      const noteId = `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const updatedCampaigns = strategy.campaigns.map(c =>
        c.id === campaignId
          ? {
            ...c, notes: [...(c.notes || []), {
              ...noteData,
              id: noteId,
              createdAt: Timestamp.fromDate(now)
            }]
          }
          : c
      );

      // Convert all dates to timestamps for Firestore
      const campaignsForFirestore = updatedCampaigns.map(c => {
        const campaign: any = {
          ...c,
          createdAt: c.createdAt instanceof Date ? Timestamp.fromDate(c.createdAt) : c.createdAt,
          updatedAt: c.updatedAt instanceof Date ? Timestamp.fromDate(c.updatedAt) : c.updatedAt,
          links: c.links.map(l => ({
            ...l,
            createdAt: l.createdAt instanceof Date ? Timestamp.fromDate(l.createdAt) : l.createdAt,
          })),
          notes: (c.notes || []).map(n => ({
            ...n,
            createdAt: n.createdAt instanceof Date ? Timestamp.fromDate(n.createdAt) : n.createdAt,
          })),
        };

        // Only add startDate and endDate if they exist
        if (c.startDate) {
          campaign.startDate = c.startDate instanceof Date ? Timestamp.fromDate(c.startDate) : c.startDate;
        }
        if (c.endDate) {
          campaign.endDate = c.endDate instanceof Date ? Timestamp.fromDate(c.endDate) : c.endDate;
        }

        return campaign;
      });

      const docRef = doc(db, 'utmStrategies', strategyId);
      await updateDoc(docRef, {
        campaigns: removeUndefined(campaignsForFirestore),
        updatedAt: Timestamp.fromDate(new Date()),
      });

      return noteId;
    } catch (err) {
      console.error('Error adding note to campaign:', err);
      throw err;
    }
  };

  const deleteNoteFromCampaign = async (strategyId: string, campaignId: string, noteId: string): Promise<void> => {
    try {
      const strategy = utmStrategies.find(s => s.id === strategyId);
      if (!strategy) throw new Error('Strategy not found');

      const campaign = strategy.campaigns.find(c => c.id === campaignId);
      if (!campaign) throw new Error('Campaign not found');

      const updatedCampaigns = strategy.campaigns.map(c =>
        c.id === campaignId
          ? { ...c, notes: (c.notes || []).filter(n => n.id !== noteId) }
          : c
      );

      await updateUtmStrategy(strategyId, { campaigns: updatedCampaigns });
    } catch (err) {
      console.error('Error deleting note from campaign:', err);
      throw err;
    }
  };

  // ==========================================
  // ENGAGEMENT LETTER OPERATIONS
  // ==========================================

  // Track recent letter creations to prevent duplicates
  const recentLetterCreations = useRef<Map<string, number>>(new Map());

  const createEngagementLetter = async (letterData: Omit<EngagementLetter, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    // Create a key from leadId + title to detect duplicate requests
    const dedupeKey = `${letterData.leadId}-${letterData.title}`;
    const now = Date.now();
    const lastCreation = recentLetterCreations.current.get(dedupeKey);

    // Prevent duplicate creation within 5 seconds
    if (lastCreation && (now - lastCreation) < 5000) {
      console.warn('Duplicate engagement letter creation blocked:', dedupeKey);
      throw new Error('Duplicate creation blocked - please wait before creating another letter');
    }

    // Mark this key as being created
    recentLetterCreations.current.set(dedupeKey, now);

    try {
      const createdAt = new Date();
      const docRef = await addDoc(collection(db, 'engagementLetters'), {
        ...removeUndefined(letterData),
        createdAt: Timestamp.fromDate(createdAt),
        updatedAt: Timestamp.fromDate(createdAt),
      });
      return docRef.id;
    } catch (err) {
      // Clear the dedupe key on error so user can retry
      recentLetterCreations.current.delete(dedupeKey);
      console.error('Error creating engagement letter:', err);
      throw err;
    }
  };

  const updateEngagementLetter = async (id: string, updates: Partial<EngagementLetter>): Promise<void> => {
    try {
      const docRef = doc(db, 'engagementLetters', id);

      // Handle signature timestamp conversion
      const updateData: any = {
        ...removeUndefined(updates),
        updatedAt: Timestamp.fromDate(new Date()),
      };

      // Convert signature dates if present
      if (updates.signature) {
        updateData.signature = {
          ...updates.signature,
          signedAt: updates.signature.signedAt instanceof Date
            ? Timestamp.fromDate(updates.signature.signedAt)
            : updates.signature.signedAt,
        };
      }

      // Convert other date fields
      if (updates.sentAt) {
        updateData.sentAt = Timestamp.fromDate(updates.sentAt instanceof Date ? updates.sentAt : new Date(updates.sentAt));
      }
      if (updates.viewedAt) {
        updateData.viewedAt = Timestamp.fromDate(updates.viewedAt instanceof Date ? updates.viewedAt : new Date(updates.viewedAt));
      }
      if (updates.signedAt) {
        updateData.signedAt = Timestamp.fromDate(updates.signedAt instanceof Date ? updates.signedAt : new Date(updates.signedAt));
      }
      if (updates.paidAt) {
        updateData.paidAt = Timestamp.fromDate(updates.paidAt instanceof Date ? updates.paidAt : new Date(updates.paidAt));
      }

      await updateDoc(docRef, updateData);
    } catch (err) {
      console.error('Error updating engagement letter:', err);
      throw err;
    }
  };

  const deleteEngagementLetter = async (id: string): Promise<void> => {
    try {
      await deleteDoc(doc(db, 'engagementLetters', id));
    } catch (err) {
      console.error('Error deleting engagement letter:', err);
      throw err;
    }
  };

  const getEngagementLettersForLead = (leadId: string): EngagementLetter[] => {
    return engagementLetters.filter(letter => letter.leadId === leadId);
  };

  const value: CRMContextType = {
    // Lazy heavy collections
    requestCrmCollections,
    crmCollectionLoaded,

    architectHubReady,

    // Data
    leads,
    contacts,
    deals,

    // Lead operations
    createLead,
    updateLead,
    deleteLead,
    bulkUpdateLeads,
    bulkDeleteLeads,
    bulkArchiveLeads,
    createInitialCallsForRecentLeads,

    // Contact operations
    createContact,
    updateContact,
    deleteContact,

    // Legacy Deal operations
    createDeal,
    updateDeal,
    deleteDeal,
    deleteAllTestDeals,

    // Pipeline operations
    pipelines,
    createPipeline,
    updatePipeline,
    deletePipeline,
    getDefaultPipeline,
    initializeDefaultPipelines,

    // Enhanced Deal operations
    enhancedDeals,
    createEnhancedDeal,
    updateEnhancedDeal,
    deleteEnhancedDeal,
    moveDealToStage,
    markDealWon,
    markDealLost,
    markDealActive,
    convertLeadToDeal,
    createProjectForLead,
    linkDealToLead,
    transferDealToLead,

    // Activity operations
    createActivity,
    updateActivity,
    deleteActivity,
    completeActivity,

    // Timber Project operations
    timberProjects,
    createTimberProject,
    updateTimberProject,
    persistProductionCommentAccessForDeal,
    deleteTimberProject,
    convertDealToProject,
    createPreconProject,
    syncSignedDealSideEffects: syncSignedDealSideEffectsFn,

    // Project Time Entries
    projectTimeEntries,
    createProjectTimeEntry,
    updateProjectTimeEntry,
    getTimeEntriesForProject,

    // Project Onboarding
    projectOnboardings,
    updateOnboardingItem,
    deleteOnboardingItem,
    getOnboardingForProject,

    // Project Task operations
    projectTasks,
    createProjectTask,
    updateProjectTask,
    deleteProjectTask,
    getTasksForProject,

    // Task Groups
    taskGroups,
    createTaskGroup,
    updateTaskGroup,
    deleteTaskGroup,
    ensureProcurementTaskGroupForProject,

    // Task Comments & Activity
    taskComments,
    createTaskComment,
    taskActivityLogs,
    createTaskActivity,

    // Milestone operations
    milestones,
    createMilestone,
    updateMilestone,
    deleteMilestone,
    getMilestonesForProject,

    // Proposal operations
    proposals,
    createProposal,
    updateProposal,
    deleteProposal,

    // Payment operations
    payments,
    createPayment,
    updatePayment,
    deletePayment,

    // Task operations (legacy)
    tasks,
    createTask,
    updateTask,
    deleteTask,

    // SMS Message operations
    smsMessages,
    createSMSMessage,
    updateSMSMessage,

    // Email Message operations
    emailMessages,
    createEmailMessage,
    updateEmailMessage,

    // Strategic Marker operations
    strategicLayers,
    strategicMarkers,
    createStrategicLayer,
    updateStrategicLayer,
    deleteStrategicLayer,
    createStrategicMarker,
    bulkCreateStrategicMarkers,
    updateStrategicMarker,
    deleteStrategicMarker,
    deleteAllMarkersInLayer,
    getMarkersForLayer,
    toggleLayerVisibility,

    // User Management
    crmUsers,
    updateCRMUser,
    adminSetUserProfilePhoto,
    adminClearUserProfilePhoto,
    deactivateUser,
    reactivateUser,
    deleteCRMUser,

    // Architect Invites
    architectInvites,
    createArchitectInvite,
    updateArchitectInvite,
    revokeArchitectInvite,

    // Engineer Invites
    engineerInvites,
    createEngineerInvite,
    updateEngineerInvite,
    revokeEngineerInvite,

    // CPA Invites
    cpaInvites,
    createCpaInvite,
    updateCpaInvite,
    revokeCpaInvite,
    employeeInvites,
    createEmployeeInvite,
    revokeEmployeeInvite,
    productionCommentAccess,

    supplierOrgs,
    supplierInvites,
    rfqProjects,
    rfqPackages,
    rfqSubmissions,
    createSupplierOrg,
    updateSupplierOrg,
    deleteSupplierOrg,
    createSupplierInvite,
    updateSupplierInvite,
    revokeSupplierInvite,
    deleteSupplierInvite,
    createRfqProject,
    updateRfqProject,
    publishRfqPackage,
    deleteRfqPackage,
    createRfqSubmission,
    updateRfqSubmission,
    deleteRfqSubmission,

    // Bid operations
    bids,
    createBid,
    updateBid,
    deleteBid,
    getBidsForLead,
    getBidsForDeal,
    addGroupToBid,
    updateBidGroup,
    deleteBidGroup,
    generateBidNumber,

    // UTM Strategy operations
    utmStrategies,
    createUtmStrategy,
    updateUtmStrategy,
    deleteUtmStrategy,
    addCampaignToStrategy,
    updateCampaign,
    deleteCampaign,
    addLinkToCampaign,
    deleteLinkFromCampaign,
    addNoteToCampaign,
    deleteNoteFromCampaign,

    // Engagement Letter operations
    engagementLetters,
    createEngagementLetter,
    updateEngagementLetter,
    deleteEngagementLetter,
    getEngagementLettersForLead,

    // Utility functions
    getAllLabels: () => {
      const allLabels = new Set<string>();
      // Add saved labels from Firestore
      savedLabels.forEach(label => allLabels.add(label));
      // Add labels from leads
      leads.forEach(lead => {
        if (lead.labels && Array.isArray(lead.labels)) {
          lead.labels.forEach(label => {
            if (label && label.trim()) {
              allLabels.add(label.trim());
            }
          });
        }
      });
      return Array.from(allLabels).sort();
    },

    saveLabel: async (label: string) => {
      const trimmedLabel = label.trim();
      if (!trimmedLabel || savedLabels.includes(trimmedLabel)) return;

      try {
        const newLabels = [...savedLabels, trimmedLabel].sort();
        await setDoc(doc(db, 'settings', 'labels'), { labels: newLabels });
        setSavedLabels(newLabels);
      } catch (err) {
        console.error('Error saving label:', err);
      }
    },

    savedLabels,

    // State
    loading,
    error,
  };

  return <CRMContext.Provider value={value}>{children}</CRMContext.Provider>;
};